// ===========================================
// PIXI.JS TABLE PAGE
// ===========================================
// Full game table using PixiJS rendering engine.
// Integrates with existing game state stores and useGameEvents hook.
// ===========================================

import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PixiGameTableV1, PixiGameTableV1Ref, resetClearTablePromise, clearModuleState } from '../components/game/pixi';
import { getStoredPosition, type ControlPanelPosition } from '../components/game/pixi/PixiControlPanel';
import { SidePanel, type SidebetId } from '../components/game/SidePanel';
// NOTE: FieldChipRail, StagedChipsDisplay, and floating buttons have been migrated to PixiJS
import { ChipPurchaseModal } from '../components/ChipPurchaseModal';
import { ShareModal } from '../components/ShareModal';
import { TableSettingsModal } from '../components/game/TableSettingsModal';
import { ReconnectingModal } from '../components/ReconnectingModal';
// SeatTutorialModal removed - now using PixiHowToBetModal
import { LoadingVideo } from '../components/LoadingVideo';
// NOTE: useStagedChipAnimations removed - chip animations now in PixiJS
import { useStagedChipsStore } from '../stores/stagedChipsStore';
import { useGameStateStore, useDealer, useDealerPersona, useInsuranceOffered, useInsuranceCountdown, useMyPlayerState, useGamePhase, useAvailableActions, useInsuranceDecisionPending } from '../stores/gameStateStore';
import { useCardTrackingStore, selectTrueCount, selectPenetration as selectClientPenetration } from '../stores/cardTrackingStore';
import { GamePhase } from '@busted-blackjack/shared';
// InsuranceToggle removed - now using PixiInsurancePanel
import { useAnimationQueueStore } from '../stores/animationQueueStore';
import { useAuthStore } from '../stores/authStore';
import { useSocketStore, onReconnect, setPersistedTableId } from '../stores/socketStore';
import { useLayoutStore } from '../stores/layoutStore';
import { useTableLifecycleRegistry } from '../stores/tableLifecycleRegistry';
import { useAnimationConfigStore } from '../stores/animationConfigStore';
import { useGameEvents, type SeatResolutionEvent } from '../hooks/useGameEvents';
import { useRecommendation } from '../hooks/useRecommendation';
import { useResync } from '../hooks/useResync';
import { useVisibilityHandler } from '../hooks/useVisibilityHandler';
import { formatBalance } from '../utils/format';
import { breakdownChips, capChipsForAnimation } from '../utils/chipAnimations';
import { diagLog } from '../utils/diagnosticLogger';
import { getClientTableMaster } from '../utils/ClientTableMaster';
import type { HandState, DealerState, StatsData } from '@busted-blackjack/shared';
import { HandOutcome, BlackjackEndpointIds, BLACKJACK_DENOMINATIONS, DEFAULT_STAGGER_CONFIG, DEFAULT_UI_DELAY_CONFIG } from '@busted-blackjack/shared';

// Table image for measuring actual table dimensions
import tableImageUrl from '../assets/table2.png';

// ===========================================
// AUTO-REPEAT BUTTON COMPONENT
// ===========================================
// Holds down to continuously trigger onClick

interface AutoRepeatButtonProps {
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
  initialDelay?: number;
  repeatInterval?: number;
}

function AutoRepeatButton({ onClick, className, children, initialDelay = 400, repeatInterval = 50 }: AutoRepeatButtonProps) {
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const startRepeat = useCallback(() => {
    onClick(); // Fire immediately
    timeoutRef.current = window.setTimeout(() => {
      intervalRef.current = window.setInterval(onClick, repeatInterval);
    }, initialDelay);
  }, [onClick, initialDelay, repeatInterval]);

  const stopRepeat = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopRepeat();
  }, [stopRepeat]);

  return (
    <button
      className={className}
      onMouseDown={startRepeat}
      onMouseUp={stopRepeat}
      onMouseLeave={stopRepeat}
      onTouchStart={startRepeat}
      onTouchEnd={stopRepeat}
    >
      {children}
    </button>
  );
}

// ===========================================
// RESPONSIVE VIEWPORT CONSTANTS (from TableLayoutDemoPage)
// ===========================================

const SMALL_VIEWPORT_THRESHOLD = 700; // Below this, enter small viewport mode
const HEADER_HEIGHT = 56;             // Normal header height
const SMALL_HEADER_HEIGHT = 28;       // Small viewport header height (50%)
const TAP_TARGET_HEIGHT = 20;         // Collapsed header tap target
const SMALL_TAP_TARGET_HEIGHT = 10;   // Small viewport collapsed tap target

// ===========================================
// CHIP DENOMINATIONS (used by DEV controls)
// ===========================================
// NOTE: Chip rail positions and label config moved to PixiJS

const CHIP_DENOMINATIONS = [1, 5, 25, 100, 500, 1000, 5000, 10000];

// ===========================================
// TYPES
// ===========================================

interface PlayerState {
  seatPosition: number;
  displayName: string;
  avatarUrl?: string;
  hands: HandState[];
  currentHandIndex: number;
  insuranceBet?: number;
  confirmedBet?: number;
}

// ===========================================
// COMPONENT
// ===========================================

export default function PixiTablePage() {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'play'; // 'play' or 'spectate'
  const modeRef = useRef(mode); // Ref for use in callbacks
  modeRef.current = mode; // Keep ref in sync
  const pixiRef = useRef<PixiGameTableV1Ref>(null);
  const gameAreaRef = useRef<HTMLDivElement>(null);
  // NOTE: Control panel is now fully in PixiJS

  // Leaving state - when true, render nothing to prevent green flash during navigation
  const [isLeaving, setIsLeaving] = useState(false);

  // Measured container dimensions (from ResizeObserver) - more reliable than calculated
  const [measuredDimensions, setMeasuredDimensions] = useState<{ width: number; height: number } | null>(null);

  // Auth store
  const user = useAuthStore((state) => state.user);
  const balance = user?.balance || 0;

  // Recommendation hook - populates BUDDY sidebar and tracks recommendations
  const { recommendedAction, insuranceRecommendation } = useRecommendation();

  // ===========================================
  // RESYNC HOOKS - Unified state resync
  // ===========================================
  // Handles all resync scenarios: tab visibility, reconnect, page refresh, round boundary
  const { resync } = useResync();

  // Visibility handler - clears state after 5s hidden, triggers resync on return
  const handleVisibilityResync = useCallback(() => {
    console.log('[PixiTablePage] Visibility resync triggered');
    // NOTE: Don't call clearTable() here - it's an async animation that would race
    // with the resync. The Zustand state was already cleared by useVisibilityHandler
    // when the tab was hidden.

    // Trigger resync - this sends join_table to server which responds with table_joined
    // The table_joined handler in useGameEvents automatically calls fullVisualResync
    // so we don't need to manually trigger it here
    resync({ reason: 'visibility' });
  }, [resync]);

  useVisibilityHandler({
    onNeedsResync: handleVisibilityResync,
    enabled: !!tableId, // Only enable when at a table
  });

  // Auto-play preference from user settings
  const autoPlay = useAuthStore((state) => state.user?.autoPlay ?? false);

  // ===========================================
  // POST-RESYNC AUTOPLAY
  // ===========================================
  // After visibility resync, the normal TURN_STARTED event flow is missed.
  // This function manually triggers autoplay if conditions are met.
  const executePostResyncAutoplay = useCallback(() => {
    // Check if autoplay is enabled
    const user = useAuthStore.getState().user;
    if (!user?.autoPlay) {
      console.log('[PixiTablePage] Post-resync autoplay: disabled');
      return;
    }

    const gameState = useGameStateStore.getState();
    const { mySeatPosition, currentTurnSeat, phase, availableActions, players } = gameState;

    // Check if it's our turn in PLAYING phase
    if (phase !== GamePhase.PLAYING || currentTurnSeat !== mySeatPosition || mySeatPosition === null) {
      console.log('[PixiTablePage] Post-resync autoplay: not our turn', {
        phase,
        currentTurnSeat,
        mySeatPosition,
      });
      return;
    }

    // Get current hand state for recommendation
    const player = players.get(mySeatPosition);
    if (!player?.hands?.length) {
      console.log('[PixiTablePage] Post-resync autoplay: no hands');
      return;
    }

    const handIndex = gameState.currentHandIndex ?? 0;
    const hand = player.hands[handIndex];
    if (!hand?.cards?.length) {
      console.log('[PixiTablePage] Post-resync autoplay: no cards in hand');
      return;
    }

    // Get recommendation from control panel (it computes based on current state)
    // The control panel's computeRecommendation is called reactively
    const recommendation = pixiRef.current?.getComputedRecommendation?.();
    if (!recommendation) {
      console.log('[PixiTablePage] Post-resync autoplay: no recommendation available');
      return;
    }

    console.log(`[PixiTablePage] Post-resync autoplay: executing ${recommendation.action}`);

    // Log for diagnostics
    diagLog.action({
      seat: mySeatPosition,
      action: recommendation.action,
      source: 'AUTOPLAY',
    });

    // Map action to socket message
    const actionMap: Record<string, string> = {
      'HIT': 'hit',
      'STAND': 'stand',
      'DOUBLE': 'double',
      'SPLIT': 'split',
      'SURRENDER': 'surrender',
    };

    const messageType = actionMap[recommendation.action];
    if (messageType) {
      const socket = useSocketStore.getState().socket;
      if (socket) {
        socket.emit('client_message', { type: messageType });
        console.log(`[PixiTablePage] Post-resync autoplay: sent ${messageType}`);
      }
    }
  }, []);

  // BUDDY features status (for HUD visibility) - show when user has hints enabled
  const showRecommendations = useAuthStore((state) => state.user?.showRecommendations ?? false);
  console.log('[PixiTablePage] BUDDY status - showRecommendations:', showRecommendations);

  // BUDDY HUD data (DP and TC) - both from cardTrackingStore for real-time updates
  const penetration = useCardTrackingStore(selectClientPenetration);
  const trueCount = useCardTrackingStore(selectTrueCount);

  // Layout store - initialize on mount and provide editor access
  const initializeLayout = useLayoutStore((state) => state.initializeDefault);
  const hasLayout = useLayoutStore((state) => state.layout !== null);
  const layout = useLayoutStore((state) => state.layout);
  const updateLayoutPosition = useLayoutStore((state) => state.updatePosition);

  useEffect(() => {
    // Initialize with default layout on mount
    // TODO: In production, layout will be received from server via table_joined message
    if (!hasLayout) {
      console.log('[PixiTablePage] Initializing default layout');
      initializeLayout();
    }
  }, [hasLayout, initializeLayout]);

  // Reset stuck clearTable promise on mount (handles HMR and previous session leftovers)
  useEffect(() => {
    resetClearTablePromise();
  }, []);

  // ===========================================
  // TABLE LIFECYCLE REGISTRY - JOIN/RETURN Architecture
  // ===========================================
  // Centralized management of table join/leave lifecycle.
  // Philosophy: "Keep socket alive - only handle actual disconnections."
  // Server keeps game running regardless of client visibility state.
  // On any reconnection, client syncs from server's table_joined snapshot.
  //
  // Callbacks are registered here so the lifecycle registry can:
  // - Clear Pixi visuals (onClearVisuals)
  // - Clear animation queue (onClearAnimationQueue)
  // - Disconnect socket (onDisconnectSocket)
  // - Connect socket and join table (onConnectAndJoin)
  // - Request fresh table state (onRequestTableState)
  // ===========================================

  useEffect(() => {
    const registry = useTableLifecycleRegistry.getState();

    // Register all lifecycle callbacks
    registry.registerCallbacks({
      onClearVisuals: () => {
        console.log('[PixiTablePage] Lifecycle: Clearing Pixi visuals');
        pixiRef.current?.clearTable?.();
      },

      onClearAnimationQueue: () => {
        console.log('[PixiTablePage] Lifecycle: Clearing animation queue');
        useAnimationQueueStore.getState().clear();
        // Mark hand animations as complete (no pending animations after clear)
        useAnimationQueueStore.getState().setMyHandAnimationsComplete(true);
        // NOTE: We no longer reset lastEventSequence to -1 here.
        // The server now sends lastEventSequence in table_joined, and useGameEvents
        // sets it from there. This ensures the client only processes events AFTER
        // the snapshot moment, preventing visual chaos from duplicate/stale events.
      },

      onDisconnectSocket: () => {
        console.log('[PixiTablePage] Lifecycle: Disconnecting socket');
        useSocketStore.getState().disconnect();
      },

      onConnectAndJoin: (joinTableId: string) => {
        console.log('[PixiTablePage] Lifecycle: Connecting and joining table:', joinTableId);
        // Ensure socket is connected
        const socketState = useSocketStore.getState();
        if (!socketState.isConnected && !socketState.isConnecting) {
          socketState.connect();
        }
        // Join table (will emit when socket connects)
        const currentSocket = socketState.socket;
        if (currentSocket?.connected) {
          currentSocket.emit('client_message', { type: 'join_table', tableId: joinTableId, mode: modeRef.current });
        } else {
          // Socket not yet connected - wait for connect event
          // The socketStore's connect handler will auto-join via persistedTableId
          setPersistedTableId(joinTableId);
        }
        sessionStorage.setItem('lastJoinedTableId', joinTableId);
      },

      onRequestTableState: (requestTableId: string) => {
        console.log('[PixiTablePage] Lifecycle: Requesting fresh table state:', requestTableId);
        const currentSocket = useSocketStore.getState().socket;
        if (currentSocket?.connected) {
          currentSocket.emit('client_message', { type: 'join_table', tableId: requestTableId, mode: modeRef.current });
        }
      },
    });

    // Cleanup: unregister callbacks on unmount
    return () => {
      console.log('[PixiTablePage] Lifecycle: Unregistering callbacks');
      useTableLifecycleRegistry.getState().unregisterCallbacks();
    };
  }, []); // Empty deps - register once on mount

  // ===========================================
  // BUDDY HUD VISIBILITY AND UPDATES
  // ===========================================
  // HUD shows when showRecommendations is enabled (user has BUDDY hints ON)
  // The useEffect in PixiGameTableV1 watches the buddyActive prop

  // Staged chips state
  const stagedAmount = useStagedChipsStore((state) => state.stagedAmount);
  const stagedChips = useStagedChipsStore((state) => state.stagedChips);
  const toggleAutobetLocal = useStagedChipsStore((state) => state.toggleAutobet);
  const autobetEnabled = useStagedChipsStore((state) => state.autobetEnabled);

  // Toggle autobet: update local state for immediate UI feedback + send to server
  // CRITICAL: Read autobetEnabled from getState() instead of closure to avoid
  // recreating this callback when autobet changes. If callback changes, PixiControlPanel
  // gets destroyed and recreated, which reads stale state during initialization.
  const toggleAutobet = useCallback(() => {
    const currentAutobetEnabled = useStagedChipsStore.getState().autobetEnabled;
    const newEnabled = !currentAutobetEnabled;
    toggleAutobetLocal(); // Toggle local state for immediate UI feedback
    // Send to server using socket directly (sendMessage not available yet)
    const socket = useSocketStore.getState().socket;
    console.log('[PixiTablePage] toggleAutobet - socket exists:', !!socket, 'connected:', socket?.connected);
    if (socket) {
      // Include staged bet info so server can use it for first autobet (when lastBet is not set)
      const { stagedAmount, stagedChips } = useStagedChipsStore.getState();
      console.log('[PixiTablePage] Emitting toggle_autobet message:', {
        type: 'toggle_autobet',
        enabled: newEnabled,
        amount: stagedAmount,
        betChips: stagedChips
      });
      socket.emit('client_message', {
        type: 'toggle_autobet',
        enabled: newEnabled,
        amount: stagedAmount > 0 ? stagedAmount : undefined,
        betChips: stagedChips.length > 0 ? stagedChips : undefined
      });
    } else {
      console.error('[PixiTablePage] Cannot send toggle_autobet - no socket connection!');
    }
    console.log('[PixiTablePage] Toggled autobet:', newEnabled);
  }, [toggleAutobetLocal]); // FIXED: Removed autobetEnabled - read from getState() instead

  // Game phase for autobet
  // Include 'complete' because server sets phase to 'complete' after resolution,
  // and transitions to 'betting' when first bet is placed
  const gamePhase = useGamePhase();
  const isBettingPhase = gamePhase === GamePhase.WAITING || gamePhase === GamePhase.BETTING || gamePhase === 'complete';

  // Available actions for auto-play
  const availableActions = useAvailableActions();

  // Animated counter state - tracks displayed amount during animations
  const [displayedAmount, setDisplayedAmount] = useState(0);
  const swapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChipsRef = useRef<number[]>([]); // Chips waiting for swap animation
  const chipsAnimatingInRef = useRef<number>(0); // Count of chips currently animating INTO staged area

  // Animation callbacks for counter updates
  const handleChipAnimationStart = useCallback((denomination: number, direction: 'in' | 'out') => {
    // When chip starts moving OUT, immediately decrement counter
    if (direction === 'out') {
      setDisplayedAmount(prev => Math.max(0, prev - denomination));
    }
    // Track chips moving IN to prevent sync effect from double-counting
    if (direction === 'in') {
      chipsAnimatingInRef.current++;
    }
  }, []);

  const handleChipAnimationEnd = useCallback((denomination: number, direction: 'in' | 'out') => {
    // When chip lands IN, increment counter
    if (direction === 'in') {
      chipsAnimatingInRef.current = Math.max(0, chipsAnimatingInRef.current - 1);
      setDisplayedAmount(prev => prev + denomination);
    }
  }, []);

  // Track if we've done initial sync (to distinguish initial load from animation states)
  const hasInitializedDisplayedAmount = useRef(false);

  // Sync displayedAmount when stagedAmount is reset externally (e.g., bet placed, page load, reconnect)
  useEffect(() => {
    // Initial load: sync displayedAmount to stagedAmount from persisted storage
    if (!hasInitializedDisplayedAmount.current && stagedAmount > 0) {
      setDisplayedAmount(stagedAmount);
      hasInitializedDisplayedAmount.current = true;
      return;
    }
    // Mark as initialized even if stagedAmount is 0
    if (!hasInitializedDisplayedAmount.current) {
      hasInitializedDisplayedAmount.current = true;
    }
    // If stagedAmount is 0 and displayedAmount isn't, sync them (bet placed, clear)
    if (stagedAmount === 0 && displayedAmount !== 0) {
      setDisplayedAmount(0);
    }
    // If stagedAmount was restored (reconnect) and displayedAmount is behind, sync up
    // This handles the case where stagedAmount is set externally after initialization
    // SKIP if chips are currently animating in - their callbacks will update displayedAmount
    if (stagedAmount > 0 && displayedAmount === 0 && chipsAnimatingInRef.current === 0) {
      setDisplayedAmount(stagedAmount);
    }
  }, [stagedAmount, displayedAmount]);

  // ===========================================
  // DEV CONTROLS STATE
  // ===========================================
  const [showDevControls, setShowDevControls] = useState(false);
  const [devTab, setDevTab] = useState<'seats' | 'chips' | 'table' | 'panel' | 'cards' | 'layout'>('seats');
  const [devPanelPos, setDevPanelPos] = useState({ x: 0, y: 0 });
  const devPanelRef = useRef<HTMLDivElement>(null);
  const [selectedLayoutEndpoint, setSelectedLayoutEndpoint] = useState<string>('staged-bet'); // For layout editor
  const [devSocketDisconnected, setDevSocketDisconnected] = useState(false); // Manual disconnect for testing

  // DEV: Toggle socket connection for testing reconnection
  const handleDevSocketToggle = useCallback(() => {
    const currentSocket = useSocketStore.getState().socket;
    if (!currentSocket) return;

    if (devSocketDisconnected) {
      console.log('[DEV] Reconnecting socket...');
      currentSocket.connect();
      setDevSocketDisconnected(false);
    } else {
      console.log('[DEV] Disconnecting socket...');
      currentSocket.disconnect();
      setDevSocketDisconnected(true);
    }
  }, [devSocketDisconnected]);

  // Seat controls
  const [seatRadiusScale, setSeatRadiusScale] = useState(191);
  const [seatSizeScale, setSeatSizeScale] = useState(100);
  const [seatGroupOffset, setSeatGroupOffset] = useState({ x: 0, y: -40 });

  // Sidebet modal state (modal is rendered in PixiJS, this just stores selections)
  // rounds: 1, 5, 10, or -1 for infinite/ongoing
  const [sidebetSelections, setSidebetSelections] = useState<Record<string, { percentage: number; rounds: number }>>({});

  // Chip rail controls
  const [chipRailOffset, setChipRailOffset] = useState({ x: 0, y: 183 });
  const [stagedBetOffset, setStagedBetOffset] = useState({ x: 0, y: 7 });
  // Staged bet box config
  const [stagedBetBoxConfig, setStagedBetBoxConfig] = useState({
    width: 275,
    height: 50,
    fontSize: 19,
    borderThickness: 3,
    clearFontSize: 13,
    buildYourBetFontSize: 28,
  });
  // Individual chip X offsets
  const [chipXOffsets, setChipXOffsets] = useState<Record<number, number>>({
    1: -76, 5: -99, 25: -118, 100: -136, 500: 140, 1000: 126, 5000: 107, 10000: 84
  });
  // Individual chip Y offsets
  const [chipYOffsets, setChipYOffsets] = useState<Record<number, number>>({
    1: -342, 5: -294, 25: -260, 100: -238, 500: -238, 1000: -260, 5000: -294, 10000: -342
  });
  // Individual chip label Y offsets
  const [chipLabelYOffsets, setChipLabelYOffsets] = useState<Record<number, number>>({
    1: 0, 5: 0, 25: 0, 100: 0, 500: 0, 1000: 0, 5000: 0, 10000: 0
  });
  // Chip rail label pill opacity (0-100, 0 = no pill)
  const [chipLabelPillOpacity, setChipLabelPillOpacity] = useState(100);
  // Username Y offset within seat box
  const [usernameY, setUsernameY] = useState(-2);

  // Table element controls
  const [shoeConfig, setShoeConfig] = useState({ x: -14, y: 6, rotation: 90, scale: 120 });
  const [trayConfig, setTrayConfig] = useState({ x: 105, y: -9, rotation: 0, scale: 90 });
  const [shoeCardsConfig, setShoeCardsConfig] = useState({ x: -29, y: -3, rotation: -5, scale: 116 });
  const [chipBorderConfig, setChipBorderConfig] = useState({ opacity: 30, thickness: 7, color: '#c2c89c' });
  const [chipCounterConfig, setChipCounterConfig] = useState({ show: true, pillOpacity: 100, fontColor: '#FB923C', fontSize: 14, x: 0, y: 8 });
  const [kittyConfig, setKittyConfig] = useState({ x: 0, y: 0, rotation: 0 });
  // Opponent-specific kitty config (separate from player's)
  const [opponentKittyConfig, setOpponentKittyConfig] = useState({ x: 0, y: 0, rotation: 0 });
  // Toggle for DEV controls: edit player configs vs opponent configs
  const [devConfigTarget, setDevConfigTarget] = useState<'player' | 'opponent'>('player');
  // Per-card offsets for shoe cards (3 cards: 0, 1, 2)
  const [shoeCardOffsets, setShoeCardOffsets] = useState<Record<0 | 1 | 2, { x: number; y: number; rotation: number; scale: number }>>({
    0: { x: 1, y: -4, rotation: 8, scale: 105 },
    1: { x: 2, y: -3, rotation: 0, scale: 105 },
    2: { x: -3, y: -1, rotation: -2, scale: 110 },
  });
  // Dealer button config
  // null means "use server buttonPosition", 0 means "hide", 1-7 is explicit DEV override
  const [dealerButtonSeatOverride, setDealerButtonSeatOverride] = useState<number | null>(null);
  const [dealerButtonConfig, setDealerButtonConfig] = useState({
    x: 0, y: 0, size: 110,
    bgColor: '#ffffff', fontColor: '#111827', borderColor: '#111827', borderWidth: 2
  });
  // Tray chips (dealer chip rail)
  const [trayChipsOffset, setTrayChipsOffset] = useState({ x: 0, y: 0 });
  // Turn indicator beam config
  const [beamConfig, setBeamConfig] = useState({ color: '#d8bb79', opacity: 40, width: 21 });
  // Turn indicator beam rotations per seat (visual positions 1-7)
  const [beamRotations, setBeamRotations] = useState<Record<number, number>>({
    1: 259,      // Far left
    2: 234.5,    // Left
    3: 210.5,    // Inner left
    4: 180,      // Center (bottom)
    5: 149.5,    // Inner right
    6: 125.5,    // Right
    7: 101,      // Far right
  });
  // DEV: Force beam visible for positioning adjustments
  const [devShowBeam, setDevShowBeam] = useState(false);
  const [devBeamSeat, setDevBeamSeat] = useState(4); // Default to center seat

  // BUDDY HUD config (DP and TC indicators) - locked-in values
  const [buddyHudConfig, setBuddyHudConfig] = useState({
    dpX: 250, tcX: 1065, y: 26, barY: 6,
    barWidth: 189, barHeight: 4,
    labelFontSize: 36, valueFontSize: 16, tickLabelFontSize: 12
  });

  // Per-seat configs (keyed by visual position 0=dealer, 1-7=players)
  const [seatConfigs, setSeatConfigs] = useState<Record<number, { x: number; y: number; rot: number; scale: number }>>({
    0: { x: 0, y: 37, rot: 0, scale: 132 }, // Dealer [LOCKED]
    1: { x: -43, y: 0, rot: 0, scale: 100 },
    2: { x: -10, y: -8, rot: 0, scale: 100 },
    3: { x: 0, y: -10, rot: 0, scale: 100 },
    4: { x: 0, y: 2, rot: 0, scale: 108 },
    5: { x: 0, y: -10, rot: 0, scale: 100 },
    6: { x: 10, y: -8, rot: 0, scale: 100 },
    7: { x: 44, y: 0, rot: 0, scale: 100 },
  });
  const [betChipConfigs, setBetChipConfigs] = useState<Record<number, { radiusOffset: number; x: number; y: number; scale: 100 }>>({
      0: { radiusOffset: 1, x: 0, y: 0, scale: 100 },
      1: { radiusOffset: 5, x: 0, y: 0, scale: 100 },
      2: { radiusOffset: 21, x: 2, y: 0, scale: 100 },
      3: { radiusOffset: 19, x: 20, y: 0, scale: 100 },
      4: { radiusOffset: 76, x: 0, y: 0, scale: 100 },
      5: { radiusOffset: 75, x: 0, y: 0, scale: 100 },
      6: { radiusOffset: 43, x: 0, y: -14, scale: 100 },
      7: { radiusOffset: -15, x: -27, y: 29, scale: 100 },
    });
  // Insurance now at SAME radial level as main bet (-134)
  // x = tangential offset (positive = right of main bet)
  // y = additional radial offset (if needed)
  const [insuranceChipConfigs, setInsuranceChipConfigs] = useState<Record<number, { radiusOffset: number; x: number; y: number; scale: number }>>({
      0: { radiusOffset: 0, x: 16, y: 0, scale: 100 },
      1: { radiusOffset: 56, x: 54, y: 34, scale: 100 },
      2: { radiusOffset: 49, x: 58, y: 5, scale: 100 },
      3: { radiusOffset: 20, x: 40, y: 0, scale: 100 },
      4: { radiusOffset: 5, x: 50, y: 0, scale: 100 },
      5: { radiusOffset: -13, x: 36, y: -5, scale: 100 },
      6: { radiusOffset: -37, x: 50, y: -10, scale: 100 },
      7: { radiusOffset: -49, x: 50, y: -34, scale: 100 },
    });
  // Per-seat opponent kitty offsets (applied on top of global opponentKittyConfig)
  const [opponentKittyConfigs, setOpponentKittyConfigs] = useState<Record<number, { radiusOffset: number; x: number; y: number; scale: number }>>({
      1: { radiusOffset: 0, x: 0, y: 0, scale: 100 },
      2: { radiusOffset: 0, x: 0, y: 0, scale: 100 },
      3: { radiusOffset: 0, x: 0, y: 0, scale: 100 },
      4: { radiusOffset: 0, x: 0, y: 0, scale: 100 },
      5: { radiusOffset: 0, x: 0, y: 0, scale: 100 },
      6: { radiusOffset: 0, x: 0, y: 0, scale: 100 },
      7: { radiusOffset: 0, x: 0, y: 0, scale: 100 },
    });
  // Dealer scorebox position offset from default position
  const [dealerScoreboxOffset, setDealerScoreboxOffset] = useState<{ x: number; y: number }>({ x: 0, y: -2 }); // [LOCKED]
  const [selectedSeat, setSelectedSeat] = useState<number>(0); // Currently selected seat for DEV controls (0=dealer)
  const [showMockPlayers, setShowMockPlayers] = useState(true); // Show mock players for positioning
  const [showBetChips, setShowBetChips] = useState(false); // Show/hide bet chip stacks at seats (DEV only)
  const [showInsuranceChips, setShowInsuranceChips] = useState(false); // Show/hide insurance chip stacks at seats (DEV only)
  const [showHandLabels, setShowHandLabels] = useState(false); // Show/hide static hand labels at seats (DEV only)
  const [handLabelConfig, setHandLabelConfig] = useState({ x: 0, y: 12 }); // Hand label global position offsets
  // Per-seat hand label position offsets (added to global config)
  const [handLabelConfigs, setHandLabelConfigs] = useState<Record<number, { x: number; y: number }>>({
    1: { x: 0, y: 0 }, 2: { x: 0, y: 0 }, 3: { x: 0, y: 0 }, 4: { x: 0, y: 0 },
    5: { x: 0, y: 0 }, 6: { x: 0, y: 0 }, 7: { x: 0, y: 0 },
  });
  const [devPopulateTable, setDevPopulateTable] = useState(false); // Populate table with all elements for layout testing

  // Sidebet icon position config (3 slots in arch above main bet)
  const [sidebetIconConfig, setSidebetIconConfig] = useState({
    baseRadiusOffset: -217,  // Y offset from seat center (negative = toward dealer)
    scale: 100,              // Icon scale percentage
    slot0: { x: -45, y: -5 }, // Left position
    slot1: { x: 0, y: -33 },  // Center position (arch peak)
    slot2: { x: 45, y: -5 },  // Right position
  });
  // Opponent-specific sidebet icon config (separate from player's)
  const [opponentSidebetIconConfig, setOpponentSidebetIconConfig] = useState({
    baseRadiusOffset: -217,  // Y offset from seat center (negative = toward dealer)
    scale: 100,              // Icon scale percentage
    slot0: { x: -45, y: -5 }, // Left position
    slot1: { x: 0, y: -33 },  // Center position (arch peak)
    slot2: { x: 45, y: -5 },  // Right position
  });

  // Per-seat sidebet icon offsets (applied ON TOP of global/opponent config)
  // radiusOffset: additional radial offset for this seat
  // x, y: additional tangential/radial offset for this seat
  const [sidebetIconConfigs, setSidebetIconConfigs] = useState<Record<number, { radiusOffset: number; x: number; y: number }>>({
      1: { radiusOffset: 95, x: 0, y: 0 },
      2: { radiusOffset: 38, x: -23, y: 1 },
      3: { radiusOffset: 42, x: -9, y: 0 },
      4: { radiusOffset: 0, x: 0, y: 0 },
      5: { radiusOffset: 47, x: 0, y: 0 },
      6: { radiusOffset: 70, x: -4, y: -11 },
      7: { radiusOffset: 100, x: 0, y: 0 },
    });

  // Panel controls - 4 vertical positions + bottom + top
  // viewport-left: Far left edge of viewport
  // table-left: Flushed to left edge of game table
  // table-right: Flushed to right edge of game table
  // viewport-right: Far right edge of viewport
  // Seeded from stored position to match PixiControlPanel's initial state
  const [panelPosition, setPanelPosition] = useState<ControlPanelPosition>(() => {
    if (tableId) {
      const stored = getStoredPosition(tableId);
      if (stored) {
        console.log('[PixiTablePage] Initializing panel position from storage:', stored);
        return stored;
      }
    }
    return 'bottom';
  });

  // Table image dimensions for accurate positioning
  // Canvas sized dynamically: table image (1244x794) + margins for UI elements
  const TABLE_IMAGE_WIDTH = 1244;
  const TABLE_IMAGE_HEIGHT = 794;
  const UI_BELOW_TABLE_MARGIN = 0;
  const UI_HORIZONTAL_MARGIN = 28;
  const CANVAS_WIDTH = TABLE_IMAGE_WIDTH + (UI_HORIZONTAL_MARGIN * 2); // 1300
  const CANVAS_HEIGHT = TABLE_IMAGE_HEIGHT + UI_BELOW_TABLE_MARGIN; // 850
  const CANVAS_ASPECT_RATIO = CANVAS_WIDTH / CANVAS_HEIGHT;
  const [tableImageWidth, setTableImageWidth] = useState<number>(CANVAS_WIDTH); // Default to full canvas width

  // Load table image to get actual dimensions
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      console.log(`[Table Image] Loaded: ${img.naturalWidth}x${img.naturalHeight}`);
      setTableImageWidth(img.naturalWidth);
    };
    img.src = tableImageUrl;
  }, []);

  // ===========================================
  // RESPONSIVE VIEWPORT STATE
  // ===========================================
  const [isSmallViewport, setIsSmallViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return Math.min(window.innerWidth, window.innerHeight) < SMALL_VIEWPORT_THRESHOLD;
  });
  const [forceRotation, setForceRotation] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isSmall = Math.min(w, h) < SMALL_VIEWPORT_THRESHOLD;
    const isPortrait = h > w;
    if (isSmall && isPortrait) {
      const angle = window.screen?.orientation?.angle ?? 0;
      return angle === 180 ? 90 : -90;
    }
    return 0;
  });
  const [effectiveViewport, setEffectiveViewport] = useState(() => {
    if (typeof window === 'undefined') return { width: 1300, height: 850 };
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isSmall = Math.min(w, h) < SMALL_VIEWPORT_THRESHOLD;
    // For small viewports, always use landscape dimensions
    return isSmall
      ? { width: Math.max(w, h), height: Math.min(w, h) }
      : { width: w, height: h };
  });
  // Calculate collapsible mode from viewport dimensions (effective height < 700)
  const [isCollapsibleMode, setIsCollapsibleMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isSmall = Math.min(w, h) < SMALL_VIEWPORT_THRESHOLD;
    const effHeight = isSmall ? Math.min(w, h) : h;
    return effHeight < SMALL_VIEWPORT_THRESHOLD;
  });
  // Header visibility is inverse of collapsible mode initially
  const [isHeaderVisible, setIsHeaderVisible] = useState(() => {
    if (typeof window === 'undefined') return true;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isSmall = Math.min(w, h) < SMALL_VIEWPORT_THRESHOLD;
    const effHeight = isSmall ? Math.min(w, h) : h;
    const collapsible = effHeight < SMALL_VIEWPORT_THRESHOLD;
    // In collapsible mode, header starts hidden
    return !collapsible;
  });
  // Physical viewport dimensions (for rotation wrapper)
  const [physicalViewport, setPhysicalViewport] = useState(() => {
    if (typeof window === 'undefined') return { width: 1300, height: 850 };
    return { width: window.innerWidth, height: window.innerHeight };
  });

  // Scorebox and outcome label controls
  const [scoreboxConfig, setScoreboxConfig] = useState({ scale: 150, fontSize: 19, x: 0, y: 0, minWidth: 35 });
  const [outcomeLabelConfig, setOutcomeLabelConfig] = useState({ scale: 100, fontSize: 15, x: 27, fontFamily: 'Arial' });

  // Control panel text size controls (percentage-based: 100 = 100%)
  const [panelTextSizes, setPanelTextSizes] = useState({
    mainButtonsVert: 175,   // Main buttons (HIT/STAND/BET) in vertical layout
    mainButtonsHoriz: 234,  // Main buttons in horizontal layout
    sideButtonsVert: 150,   // Side buttons (SPLIT/DOUBLE/etc) in vertical layout
    sideButtonsHoriz: 150,  // Side buttons in horizontal layout
  });

  // Card config controls
  const [cardConfig, setCardConfig] = useState({
    scale: 117,           // Overall card scale percentage
    cornerFontSize: 20,   // Corner rank/suit font size
    cornerSuitSize: 24,   // Corner suit symbol size
    centerSuitSize: 70,   // Center suit symbol size
    borderThickness: 1,   // Card border width
    seatCardsY: -5,       // Cards Y offset within seats (local coords)
    dealerCardsX: 0,      // Cards X offset for dealer (local coords) [LOCKED]
    dealerCardsY: 35,     // Cards Y offset for dealer (local coords) [LOCKED]
    cornerGap: -6,        // Vertical gap between rank and suit
  });

  // Per-seat card hand position configs (X, Y, radiusOffset for each seat)
  const [perSeatCardConfigs, setPerSeatCardConfigs] = useState<Record<number, { x: number; y: number; radiusOffset: number }>>({
      1: { x: 0, y: 0, radiusOffset: 41 },
      2: { x: 0, y: 0, radiusOffset: 3 },
      3: { x: 0, y: 0, radiusOffset: -11 },
      4: { x: 0, y: 0, radiusOffset: -1 },
      5: { x: 0, y: 0, radiusOffset: -10 },
      6: { x: 0, y: 0, radiusOffset: 3 },
      7: { x: 0, y: 0, radiusOffset: 41 },
    });

  // Dev mode: which seat is currently showing a test hand for positioning
  const [devCardSeat, setDevCardSeat] = useState<number | null>(null);

  // Game state store (need mySeatPosition early for devCardVisualPos calculation)
  const mySeatPosition = useGameStateStore((state) => state.mySeatPosition);

  // Convert selected dev seat to visual position (configs are keyed by visual position)
  // Visual position determines where on screen the seat appears (4 = center/bottom)
  const devCardVisualPos = devCardSeat !== null
    ? (mySeatPosition === null ? devCardSeat : ((devCardSeat - mySeatPosition + 10) % 7) + 1)
    : null;

  // Corner card configs (scorekeeper on left, modal replacement on right)
  const [leftCornerCardConfig, setLeftCornerCardConfig] = useState({
    x: -530,
    y: 550,
    rotation: 90,
    scale: 160,
  });
  const [rightCornerCardConfig, setRightCornerCardConfig] = useState({
    x: 530,
    y: 550,
    rotation: -90,
    scale: 160,
  });

  // Game state store
  const players = useGameStateStore((state) => state.players);
  const dealer = useDealer();
  const dealerPersona = useDealerPersona();
  // Note: mySeatPosition is declared earlier (needed for devCardVisualPos)
  const prevMySeatPositionRef = useRef<number | null>(null);

  // Track seat transitions for logging (staged bet restoration is handled in socket message handler)
  useEffect(() => {
    if (prevMySeatPositionRef.current === null && mySeatPosition !== null) {
      console.log('[PixiTablePage] Joined seat:', mySeatPosition);
    }
    prevMySeatPositionRef.current = mySeatPosition;
  }, [mySeatPosition]);

  const currentTurnSeat = useGameStateStore((state) => state.currentTurnSeat);
  const currentHandIndex = useGameStateStore((state) => state.currentHandIndex);
  const buttonPosition = useGameStateStore((state) => state.buttonPosition);
  const tableName = useGameStateStore((state) => state.tableName);
  const playerCount = useGameStateStore((state) => state.players.size);

  // Convert actual seat to visual seat position (rotated so player is always at visual position 4)
  const getVisualPositionForSeat = useCallback((actualSeat: number): number => {
    if (mySeatPosition === null) return actualSeat;
    return ((actualSeat - mySeatPosition + 10) % 7) + 1;
  }, [mySeatPosition]);

  // Computed dealer button seat: use DEV override if set, otherwise use server buttonPosition
  // Convert to VISUAL seat position (rotated so player sees themselves at position 4)
  // Only show when there are 2+ players (same logic as React table)
  const effectiveDealerButtonSeat = useMemo(() => {
    // DEV override takes precedence (0 = hide, 1-7 = specific visual seat)
    if (dealerButtonSeatOverride !== null) {
      return dealerButtonSeatOverride;
    }
    // Use server buttonPosition, but convert to visual seat position
    if (playerCount >= 2 && buttonPosition && buttonPosition >= 1 && buttonPosition <= 7) {
      return getVisualPositionForSeat(buttonPosition);
    }
    // Hide button otherwise
    return 0;
  }, [dealerButtonSeatOverride, buttonPosition, playerCount, getVisualPositionForSeat]);

  // Insurance state
  const insuranceOffered = useInsuranceOffered();
  const insuranceCountdown = useInsuranceCountdown();
  const myPlayerState = useMyPlayerState();
  const [insuranceDecisionMade, setInsuranceDecisionMade] = useState(false);
  const [insuranceDecisionPending, setInsuranceDecisionPending] = useState(false); // Waiting for server ack (local)
  const storeInsuranceDecisionPending = useInsuranceDecisionPending(); // Store value (set by autoplay)

  // Socket store
  const socket = useSocketStore((state) => state.socket);
  const isConnected = useSocketStore((state) => state.isConnected);

  // Local state
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // Track whether seat tutorial has been shown this session (for PixiHowToBetModal)
  const seatTutorialShownRef = useRef(false);

  // SidePanel state (stats, chat)
  const [stats, setStats] = useState<StatsData | null>(null);
  const [chatMessages, setChatMessages] = useState<{ username: string; message: string; isSystem?: boolean }[]>([]);

  // Active sidebets for SidePanel indicator
  const mySidebetConfigs = useGameStateStore((state) => state.mySidebetConfigs);
  // Normalize Over/Under 13 keys (over-under-13-over -> over-under-13) for SidePanel display
  const activeSidebetIds = useMemo(() => {
    return Array.from(mySidebetConfigs.keys()).map(key => {
      if (key.startsWith('over-under-13-')) {
        return 'over-under-13';
      }
      return key;
    });
  }, [mySidebetConfigs]);

  // ===========================================
  // SYNC SIDEBET CONFIGS TO SERVER (ROBUST)
  // ===========================================
  // Ensures server's seat.lastSidebetConfigs is always up-to-date for autobet.
  // Called: (1) when configs change, (2) on reconnect/rejoin, (3) after table_joined

  // Force sync function - can be called from anywhere
  const syncSidebetConfigsToServer = useCallback((force: boolean = false) => {
    const configs = useGameStateStore.getState().mySidebetConfigs;
    const configsArray = Array.from(configs.entries()).map(([sidebetType, config]) => ({
      sidebetType,
      percentage: config.percentage,
      rounds: config.rounds,
    }));

    // Get socket from store and check connection
    const currentSocket = useSocketStore.getState().socket;
    if (!currentSocket?.connected) {
      console.log('[PixiTablePage] Socket not connected, skipping sidebet config sync');
      return;
    }

    // Skip empty configs unless forced
    if (configsArray.length === 0 && !force) {
      return;
    }

    // Send sync message to server
    console.log('[PixiTablePage] Syncing sidebet configs to server:', configsArray, force ? '(forced)' : '');
    currentSocket.emit('client_message', {
      type: 'sync_sidebet_configs',
      configs: configsArray,
    });
  }, []);

  // Expose sync function via ref so it can be called from table_joined handler
  const syncSidebetConfigsRef = useRef(syncSidebetConfigsToServer);
  syncSidebetConfigsRef.current = syncSidebetConfigsToServer;

  // Auto-sync when configs change
  const prevSidebetConfigsRef = useRef<string>('');
  const hasEverSyncedRef = useRef(false);
  useEffect(() => {
    // Serialize current configs for comparison
    const configsArray = Array.from(mySidebetConfigs.entries()).map(([sidebetType, config]) => ({
      sidebetType,
      percentage: config.percentage,
      rounds: config.rounds,
    }));
    const serialized = JSON.stringify(configsArray);

    // Skip if configs haven't changed (avoids duplicate syncs)
    if (serialized === prevSidebetConfigsRef.current) {
      return;
    }

    const hadConfigs = prevSidebetConfigsRef.current !== '' && prevSidebetConfigsRef.current !== '[]';
    prevSidebetConfigsRef.current = serialized;

    // Don't sync empty configs on initial mount (before any configs were set)
    // BUT DO sync empty configs if we previously had configs (user terminated all)
    if (configsArray.length === 0 && !hasEverSyncedRef.current) {
      return;
    }

    // Track that we've synced at least once
    if (configsArray.length > 0) {
      hasEverSyncedRef.current = true;
    }

    // Sync to server (including empty array to clear server-side configs)
    // Use force=true when syncing empty array (to clear server-side lastSidebetConfigs)
    const forceSync = configsArray.length === 0 && hasEverSyncedRef.current;
    console.log(`[PixiTablePage] Syncing sidebet configs: ${configsArray.length} configs, hadConfigs=${hadConfigs}, force=${forceSync}`);
    syncSidebetConfigsToServer(forceSync);
  }, [mySidebetConfigs, syncSidebetConfigsToServer]);

  // ===========================================
  // SYNC STAGED BET TO SERVER FOR AUTOBET
  // ===========================================
  // When staged bet changes, sync to server so autobet uses the updated amount
  useEffect(() => {
    // Don't sync on initial mount with 0 amount
    if (stagedAmount === 0 && stagedChips.length === 0) {
      return;
    }

    // Get socket from store and check connection
    const currentSocket = useSocketStore.getState().socket;
    if (!currentSocket?.connected) {
      return;
    }

    // Debounce the sync to avoid spamming server during rapid chip clicks
    const debounceTimer = setTimeout(() => {
      console.log('[PixiTablePage] Syncing staged bet to server:', stagedAmount);
      currentSocket.emit('client_message', {
        type: 'sync_staged_bet',
        amount: stagedAmount,
        betChips: stagedChips,
      });
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [stagedAmount, stagedChips]);

  // Get setMySeat action from store
  const setMySeat = useGameStateStore((state) => state.setMySeat);

  // ===========================================
  // RESPONSIVE VIEWPORT DETECTION
  // ===========================================
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    // Core calculation logic - separated so it can be called immediately or debounced
    const calculateViewport = () => {
      const physicalWidth = window.innerWidth;
      const physicalHeight = window.innerHeight;
      const smallerDimension = Math.min(physicalWidth, physicalHeight);
      const largerDimension = Math.max(physicalWidth, physicalHeight);
      const isSmall = smallerDimension < SMALL_VIEWPORT_THRESHOLD;
      const isPortrait = physicalHeight > physicalWidth;

      // Track physical viewport for rotation wrapper
      setPhysicalViewport({ width: physicalWidth, height: physicalHeight });
      setIsSmallViewport(isSmall);

      // Calculate force rotation for portrait mode on small devices
      if (isSmall && isPortrait) {
        const angle = window.screen?.orientation?.angle ?? 0;
        // Rotate to landscape: 0° portrait → -90°, 180° portrait → 90°
        setForceRotation(angle === 180 ? 90 : -90);
      } else {
        setForceRotation(0);
      }

      // Effective viewport: always landscape for small viewports
      if (isSmall) {
        setEffectiveViewport({ width: largerDimension, height: smallerDimension });
      } else {
        setEffectiveViewport({ width: physicalWidth, height: physicalHeight });
      }

      // Collapsible mode when effective height < 700
      const effHeight = isSmall ? smallerDimension : physicalHeight;
      setIsCollapsibleMode(effHeight < SMALL_VIEWPORT_THRESHOLD);
    };

    // Debounced handler for resize events (prevents layout thrashing during resize)
    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(calculateViewport, 50);
    };

    // Run initial calculation IMMEDIATELY (no debounce) so canvas gets correct dimensions
    calculateViewport();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Modern orientation API
    const orientationApi = window.screen?.orientation;
    if (orientationApi) {
      orientationApi.addEventListener('change', handleResize);
    }

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (orientationApi) {
        orientationApi.removeEventListener('change', handleResize);
      }
    };
  }, []);

  // Auto-switch panel to viewport-right when entering small viewport mode
  useEffect(() => {
    if (isSmallViewport && panelPosition === 'bottom') {
      setPanelPosition('viewport-right');
    }
  }, [isSmallViewport, panelPosition]);

  // Auto-hide header when entering collapsible mode
  useEffect(() => {
    if (isCollapsibleMode) {
      setIsHeaderVisible(false);
    } else {
      setIsHeaderVisible(true);
    }
  }, [isCollapsibleMode]);

  // ===========================================
  // SOCKET MESSAGE HANDLING FOR SEAT DETECTION
  // ===========================================
  // Listen for table_joined/table_state to detect when we're seated

  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleServerMessage = (message: { type: string; config?: { cardDeal: number; cardFlip: number; chipFlight: number; chipsPaid: number; chipsCollected: number; cardsCleared: number; splitHands: number; sidebetPlaced: number; sidebetChipsPaid: number; sidebetChipsCollected: number }; stagger?: { chipPlacement: number; chipPayout: number; chipCollection: number; chipToDealer: number; chipMidRoundBust: number; cardRake: number; sidebetChip: number; insuranceChip: number; doubleDownChip: number; betConfirmChip: number }; table?: { seats?: Array<{ seatIndex: number; userId?: string; totalBet?: number; hands?: Array<{ bet?: number; betChips?: number[] }> } | null> }; stats?: StatsData; username?: string; message?: string }) => {
      // Handle animation_config - server sends this BEFORE table_joined
      // Client caches these values for the session and uses them as fallbacks
      if (message.type === 'animation_config' && message.config && message.stagger) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const msg = message as any;
        console.log('[PixiTablePage] Received animation_config from server:', {
          config: msg.config,
          stagger: msg.stagger,
          uiDelays: msg.uiDelays,
          timers: msg.timers,
          pauses: msg.pauses,
        });
        // Call setConfig with all timing configs from server
        useAnimationConfigStore.getState().setConfig(
          msg.config,
          msg.stagger,
          msg.uiDelays ?? { insuranceChipWait: 300, doubleDownChipWait: 400, chipConsolidationSwap: 200 },
          msg.timers ?? { bettingInvisiblePhase: 20000, bettingVisiblePhase: 10000, maxConsecutiveSitOuts: 3, insuranceInvisiblePhase: 20000, insuranceVisiblePhase: 10000, turnInvisiblePhase: 20000, turnVisiblePhase: 10000 },
          msg.pauses ?? { afterDeal: 400, afterBlackjackCheck: 200, afterBust: 300, afterDealerReveal: 400, afterResolution: 300, betweenRounds: 600, clientEventProcessing: 100 }
        );
      }

      // Process table_joined and table_state to find our seat
      if ((message.type === 'table_joined' || message.type === 'table_state') && message.table?.seats) {
        console.log('[PixiTablePage] Checking seats for userId:', user.id);
        for (const seat of message.table.seats) {
          console.log('[PixiTablePage] Seat:', seat?.seatIndex, 'userId:', seat?.userId);
          if (seat && seat.userId === user.id) {
            const seatPosition = seat.seatIndex + 1; // Convert 0-indexed to 1-indexed
            console.log('[PixiTablePage] Found my seat:', seatPosition);

            // Restore or clear staged bet based on existing bet state
            // Only restore on table_joined (initial join/reconnect), not table_state updates
            if (message.type === 'table_joined') {
              // Check active bet first, then fall back to lastBet (for page refresh during betting phase)
              const existingBet = seat.totalBet || seat.hands?.[0]?.bet || 0;
              const existingBetChips = seat.hands?.[0]?.betChips || [];
              const lastBet = (seat as { lastBet?: number }).lastBet || 0;
              const lastBetChips = (seat as { lastBetChips?: number[] }).lastBetChips || [];

              // Debug: log what we received from server
              console.log('[PixiTablePage] table_joined bet state:', {
                totalBet: seat.totalBet,
                handBet: seat.hands?.[0]?.bet,
                betChips: seat.hands?.[0]?.betChips,
                existingBet,
                existingBetChips,
                lastBet,
                lastBetChips,
              });

              if (existingBet > 0) {
                // Restore staged bet from server (reconnection scenario during active hand)
                // If betChips is empty, create a breakdown from the amount
                const chipsToRestore = existingBetChips.length > 0
                  ? existingBetChips
                  : breakdownChips(existingBet);
                console.log('[PixiTablePage] Restoring staged bet on rejoin:', existingBet, chipsToRestore);
                useStagedChipsStore.getState().setStagedAmount(existingBet);
                useStagedChipsStore.getState().setStagedChips(chipsToRestore);
              } else if (lastBet > 0) {
                // Restore from lastBet (page refresh during betting phase after round ended)
                const chipsToRestore = lastBetChips.length > 0
                  ? lastBetChips
                  : breakdownChips(lastBet);
                console.log('[PixiTablePage] Restoring staged bet from lastBet:', lastBet, chipsToRestore);
                useStagedChipsStore.getState().setStagedAmount(lastBet);
                useStagedChipsStore.getState().setStagedChips(chipsToRestore);
              } else {
                // No bet data from server - check if this is a reconnection
                // On reconnection, preserve staged chips from sessionStorage
                // On fresh join (hasJoinedRef was false before), clear them
                const stagedState = useStagedChipsStore.getState();
                if (hasJoinedRef.current && stagedState.stagedAmount > 0) {
                  // Reconnection with existing staged chips - preserve them
                  console.log('[PixiTablePage] Reconnection, preserving staged chips:', stagedState.stagedAmount);
                } else {
                  // Fresh join with no existing bet - clear staged chips
                  console.log('[PixiTablePage] New join, clearing staged chips');
                  useStagedChipsStore.getState().clearAll();
                }
              }

              // Restore sidebet configs from server state
              // Server sends sidebets as: { sidebetType: { amount, percentage, rounds, ... } }
              const sidebetsObj = (seat as { sidebets?: Record<string, { amount: number; percentage: number; rounds: number }> }).sidebets;
              if (sidebetsObj && Object.keys(sidebetsObj).length > 0) {
                console.log('[PixiTablePage] Restoring sidebet configs:', sidebetsObj);
                const gameState = useGameStateStore.getState();
                // Clear existing configs first (avoid duplicates)
                gameState.clearSidebetConfigs();
                // Restore each sidebet config
                for (const [sidebetType, sidebet] of Object.entries(sidebetsObj)) {
                  if (sidebet.percentage && sidebet.rounds !== undefined) {
                    gameState.setSidebetConfig(sidebetType, sidebet.percentage, sidebet.rounds);
                    console.log(`[PixiTablePage] Restored sidebet config: ${sidebetType}, ${sidebet.percentage}%, ${sidebet.rounds === -1 ? 'ongoing' : sidebet.rounds + ' rounds'}`);
                  }
                }
              }

              // ROBUST SYNC: Always sync sidebet configs to server on table_joined
              // This ensures seat.lastSidebetConfigs is set for autobet even if:
              // - Server has configs (just restored) - re-sync to ensure consistency
              // - Server has no configs but client has local configs (reconnect scenario)
              // - Client has configs from a previous session
              // Use setTimeout to ensure state updates have propagated
              setTimeout(() => {
                const localConfigs = useGameStateStore.getState().mySidebetConfigs;
                if (localConfigs.size > 0) {
                  console.log('[PixiTablePage] table_joined: Force-syncing sidebet configs to server');
                  syncSidebetConfigsRef.current?.(true);
                }
              }, 100);

              // Restore autobetEnabled from server state
              const serverAutobetEnabled = (seat as { autobetEnabled?: boolean }).autobetEnabled;
              if (serverAutobetEnabled !== undefined) {
                console.log('[PixiTablePage] Restoring autobetEnabled from server:', serverAutobetEnabled);
                useStagedChipsStore.getState().setAutobetEnabled(serverAutobetEnabled);
              }

              // Show seat tutorial modal on first seat join (if not hidden by preference)
              // Only show once per session (not on reconnects/refreshes)
              // Use setTimeout to ensure PixiJS modal is created (depends on loading state)
              console.log('[PixiTablePage] How To Bet check:', {
                seatTutorialShownRef: seatTutorialShownRef.current,
                hideSeatTutorial: user?.hideSeatTutorial,
                shouldShow: !seatTutorialShownRef.current && !user?.hideSeatTutorial,
              });
              if (!seatTutorialShownRef.current && !user?.hideSeatTutorial) {
                seatTutorialShownRef.current = true;
                setTimeout(() => {
                  console.log('[PixiTablePage] Showing How To Bet modal, pixiRef exists:', !!pixiRef.current);
                  pixiRef.current?.showHowToBetModal();
                }, 500);
              }
            } else {
              // On table_state updates, also sync autobetEnabled from server
              // This ensures client stays in sync after toggle_autobet
              const serverAutobetEnabled = (seat as { autobetEnabled?: boolean }).autobetEnabled;
              const localAutobetEnabled = useStagedChipsStore.getState().autobetEnabled;
              if (serverAutobetEnabled !== undefined && serverAutobetEnabled !== localAutobetEnabled) {
                console.log('[PixiTablePage] Syncing autobetEnabled from server state:', serverAutobetEnabled);
                useStagedChipsStore.getState().setAutobetEnabled(serverAutobetEnabled);
              }
            }

            setMySeat(seatPosition);
            return;
          }
        }
        // If we didn't find ourselves in any seat, we're not seated
        // Don't clear mySeatPosition here - it might just be a state update for other players
      }

      // Handle stats update
      if (message.type === 'stats_update' && message.stats) {
        setStats(message.stats);
      }

      // Handle chat messages
      if (message.type === 'chat_message' && message.username && message.message) {
        setChatMessages((prev) => [...prev, { username: message.username!, message: message.message! }]);
      }

      // Handle system messages
      if (message.type === 'system_message' && message.message) {
        setChatMessages((prev) => [...prev, { username: 'System', message: message.message!, isSystem: true }]);
      }
    };

    socket.on('server_message', handleServerMessage);
    return () => {
      socket.off('server_message', handleServerMessage);
    };
  }, [socket, user?.id, setMySeat]);

  // ===========================================
  // GAME EVENTS INTEGRATION
  // ===========================================
  // Connect useGameEvents hook to trigger PixiJS animations

  useGameEvents({
    tableId: tableId || '',

    // Card dealt animation
    // Card is passed directly from the event to avoid race conditions with game state updates
    // duration is provided by server via event.duration (server controls timing)
    onCardDealt: async (target, seatPosition, handIndex, cardIndex, card, duration) => {
      console.log('[PixiTablePage] onCardDealt:', target, seatPosition, handIndex, cardIndex, card?.rank, card?.suit, 'duration:', duration);

      // Debug to on-screen log
      const w = window as unknown as { pixiDebugLog?: (m: string) => void };
      w.pixiDebugLog?.(`[onCardDealt] target=${target} seat=${seatPosition} hand=${handIndex} cardIdx=${cardIndex} card=${card?.rank}${card?.suit} dur=${duration ?? 'default'}`);

      if (!pixiRef.current) {
        w.pixiDebugLog?.(`[onCardDealt] SKIPPED - pixiRef not ready`);
        return;
      }

      if (!card) {
        console.warn('[PixiTablePage] Card not provided in event');
        return;
      }

      // Trigger PixiJS deal animation using card data from event (not state)
      // Pass server-provided duration to animation (undefined = use default)
      if (target === 'dealer') {
        await pixiRef.current.dealCardToDealer(card, cardIndex, duration);

        // Show insurance badges when dealer's up card (cardIndex 0) is an Ace
        if (cardIndex === 0 && card.rank === 'A') {
          console.log('[PixiTablePage] Dealer Ace landed - showing insurance badges');
          const gameState = useGameStateStore.getState();
          console.log('[PixiTablePage] Players in state:', gameState.players.size);
          gameState.players.forEach(player => {
            if (!player) return;
            const hasActiveHand = player.hands?.some(h => h.cards?.length > 0) ?? false;
            // Don't show badge if player already made their insurance decision
            const alreadyDecided = player.insuranceDecided ?? false;
            console.log('[PixiTablePage] Player badge check:', {
              seatPosition: player.seatPosition,
              handsCount: player.hands?.length,
              cardsInHand0: player.hands?.[0]?.cards?.length,
              hasActiveHand,
              alreadyDecided,
            });
            if (hasActiveHand && !alreadyDecided) {
              console.log('[PixiTablePage] Calling showInsuranceBadge for seat:', player.seatPosition);
              pixiRef.current?.showInsuranceBadge(player.seatPosition);
              diagLog.ui({ element: 'INSURANCE_BADGE', action: 'SHOW', seat: player.seatPosition, source: 'SYSTEM', details: { trigger: 'dealer_ace_landed' } });
            }
          });
        }
      } else if (seatPosition !== undefined) {
        // For split hands, navigate carousel to target hand BEFORE dealing the card
        // This ensures the card is visible when it lands
        const totalHands = pixiRef.current.getTotalHands(seatPosition);
        const currentViewedHand = pixiRef.current.getCurrentViewedHand(seatPosition);
        if (totalHands > 1 && currentViewedHand !== handIndex) {
          console.log(`[PixiTablePage] Split hand: navigating to hand ${handIndex} before card deal`);
          await pixiRef.current.navigateToHand(seatPosition, handIndex);
        }

        await pixiRef.current.dealCardToSeat(seatPosition, card, cardIndex, handIndex, duration);
      }
    },

    // Dealer hits animation
    onDealerHits: async (cardIndex, duration) => {
      console.log('[PixiTablePage] onDealerHits:', cardIndex, 'duration:', duration);

      if (!pixiRef.current) return;

      const gameState = useGameStateStore.getState();
      const card = gameState.dealer.cards[cardIndex];

      if (card) {
        await pixiRef.current.dealCardToDealer(card, cardIndex, duration);
      }
    },

    // Dealer reveals hole card
    onDealerRevealsHole: async (flipDuration?: number) => {
      console.log(`[PixiTablePage] onDealerRevealsHole, flipDuration: ${flipDuration ?? 'default'}`);
      await pixiRef.current?.revealHoleCard(flipDuration);
    },

    // Dealer has natural blackjack - straighten dealer hand (no DEALER_STANDS event fires)
    onDealerBlackjack: async () => {
      console.log('[PixiTablePage] onDealerBlackjack - straightening dealer hand');
      await pixiRef.current?.straightenDealerCards();
    },

    // Dealer stands - straighten dealer hand after final card
    onDealerStands: async () => {
      console.log('[PixiTablePage] onDealerStands - straightening dealer hand');
      await pixiRef.current?.straightenDealerCards();
    },

    // Dealer busts - straighten dealer hand after final card
    onDealerBusts: async () => {
      console.log('[PixiTablePage] onDealerBusts - straightening dealer hand');
      await pixiRef.current?.straightenDealerCards();
    },

    // DEPRECATED: onChipsPaid replaced by onSeatResolution
    // Frontend now uses SEAT_RESOLUTION event for coordinated split hand resolution
    // TODO: Remove this handler entirely once SEAT_RESOLUTION is fully tested
    // onChipsPaid: async (seatPosition, handIndex, amount, duration) => {
    //   console.log('[PixiTablePage] onChipsPaid:', seatPosition, handIndex, amount, 'duration:', duration ?? 'default');
    //
    //   if (!pixiRef.current || amount <= 0) return;
    //
    //   // For split hands, navigate carousel to target hand BEFORE chip animation
    //   const totalHands = pixiRef.current.getTotalHands(seatPosition);
    //   const currentViewedHand = pixiRef.current.getCurrentViewedHand(seatPosition);
    //   if (totalHands > 1 && currentViewedHand !== handIndex) {
    //     console.log(`[PixiTablePage] Split hand: navigating to hand ${handIndex} before chips paid`);
    //     await pixiRef.current.navigateToHand(seatPosition, handIndex);
    //   }
    //
    //   // Cap $10k chips at 100 to prevent performance issues with large wins
    //   const rawChips = breakdownChips(amount);
    //   const { chips: winningsChips } = capChipsForAnimation(rawChips);
    //   // Arrivals should be low-to-high (1$ → 10K)
    //   const winningsLowToHigh = [...winningsChips].reverse();
    //
    //   // Step 1: Dealer pays winnings - chips from dealer tray to player kitty (staggered starts)
    //   console.log('[PixiTablePage] onChipsPaid - Step 1: winnings to kitty', winningsLowToHigh);
    //   const winningsPromises: Promise<void>[] = [];
    //   winningsLowToHigh.forEach((denom, i) => {
    //     const promise = new Promise<void>((resolve) => {
    //       setTimeout(async () => {
    //         await pixiRef.current?.animateChipFromDealer(seatPosition, denom);
    //         resolve();
    //       }, i * DEFAULT_STAGGER_CONFIG.chipPayout);
    //     });
    //     winningsPromises.push(promise);
    //   });
    //   await Promise.all(winningsPromises);
    //
    //   // Step 2: Get ALL chips in kitty (original bet + winnings just added) - pass handIndex for split hand support
    //   const allKittyChips = pixiRef.current.getKittyChips(seatPosition, handIndex);
    //   console.log('[PixiTablePage] onChipsPaid - Step 2: all kitty chips', allKittyChips, 'handIndex:', handIndex);
    //
    //   // Step 3: Player collects ALL chips from kitty to rail (staggered starts)
    //   // Depart in descending order (10K → 1$)
    //   // Use decreasing zIndex so first-departing chip (highest denom) stays on top
    //   const chipsHighToLow = [...allKittyChips].reverse();
    //   console.log('[PixiTablePage] onChipsPaid - Step 3: kitty to rail', chipsHighToLow);
    //   const collectPromises: Promise<void>[] = [];
    //   chipsHighToLow.forEach((denom, i) => {
    //     const promise = new Promise<void>((resolve) => {
    //       setTimeout(async () => {
    //         // First chip (i=0) gets highest zIndex, subsequent chips get decreasing zIndex
    //         await pixiRef.current?.animateChipFromKitty(seatPosition, denom, 2000 - i);
    //         resolve();
    //       }, i * DEFAULT_STAGGER_CONFIG.chipCollection);
    //     });
    //     collectPromises.push(promise);
    //   });
    //   await Promise.all(collectPromises);
    //
    //   // Step 4: Ensure kitty is cleared (safety) - pass handIndex for split hand support
    //   pixiRef.current.clearKitty(seatPosition, handIndex);
    //
    //   // Mark animations complete for autobet
    //   useAnimationQueueStore.getState().setMyHandAnimationsComplete(true);
    // },

    // DEPRECATED: onChipsCollected replaced by onSeatResolution
    // Frontend now uses SEAT_RESOLUTION event for coordinated split hand resolution
    // TODO: Remove this handler entirely once SEAT_RESOLUTION is fully tested
    // onChipsCollected: async (seatPosition, handIndex, amount, duration) => {
    //   console.log('[PixiTablePage] onChipsCollected:', seatPosition, handIndex, amount, 'duration:', duration ?? 'default');
    //
    //   if (!pixiRef.current) return;
    //
    //   // For split hands, navigate carousel to target hand BEFORE chip animation
    //   const totalHands = pixiRef.current.getTotalHands(seatPosition);
    //   const currentViewedHand = pixiRef.current.getCurrentViewedHand(seatPosition);
    //   if (totalHands > 1 && currentViewedHand !== handIndex) {
    //     console.log(`[PixiTablePage] Split hand: navigating to hand ${handIndex} before chips collected`);
    //     await pixiRef.current.navigateToHand(seatPosition, handIndex);
    //   }
    //
    //   // Get all chips in kitty (the bet chips) - pass handIndex for split hand support
    //   const kittyChips = pixiRef.current.getKittyChips(seatPosition, handIndex);
    //   console.log('[PixiTablePage] onChipsCollected - kitty chips:', kittyChips, 'handIndex:', handIndex);
    //
    //   if (kittyChips.length === 0) {
    //     console.warn(`[PixiTablePage] onChipsCollected - NO CHIPS in kitty for seat=${seatPosition} hand=${handIndex}`);
    //     return;
    //   }
    //
    //   // Cap $10k chips at 100 to prevent performance issues with large bets
    //   const { chips: cappedChips } = capChipsForAnimation(kittyChips);
    //
    //   // Animate ALL chips from kitty to dealer tray (staggered starts)
    //   // Depart in descending order (10K → 1$)
    //   // Use decreasing zIndex so first-departing chip (highest denom) stays on top
    //   const chipsHighToLow = [...cappedChips].reverse();
    //   const promises: Promise<void>[] = [];
    //   chipsHighToLow.forEach((denom, i) => {
    //     const promise = new Promise<void>((resolve) => {
    //       setTimeout(async () => {
    //         // First chip (i=0) gets highest zIndex, subsequent chips get decreasing zIndex
    //         await pixiRef.current?.animateChipToDealer(seatPosition, denom, 2000 - i);
    //         resolve();
    //       }, i * DEFAULT_STAGGER_CONFIG.chipToDealer);
    //     });
    //     promises.push(promise);
    //   });
    //   await Promise.all(promises);
    //
    //   // Ensure kitty is cleared (safety) - pass handIndex for split hand support
    //   pixiRef.current.clearKitty(seatPosition, handIndex);
    //
    //   // Mark animations complete for autobet
    //   useAnimationQueueStore.getState().setMyHandAnimationsComplete(true);
    // },

    // DEPRECATED: onChipsReturned replaced by onSeatResolution
    // Frontend now uses SEAT_RESOLUTION event for coordinated split hand resolution
    // TODO: Remove this handler entirely once SEAT_RESOLUTION is fully tested
    // onChipsReturned: async (seatPosition, handIndex, amount) => {
    //   console.log('[PixiTablePage] onChipsReturned:', seatPosition, handIndex, amount);
    //
    //   if (!pixiRef.current) return;
    //
    //   // For split hands, navigate carousel to target hand BEFORE chip animation
    //   const totalHands = pixiRef.current.getTotalHands(seatPosition);
    //   const currentViewedHand = pixiRef.current.getCurrentViewedHand(seatPosition);
    //   if (totalHands > 1 && currentViewedHand !== handIndex) {
    //     console.log(`[PixiTablePage] Split hand: navigating to hand ${handIndex} before chips returned`);
    //     await pixiRef.current.navigateToHand(seatPosition, handIndex);
    //   }
    //
    //   // Get all chips in kitty (the bet chips) - pass handIndex for split hand support
    //   const kittyChips = pixiRef.current.getKittyChips(seatPosition, handIndex);
    //   console.log('[PixiTablePage] onChipsReturned - kitty chips:', kittyChips, 'handIndex:', handIndex);
    //
    //   if (kittyChips.length === 0) {
    //     console.warn(`[PixiTablePage] onChipsReturned - NO CHIPS in kitty for seat=${seatPosition} hand=${handIndex}`);
    //     return;
    //   }
    //
    //   // Cap $10k chips at 100 to prevent performance issues with large bets
    //   const { chips: cappedChips } = capChipsForAnimation(kittyChips);
    //
    //   // Animate ALL chips from kitty to rail (staggered starts)
    //   // Depart in descending order (10K → 1$)
    //   // Use decreasing zIndex so first-departing chip (highest denom) stays on top
    //   const chipsHighToLow = [...cappedChips].reverse();
    //   const promises: Promise<void>[] = [];
    //   chipsHighToLow.forEach((denom, i) => {
    //     const promise = new Promise<void>((resolve) => {
    //       setTimeout(async () => {
    //         // First chip (i=0) gets highest zIndex, subsequent chips get decreasing zIndex
    //         await pixiRef.current?.animateChipFromKitty(seatPosition, denom, 2000 - i);
    //         resolve();
    //       }, i * DEFAULT_STAGGER_CONFIG.chipCollection);
    //     });
    //     promises.push(promise);
    //   });
    //   await Promise.all(promises);
    //
    //   // Ensure kitty is cleared (safety) - pass handIndex for split hand support
    //   pixiRef.current.clearKitty(seatPosition, handIndex);
    //
    //   useAnimationQueueStore.getState().setMyHandAnimationsComplete(true);
    // },

    // Mid-round bust - dealer collects ALL chips from kitty immediately
    onMidRoundBust: async (seatPosition, handIndex, betAmount) => {
      console.log('[PixiTablePage] onMidRoundBust:', seatPosition, handIndex, betAmount);

      if (!pixiRef.current) return;

      // For split hands, navigate carousel to target hand BEFORE chip animation
      const totalHands = pixiRef.current.getTotalHands(seatPosition);
      const currentViewedHand = pixiRef.current.getCurrentViewedHand(seatPosition);
      if (totalHands > 1 && currentViewedHand !== handIndex) {
        console.log(`[PixiTablePage] Split hand: navigating to hand ${handIndex} before mid-round bust`);
        await pixiRef.current.navigateToHand(seatPosition, handIndex);
      }

      // Get all chips in kitty (the bet chips) - pass handIndex for split hand support
      const kittyChips = pixiRef.current.getKittyChips(seatPosition, handIndex);
      console.log('[PixiTablePage] onMidRoundBust - kitty chips:', kittyChips, 'handIndex:', handIndex);

      if (kittyChips.length === 0) {
        console.warn(`[PixiTablePage] onMidRoundBust - NO CHIPS in kitty for seat=${seatPosition} hand=${handIndex}`);
        return;
      }

      // Cap $10k chips at 100 to prevent performance issues with large bets
      const { chips: cappedChips } = capChipsForAnimation(kittyChips);

      // Animate ALL chips from kitty to dealer tray (staggered starts)
      // Depart in descending order (10K → 1$)
      // Use decreasing zIndex so first-departing chip (highest denom) stays on top
      const chipsHighToLow = [...cappedChips].reverse();
      const promises: Promise<void>[] = [];
      chipsHighToLow.forEach((denom, i) => {
        const promise = new Promise<void>((resolve) => {
          setTimeout(async () => {
            // First chip (i=0) gets highest zIndex, subsequent chips get decreasing zIndex
            // Pass handIndex to support split hands - chips come from correct kitty
            await pixiRef.current?.animateChipToDealer(seatPosition, denom, handIndex, 2000 - i);
            resolve();
          }, i * DEFAULT_STAGGER_CONFIG.chipMidRoundBust);
        });
        promises.push(promise);
      });
      await Promise.all(promises);

      // Ensure kitty is cleared (safety) - pass handIndex for split hand support
      pixiRef.current.clearKitty(seatPosition, handIndex);

      // Recenter remaining kitties after removing the busted hand's chips
      // This fills the gap left by the cleared kitty
      if (totalHands > 1) {
        console.log(`[PixiTablePage] Mid-round bust: recentering remaining kitties for seat ${seatPosition}`);
        pixiRef.current.recenterSplitKitties(seatPosition);
      }

      useAnimationQueueStore.getState().setMyHandAnimationsComplete(true);
    },

    // ===========================================
    // SEAT_RESOLUTION - Single event for all hand outcomes
    // Frontend iterates hands in REVERSE order (4→3→2→1)
    // Outcome labels are shown synchronized with carousel rotation
    // ===========================================
    onSeatResolution: async (event) => {
      const { seatPosition, hands } = event;
      console.log(`[PixiTablePage] onSeatResolution: seat=${seatPosition}, ${hands.length} hands`);

      if (!pixiRef.current) return;

      // Process hands in REVERSE order (Hand4 → Hand3 → Hand2 → Hand1)
      for (let i = hands.length - 1; i >= 0; i--) {
        const hand = hands[i];
        console.log(`[RESOLUTION] Processing hand ${hand.handIndex}: ${hand.outcome}`);

        // Navigate carousel to this hand WITH outcome label (both labels animate together)
        const currentHand = pixiRef.current.getCurrentViewedHand(seatPosition);
        if (currentHand !== hand.handIndex) {
          // Pass outcome to navigateToHand so outcome label flies in synchronized with hand label
          console.log(`[RESOLUTION] Navigate to hand ${hand.handIndex} with outcome=${hand.outcome} (synchronized labels)`);
          await pixiRef.current.navigateToHand(seatPosition, hand.handIndex, hand.outcome);
        } else {
          // Already viewing this hand - show outcome label separately
          console.log(`[RESOLUTION] Already at hand ${hand.handIndex}, showing outcome label: ${hand.outcome}`);
          await pixiRef.current.showOutcomeLabel(seatPosition, hand.handIndex, hand.outcome);
        }

        // Play appropriate chip animation based on outcome
        switch (hand.outcome) {
          case 'win':
          case 'blackjack': {
            // Animate winnings to kitty, then all chips to rail
            const rawChips = breakdownChips(hand.payout ?? 0);
            const { chips: winningsChips } = capChipsForAnimation(rawChips);
            const winningsLowToHigh = [...winningsChips].reverse();

            // Step 1: Dealer pays winnings to kitty (parallel with stagger)
            // Pass handIndex to target the correct kitty (main vs split)
            const winPromises: Promise<void>[] = [];
            for (let j = 0; j < winningsLowToHigh.length; j++) {
              const promise = new Promise<void>(resolve => {
                setTimeout(async () => {
                  await pixiRef.current?.animateChipFromDealer(seatPosition, winningsLowToHigh[j], hand.handIndex);
                  resolve();
                }, j * DEFAULT_STAGGER_CONFIG.chipPayout);
              });
              winPromises.push(promise);
            }
            await Promise.all(winPromises);

            // Step 2: All chips from kitty to rail (parallel with stagger)
            const allKittyChips = pixiRef.current.getKittyChips(seatPosition, hand.handIndex);
            const chipsHighToLow = [...allKittyChips].reverse();
            const collectPromises: Promise<void>[] = [];
            for (let j = 0; j < chipsHighToLow.length; j++) {
              const promise = new Promise<void>(resolve => {
                setTimeout(async () => {
                  // Pass handIndex to support split hands - chips come from correct kitty
                  await pixiRef.current?.animateChipFromKitty(seatPosition, chipsHighToLow[j], hand.handIndex, 2000 - j);
                  resolve();
                }, j * DEFAULT_STAGGER_CONFIG.chipCollection);
              });
              collectPromises.push(promise);
            }
            await Promise.all(collectPromises);
            pixiRef.current.clearKitty(seatPosition, hand.handIndex);
            break;
          }

          case 'lose':
          case 'bust':
          case 'surrender': {
            // Dealer collects chips from kitty
            const kittyChips = pixiRef.current.getKittyChips(seatPosition, hand.handIndex);
            const { chips: cappedChips } = capChipsForAnimation(kittyChips);
            const chipsHighToLow = [...cappedChips].reverse();

            // Create all promises with staggered timeouts, then await all (parallel execution)
            const lossPromises: Promise<void>[] = [];
            for (let j = 0; j < chipsHighToLow.length; j++) {
              const promise = new Promise<void>(resolve => {
                setTimeout(async () => {
                  // Pass handIndex to support split hands - chips come from correct kitty
                  await pixiRef.current?.animateChipToDealer(seatPosition, chipsHighToLow[j], hand.handIndex, 2000 - j);
                  resolve();
                }, j * DEFAULT_STAGGER_CONFIG.chipToDealer);
              });
              lossPromises.push(promise);
            }
            await Promise.all(lossPromises);
            pixiRef.current.clearKitty(seatPosition, hand.handIndex);
            break;
          }

          case 'push': {
            // Return chips from kitty to rail
            const kittyChips = pixiRef.current.getKittyChips(seatPosition, hand.handIndex);
            const { chips: cappedChips } = capChipsForAnimation(kittyChips);
            const chipsHighToLow = [...cappedChips].reverse();

            // Create all promises with staggered timeouts, then await all (parallel execution)
            const pushPromises: Promise<void>[] = [];
            for (let j = 0; j < chipsHighToLow.length; j++) {
              const promise = new Promise<void>(resolve => {
                setTimeout(async () => {
                  // Pass handIndex to support split hands - chips come from correct kitty
                  await pixiRef.current?.animateChipFromKitty(seatPosition, chipsHighToLow[j], hand.handIndex, 2000 - j);
                  resolve();
                }, j * DEFAULT_STAGGER_CONFIG.chipCollection);
              });
              pushPromises.push(promise);
            }
            await Promise.all(pushPromises);
            pixiRef.current.clearKitty(seatPosition, hand.handIndex);
            break;
          }
        }
      }

      console.log('[RESOLUTION] Complete, carousel on Hand1');
      useAnimationQueueStore.getState().setMyHandAnimationsComplete(true);
    },

    // All resolutions complete - enable betting
    onAllResolutionsComplete: async () => {
      console.log('[PixiTablePage] onAllResolutionsComplete');
      useAnimationQueueStore.getState().setMyHandAnimationsComplete(true);
    },

    // Round started
    onRoundStarted: async (roundNumber, buttonPosition) => {
      console.log('[SIDEBET DEBUG] onRoundStarted - round:', roundNumber);

      // Clear any stale sidebet visuals from previous round
      console.log('[SIDEBET DEBUG] Clearing all sidebet visuals...');
      pixiRef.current?.clearAllSidebetVisuals();

      // NOTE: Don't decrement here! Rounds are decremented when sidebet is actually PLACED
      // (in onSidebetPlaced callback). This prevents over-decrementing when user doesn't bet.

      // Restore amber "configured" indicators for remaining sidebet configs
      const remainingConfigs = useGameStateStore.getState().mySidebetConfigs;
      console.log('[SIDEBET DEBUG] remainingConfigs:', remainingConfigs ? Array.from(remainingConfigs.entries()) : 'undefined');

      if (remainingConfigs && remainingConfigs.size > 0) {
        console.log('[SIDEBET DEBUG] Restoring visuals for', remainingConfigs.size, 'configs');
        pixiRef.current?.restoreConfiguredSidebetVisuals(remainingConfigs);
      } else {
        console.log('[SIDEBET DEBUG] No configs to restore');
      }
    },

    // Insurance accepted - animate chips from rail to insurance kitty
    onInsuranceAccepted: async (seatPosition, amount) => {
      console.log('[PixiTablePage] onInsuranceAccepted:', seatPosition, amount);

      if (!pixiRef.current || amount <= 0) return;

      // Break down amount into chip denominations
      const rawChips = breakdownChips(amount);
      const { chips } = capChipsForAnimation(rawChips);

      // Animate each chip to insurance kitty with stagger (parallel, not sequential)
      for (let i = 0; i < chips.length; i++) {
        setTimeout(() => {
          pixiRef.current?.animateRailToInsuranceKitty(seatPosition, chips[i]);
        }, i * DEFAULT_STAGGER_CONFIG.insuranceChip);
      }

      // Wait for all animations to complete
      await new Promise(resolve => setTimeout(resolve, chips.length * DEFAULT_STAGGER_CONFIG.insuranceChip + DEFAULT_UI_DELAY_CONFIG.insuranceChipWait));
    },

    // Insurance resolved - animate win (chips to rail) or loss (chips to dealer)
    onInsuranceResolved: async (seatPosition, dealerHasBlackjack, payoutAmount, insuranceBet) => {
      console.log('[PixiTablePage] onInsuranceResolved:', seatPosition, 'dealerHasBlackjack:', dealerHasBlackjack, 'payout:', payoutAmount, 'bet:', insuranceBet);

      if (!pixiRef.current) return;

      if (dealerHasBlackjack && payoutAmount > 0) {
        // Insurance wins: animate chips from insurance kitty to rail (with payout)
        await pixiRef.current.animateInsuranceWin(seatPosition, insuranceBet, payoutAmount);
      } else if (insuranceBet > 0) {
        // Insurance loses: animate chips from insurance kitty to dealer
        await pixiRef.current.animateInsuranceLoss(seatPosition, insuranceBet);
      }
    },

    // Double down
    onDoubleDown: async (seatPosition, handIndex, additionalBet) => {
      console.log('[PixiTablePage] onDoubleDown:', seatPosition, handIndex, additionalBet);

      if (!pixiRef.current || additionalBet <= 0) return;

      // Cap $10k chips at 100 to prevent performance issues
      const rawChips = breakdownChips(additionalBet);
      const { chips } = capChipsForAnimation(rawChips);
      for (let i = 0; i < chips.length; i++) {
        setTimeout(() => {
          pixiRef.current?.animateRailToKitty(seatPosition, chips[i]);
        }, i * DEFAULT_STAGGER_CONFIG.doubleDownChip);
      }

      await new Promise(resolve => setTimeout(resolve, chips.length * DEFAULT_STAGGER_CONFIG.doubleDownChip + DEFAULT_UI_DELAY_CONFIG.doubleDownChipWait));
    },

    // Split - animate card separation and chips from rail to new hand's kitty
    onSplit: async (seatPosition, originalHandIndex, newHandIndex, additionalBet) => {
      console.log('[PixiTablePage] onSplit:', seatPosition, originalHandIndex, newHandIndex, additionalBet);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onSplit: pixiRef not ready');
        return;
      }

      // Get the actual bet chips from game state (for accurate denomination breakdown)
      const gameState = useGameStateStore.getState();
      const player = gameState.players.get(seatPosition);
      const originalHand = player?.hands[originalHandIndex];
      const rawBetChips = originalHand?.betChips || breakdownChips(additionalBet);
      const { chips: betChips } = capChipsForAnimation(rawBetChips);

      console.log('[PixiTablePage] Split bet chips:', betChips);

      // Initialize split hand mode (total hands = newHandIndex + 1)
      pixiRef.current.setSplitHandCount(seatPosition, newHandIndex + 1);

      // Step 1: Animate the second card sliding to the new hand position
      await pixiRef.current.animateSplitCardSeparation(seatPosition, originalHandIndex, newHandIndex, 600);

      // Step 2: Animate chips from rail to new hand's kitty
      await pixiRef.current.animateSplitAction(seatPosition, originalHandIndex, newHandIndex, betChips);

      console.log('[PixiTablePage] Split action animation complete');
    },

    // Surrender - FULL chip stack animates to BOTH dealer AND rail simultaneously
    // Visual effect: chips appear to "duplicate" - one set to dealer, one to rail
    onSurrender: async (seatPosition, handIndex, betAmount, _refundAmount) => {
      console.log('[PixiTablePage] onSurrender:', seatPosition, handIndex, betAmount);

      if (!pixiRef.current) return;

      // Get the ACTUAL bet chips from game state (avoids denomination mismatch)
      const gameState = useGameStateStore.getState();
      const player = gameState.players.get(seatPosition);
      const hand = player?.hands[handIndex];
      const rawBetChips = hand?.betChips || breakdownChips(betAmount);
      // Cap $10k chips at 100 to prevent performance issues
      const { chips: betChips } = capChipsForAnimation(rawBetChips);

      console.log('[PixiTablePage] Surrender chips:', betChips);

      // Use dedicated surrender animation that creates clones for both destinations
      await pixiRef.current.animateSurrenderChips(seatPosition, betChips);

      console.log('[PixiTablePage] Surrender animation complete');
    },

    // Dealing started - rake remaining cards (must complete before dealing begins)
    onDealingStarted: async () => {
      console.log('[OOO DEBUG] onDealingStarted START - removing expired sidebets');
      // Remove any sidebets that expired last round (badge showed "0")
      pixiRef.current?.removeExpiredSidebets();

      // FALLBACK RAKE: Rake all player cards that weren't raked by SEAT_RAKE
      // In single-player mode, SEAT_RAKE may not fire if backend state was already cleared
      // This ensures any remaining Pixi cards are cleaned up before new cards are dealt
      console.log('[OOO DEBUG] onDealingStarted - fallback rake for all player seats');
      const seatRakePromises: Promise<void>[] = [];
      for (let seat = 1; seat <= 7; seat++) {
        seatRakePromises.push(pixiRef.current?.rakeCardsForSeat(seat) ?? Promise.resolve());
      }
      // Wait for ALL seat rakes to complete FIRST (important for split hands which take longer)
      await Promise.all(seatRakePromises);
      console.log('[OOO DEBUG] onDealingStarted - all seat rakes complete');

      // THEN rake dealer cards (must happen AFTER all player cards are raked for proper visual order)
      await pixiRef.current?.rakeDealerCards();
      console.log('[OOO DEBUG] onDealingStarted - dealer rake complete');

      // Flip scorecard back to card-back when new round dealing starts
      console.log('[SCORECARD] Flipping scorecard back on DEALING_STARTED');
      await pixiRef.current?.flipScorecard(false);

      // Retry failed avatar loads (self-heal transient network failures)
      pixiRef.current?.retryFailedAvatars();

      console.log('[OOO DEBUG] onDealingStarted END');
    },

    // Player turns started - straighten dealer hand before turn beam activates
    onPlayerTurnsStarted: async () => {
      console.log('[PixiTablePage] onPlayerTurnsStarted - straightening dealer hand');
      await pixiRef.current?.straightenDealerCards();
    },

    // Insurance offered - straighten dealer hand before insurance panel shows
    onInsuranceOffered: async () => {
      console.log('[PixiTablePage] onInsuranceOffered - straightening dealer hand before insurance panel');
      await pixiRef.current?.straightenDealerCards();
    },

    // Player sit out - rake their cards
    onPlayerSitOut: async (seatPosition) => {
      console.log('[PixiTablePage] onPlayerSitOut:', seatPosition);
      // TODO: Rake cards for specific seat
    },

    // Server commands to rake a SPECIFIC seat before bet confirmation
    // SEAT_RAKE arrives BEFORE BET_CONFIRMED to ensure: rake → chip animation
    // NEW ARCHITECTURE: Surgical per-seat clearing (never clearTable!)
    onSeatRake: async (seatPosition) => {
      console.log('[OOO DEBUG] ========== onSeatRake START ==========');
      console.log('[OOO DEBUG] onSeatRake - seatPosition:', seatPosition);

      if (!pixiRef.current) {
        console.warn('[OOO DEBUG] onSeatRake: pixiRef not ready, skipping');
        return;
      }

      // SURGICAL: Only rake THIS seat's cards, not the entire table
      // This prevents clearing chips/cards from players who haven't bet yet
      console.log('[OOO DEBUG] onSeatRake - RAKE START (surgical per-seat rake)');
      await pixiRef.current.rakeCardsForSeat(seatPosition);
      console.log('[OOO DEBUG] onSeatRake - RAKE COMPLETE');

      // Reset split hand state after rake (cleanup for new round)
      pixiRef.current.resetSplitHandState(seatPosition);
      console.log('[OOO DEBUG] onSeatRake - SPLIT STATE RESET');

      // NOTE: Scorecard flip is NOT triggered here. Flip is synchronized with
      // outcome label animations (flip to front on label fly-in, flip to back on label fly-out)

      console.log('[OOO DEBUG] ========== onSeatRake END ==========');
    },

    // Current user's bet confirmed - animate chips to kitty
    // SEAT_RAKE has already cleared previous round, this just handles chip animation
    onBetConfirmed: async (seatPosition, amount, betChips) => {
      console.log('[OOO DEBUG] ========== onBetConfirmed START ==========');

      if (!pixiRef.current) {
        console.warn('[OOO DEBUG] onBetConfirmed: pixiRef not ready, skipping animation');
        return;
      }

      // Clear previous kitty for this seat (sync - ensures clean slate for new bet)
      console.log('[OOO DEBUG] onBetConfirmed - clearKitty (sync)');
      pixiRef.current.clearKitty(seatPosition);

      // Animate bet chips to kitty
      const { chips: cappedBetChips } = capChipsForAnimation(betChips);
      const chipsLowToHigh = [...cappedBetChips].reverse();
      console.log('[OOO DEBUG] onBetConfirmed - CHIPS ANIMATE START', chipsLowToHigh.length, 'chips');

      const chipAnimationPromises: Promise<void>[] = [];
      chipsLowToHigh.forEach((denom, i) => {
        const promise = new Promise<void>((resolve) => {
          setTimeout(async () => {
            await pixiRef.current?.animateRailToKitty(seatPosition, denom);
            resolve();
          }, i * DEFAULT_STAGGER_CONFIG.betConfirmChip);
        });
        chipAnimationPromises.push(promise);
      });

      await Promise.all(chipAnimationPromises);
      console.log('[OOO DEBUG] onBetConfirmed - CHIPS ANIMATE COMPLETE');
      console.log('[OOO DEBUG] ========== onBetConfirmed END ==========');
    },

    // Opponent bet confirmed - animate chips from avatar to kitty (Pixi-based, not DOM)
    onOpponentBetConfirmed: async (seatPosition, amount, betChips) => {
      console.log(`[PixiTablePage] onOpponentBetConfirmed: seat=${seatPosition}, amount=$${amount}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentBetConfirmed: pixiRef not ready');
        return;
      }

      await pixiRef.current.animateOpponentBetToKitty(seatPosition, amount, betChips);
    },

    // Opponent payout - chips from dealer to opponent kitty (Pixi-based)
    onOpponentPayoutToKitty: async (seatPosition, chips) => {
      console.log(`[PixiTablePage] onOpponentPayoutToKitty: seat=${seatPosition}, chips=${chips.join(',')}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentPayoutToKitty: pixiRef not ready');
        return;
      }

      await pixiRef.current.animatePayoutToOpponentKitty(seatPosition, chips);
    },

    // Opponent win/push collection - chips from kitty to avatar (Pixi-based)
    onOpponentKittyToAvatar: async (seatPosition, chips) => {
      console.log(`[PixiTablePage] onOpponentKittyToAvatar: seat=${seatPosition}, chips=${chips.join(',')}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentKittyToAvatar: pixiRef not ready');
        return;
      }

      await pixiRef.current.animateOpponentKittyToAvatar(seatPosition, chips);
    },

    // Opponent loss - chips from kitty to dealer (Pixi-based)
    onOpponentLossToDealer: async (seatPosition, chips) => {
      console.log(`[PixiTablePage] onOpponentLossToDealer: seat=${seatPosition}, chips=${chips.join(',')}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentLossToDealer: pixiRef not ready');
        return;
      }

      await pixiRef.current.animateOpponentLossToDealer(seatPosition, chips);
    },

    // Opponent hand straightened - straighten cards for opponent's seat (Pixi-based)
    onOpponentHandStraightened: async (seatPosition, handIndex) => {
      console.log(`%c[OPPONENT STRAIGHTEN] seat=${seatPosition}, handIndex=${handIndex}`, 'background: #9C27B0; color: white; font-weight: bold; padding: 2px 6px;');

      // Log received event
      diagLog.recv({ seq: -1, type: 'OPPONENT_STRAIGHTEN', seat: seatPosition, hand: handIndex });

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentHandStraightened: pixiRef not ready');
        return;
      }

      // Log animation start
      const animId = `opponent-straighten-seat-${seatPosition}-hand-${handIndex}`;
      diagLog.animStart({ id: animId, type: 'OPPONENT_STRAIGHTEN', seat: seatPosition, hand: handIndex });

      console.log(`[OPPONENT STRAIGHTEN] Calling straightenSeatCards...`);
      await pixiRef.current.straightenSeatCards(seatPosition, handIndex);

      // Log animation end
      diagLog.animEnd({ id: animId, type: 'OPPONENT_STRAIGHTEN', seat: seatPosition, hand: handIndex });
      console.log(`[OPPONENT STRAIGHTEN] Complete`);
    },

    // ===========================================
    // PIXI-ONLY OPPONENT ANIMATIONS
    // ===========================================
    // These callbacks ensure ALL opponent animations are Pixi-based (no DOM fallbacks)

    // Opponent seat rake - rake cards for specific opponent seat (not full table clear)
    onOpponentSeatRake: async (seatPosition) => {
      console.log(`[PixiTablePage] onOpponentSeatRake: seat=${seatPosition}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentSeatRake: pixiRef not ready');
        return;
      }

      await pixiRef.current.rakeCardsForSeat(seatPosition);

      // Reset split hand state after rake (cleanup for new round)
      pixiRef.current.resetSplitHandState(seatPosition);
    },

    // Dealer rake - clears dealer cards after ALL players have raked
    // NEW ARCHITECTURE: Fires when last player clicks [BET], before dealing starts
    onDealerRake: async () => {
      console.log('[PixiTablePage] onDealerRake: raking dealer cards');

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onDealerRake: pixiRef not ready');
        // Still send ACK so server doesn't wait forever
        if (socket && tableId) {
          console.log('[PixiTablePage] onDealerRake: Sending rake_complete ACK (pixiRef not ready)');
          socket.emit('client_message', { type: 'rake_complete', tableId });
        }
        return;
      }

      // GUARD: If new round has already started dealing, don't rake those cards.
      // This can happen when DEALER_RAKE was superseded but still processes after
      // new cards have been dealt (e.g., after split hands take longer to rake).
      const currentGameState = useGameStateStore.getState();
      const currentPhase = currentGameState.phase;
      if (currentPhase === GamePhase.DEALING || currentPhase === GamePhase.PLAYING || currentPhase === GamePhase.DEALER_TURN) {
        console.log(`[PixiTablePage] onDealerRake: SKIPPING - new round already in phase '${currentPhase}'`);
        // Still send ACK to unblock server if it's waiting (e.g., timed-out but processing in parallel)
        if (socket && tableId) {
          console.log('[PixiTablePage] onDealerRake: Sending rake_complete ACK (skipped)');
          socket.emit('client_message', { type: 'rake_complete', tableId });
        }
        return;
      }

      // Animate dealer button to new position in parallel with dealer card rake
      // Read button position from store (already updated by ROUND_STARTED)
      const gameState = useGameStateStore.getState();
      const actualButtonSeat = gameState.buttonPosition;
      const mySeat = gameState.mySeatPosition;
      const playerCount = gameState.players.size;

      // Calculate visual seat (rotated so player is at position 4)
      let targetVisualSeat = 0;
      if (playerCount >= 2 && actualButtonSeat >= 1 && actualButtonSeat <= 7 && mySeat !== null) {
        targetVisualSeat = ((actualButtonSeat - mySeat + 10) % 7) + 1;
      } else if (playerCount >= 2 && actualButtonSeat >= 1 && actualButtonSeat <= 7) {
        targetVisualSeat = actualButtonSeat; // No rotation if not seated
      }

      console.log(`[PixiTablePage] onDealerRake: button position - actual=${actualButtonSeat}, visual=${targetVisualSeat}, playerCount=${playerCount}`);

      // Run dealer card rake and button animation in parallel
      await Promise.all([
        pixiRef.current.rakeDealerCards(),
        pixiRef.current.moveDealerButton(targetVisualSeat),
      ]);

      // ACK: Tell server that rake animations are complete, safe to deal
      if (socket && tableId) {
        console.log('[PixiTablePage] onDealerRake: Sending rake_complete ACK to server');
        socket.emit('client_message', { type: 'rake_complete', tableId });
      }
    },

    // Opponent accepts insurance - animate chips from avatar to insurance kitty
    onOpponentInsuranceAccepted: async (seatPosition, amount, chips) => {
      console.log(`[PixiTablePage] onOpponentInsuranceAccepted: seat=${seatPosition}, amount=$${amount}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentInsuranceAccepted: pixiRef not ready');
        return;
      }

      // Animate chips from avatar to insurance kitty using existing method
      // (animateRailToInsuranceKitty works for any seat, just sources from avatar for opponents)
      const { chips: cappedChips } = capChipsForAnimation(chips);
      for (const denom of cappedChips) {
        await pixiRef.current.animateRailToInsuranceKitty(seatPosition, denom);
      }
    },

    // Opponent insurance resolved - animate win (to avatar) or loss (to dealer)
    onOpponentInsuranceResolved: async (seatPosition, dealerHasBlackjack, insuranceChips, payoutChips) => {
      console.log(`[PixiTablePage] onOpponentInsuranceResolved: seat=${seatPosition}, dealerHasBJ=${dealerHasBlackjack}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentInsuranceResolved: pixiRef not ready');
        return;
      }

      const insuranceAmount = insuranceChips.reduce((sum, c) => sum + c, 0);
      const payoutAmount = payoutChips.reduce((sum, c) => sum + c, 0);

      if (dealerHasBlackjack) {
        // Insurance win: animate payout to insurance kitty, then all to rail
        await pixiRef.current.animateInsuranceWin(seatPosition, insuranceAmount, payoutAmount);
      } else {
        // Insurance loss: animate chips to dealer
        await pixiRef.current.animateInsuranceLoss(seatPosition, insuranceAmount);
      }
    },

    // Opponent doubles down - animate additional bet chips to EXISTING kitty
    onOpponentDoubleDown: async (seatPosition, handIndex, chips) => {
      console.log(`[PixiTablePage] onOpponentDoubleDown: seat=${seatPosition}, hand=${handIndex}, chips=${chips.join(',')}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentDoubleDown: pixiRef not ready');
        return;
      }

      // For double down, animate additional bet chips from avatar to EXISTING kitty
      // Uses dedicated function that preserves existing kitty state
      await pixiRef.current.animateOpponentDoubleDownChips(seatPosition, chips);
    },

    // Opponent splits - animate additional bet chips to new hand's kitty
    onOpponentSplit: async (seatPosition, newHandIndex, chips) => {
      console.log(`[PixiTablePage] onOpponentSplit: seat=${seatPosition}, newHand=${newHandIndex}, chips=${chips.join(',')}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentSplit: pixiRef not ready');
        return;
      }

      // Initialize split hand mode for opponent (total hands = newHandIndex + 1)
      pixiRef.current.setSplitHandCount(seatPosition, newHandIndex + 1);

      // Animate split action - for opponents, chips come from avatar to new hand's kitty
      // We use the standard animateSplitAction which handles kitty creation
      await pixiRef.current.animateSplitAction(seatPosition, 0, newHandIndex, chips);
    },

    // Opponent surrenders - animate half to dealer, half to avatar SIMULTANEOUSLY
    onOpponentSurrender: async (seatPosition, handIndex, lossChips, refundChips) => {
      console.log(`[PixiTablePage] onOpponentSurrender: seat=${seatPosition}, loss=${lossChips.join(',')}, refund=${refundChips.join(',')}`);

      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onOpponentSurrender: pixiRef not ready');
        return;
      }

      // Animate surrender - loss to dealer AND refund to avatar SIMULTANEOUSLY
      // Uses dedicated function that mirrors player surrender pattern
      await pixiRef.current.animateOpponentSurrenderChips(seatPosition, lossChips, refundChips);
    },

    // Hide insurance badge immediately when any player makes a decision (accept or decline)
    onInsuranceDecision: (seatPosition) => {
      console.log(`[PixiTablePage] onInsuranceDecision: seat=${seatPosition} - hiding badge`);
      pixiRef.current?.hideInsuranceBadge(seatPosition);
    },

    // Active hand indicator for split scenarios (dims non-active hands)
    onActiveHandChange: (seatPosition, handIndex) => {
      console.log(`[PixiTablePage] onActiveHandChange: seat=${seatPosition} hand=${handIndex}`);
      pixiRef.current?.setActiveHandIndicator(seatPosition, handIndex);
    },

    // IMPERATIVE: Show outcome label directly via PixiJS (bypasses React render cycle)
    // Returns promise that resolves when fly-in animation completes
    // NOTE: Navigation is now handled by the caller (SEAT_RESOLUTION) - this just shows the label
    onShowOutcomeLabel: async (seatPosition, handIndex, outcome) => {
      console.log('[PixiTablePage] onShowOutcomeLabel:', seatPosition, handIndex, outcome);
      if (!pixiRef.current) {
        console.warn('[PixiTablePage] onShowOutcomeLabel: pixiRef not ready');
        return;
      }

      // NOTE: Carousel navigation removed - handled by SEAT_RESOLUTION before showing label
      // This allows outcome labels to be synchronized with hand label animations

      // Show outcome label animation
      await pixiRef.current.showOutcomeLabel(seatPosition, handIndex, outcome);
    },

    // Use arrival clones approach (PixiJS doesn't need DOM clones)
    useArrivalClones: true,

    // PixiJS scene health check - called every round during ROUND_COMPLETED
    // Detects and logs scene corruption from memory leaks or extended play
    onEnsurePixiSceneHealth: () => {
      if (!pixiRef.current) return null;
      return pixiRef.current.ensurePixiSceneHealth();
    },

    // Scorecard: Server-driven scorecard content update and flip
    // Called when SCORECARD_UPDATE event is received for the PLAYER's seat
    // Outcome labels for ALL seats are now handled directly in useGameEvents via onShowOutcomeLabel
    onScorecardUpdate: async (hands, sidebets, insurance, totalNetResult, newBalance) => {
      const callbackStartTime = Date.now();
      console.log(`[SCORECARD TIMING] STEP 5a: onScorecardUpdate callback started at ${callbackStartTime}`);
      console.log(`[SCORECARD] onScorecardUpdate: hands=${hands.length}, sidebets=${sidebets.length}, insurance=${!!insurance}, total=${totalNetResult}, newBalance=${newBalance}`);

      if (!pixiRef.current) {
        console.warn('[SCORECARD] onScorecardUpdate: pixiRef not ready');
        return;
      }

      // Update scorecard content with server-provided data
      const contentUpdateStart = Date.now();
      pixiRef.current.updateScorecardContent(hands, sidebets, insurance, totalNetResult);
      const contentUpdateEnd = Date.now();
      console.log(`[SCORECARD TIMING] STEP 5b: Content update took ${contentUpdateEnd - contentUpdateStart}ms`);

      // Flip scorecard to show results
      // Outcome label is shown separately via onShowOutcomeLabel (for all seats including player)
      const flipStart = Date.now();
      console.log(`[SCORECARD TIMING] STEP 5c: Starting scorecard flip animation at ${flipStart}`);
      await pixiRef.current.flipScorecard(true);
      const flipEnd = Date.now();
      console.log(`[SCORECARD TIMING] STEP 5c: Flip animation complete at ${flipEnd}, duration=${flipEnd - flipStart}ms`);
      console.log(`[SCORECARD TIMING] STEP 5 TOTAL: Callback took ${flipEnd - callbackStartTime}ms (content=${contentUpdateEnd - contentUpdateStart}ms, flip=${flipEnd - flipStart}ms)`);
    },

    // Sync dealt card counts and scores for mid-hand joins
    // This initializes MODULE_DEALT_CARD_COUNTS and MODULE_SCORE_HISTORY
    onSyncDealtCounts: (seatCardCounts, dealerCardCount, seatScores, dealerScore) => {
      if (!pixiRef.current) return;
      pixiRef.current.syncDealtCounts(seatCardCounts, dealerCardCount, seatScores, dealerScore);
    },

    // Sidebet callbacks
    onSidebetPlaced: async (seatPosition, sidebetType, amount, rounds, duration, stagger) => {
      console.log(`[SIDEBET DEBUG] onSidebetPlaced - seat: ${seatPosition}, type: ${sidebetType}, amount: $${amount}, rounds: ${rounds}, duration: ${duration ?? 'default'}, stagger: ${stagger ?? 'default'}`);

      // Ensure icon exists at player seat (may already exist from local optimistic update)
      const slotIndex = pixiRef.current?.addSidebetToSeat(seatPosition, sidebetType, amount, rounds);
      console.log(`[SIDEBET DEBUG] addSidebetToSeat returned slot: ${slotIndex}`);

      // Animate green "active" indicator for all players (including local)
      // Await the animation so sidebets can be sequenced properly
      console.log('[SIDEBET DEBUG] Calling animateSidebetPlacement...');
      await pixiRef.current?.animateSidebetPlacement(seatPosition, sidebetType, amount, rounds, duration, stagger);
      console.log('[SIDEBET DEBUG] animateSidebetPlacement complete');

      // Mark chips as placed for this sidebet (first half of a "complete round")
      // This ensures countdown only happens for rounds where chips were placed UNDER the current config
      // (fixes issue where changing from ONGOING → "10 rounds" mid-hand would count current hand)
      const mySeat = useGameStateStore.getState().mySeatPosition;
      if (seatPosition === mySeat) {
        useGameStateStore.getState().markSidebetChipsPlaced(sidebetType);
      }

      // NOTE: Decrement is now handled in onSidebetChipsPaid/onSidebetChipsCollected
      // when chips are resolved, not when sidebet is placed
    },

    onSidebetResolved: (seatPosition, sidebetType, outcome, winType, payout) => {
      console.log(`[PixiTablePage] Sidebet resolved: seat ${seatPosition}, ${sidebetType}, ${outcome}${winType ? ` (${winType})` : ''}, payout: ${payout}`);
      // Update game state store for current user
      const gameState = useGameStateStore.getState();
      if (gameState.mySeatPosition === seatPosition) {
        gameState.updateSidebetResolution(sidebetType, outcome, winType, payout);
      }
      // Trigger PixiJS visual feedback (win glow / loss dim)
      if (outcome === 'win') {
        pixiRef.current?.showSidebetWin(seatPosition, sidebetType, winType);
      } else {
        pixiRef.current?.showSidebetLoss(seatPosition, sidebetType);
      }
    },

    onSidebetChipsPaid: async (seatPosition, sidebetType, amount, chipBreakdown, duration, isFinal) => {
      console.log(`[PixiTablePage] Sidebet chips paid: seat ${seatPosition}, ${sidebetType}, $${amount}, duration: ${duration ?? 'default'}, serverChips: ${chipBreakdown?.totalChips ?? 'N/A'}, isFinal: ${isFinal ?? false}`);

      // SERVER-AUTHORITATIVE COUNTDOWN: No client decrement - server owns the countdown
      // Client only renders animations and retires icon when server signals isFinal

      // Trigger PixiJS chip animation from dealer to sidebet icon to player
      // chipBreakdown is server-authoritative - ensures client animation matches server timing
      await pixiRef.current?.animateSidebetWin(seatPosition, sidebetType, amount, chipBreakdown, duration);

      // SERVER-AUTHORITATIVE: Retire icon AFTER final payout animation completes
      if (isFinal) {
        console.log(`[PixiTablePage] isFinal=true - retiring sidebet icon for ${sidebetType} at seat ${seatPosition}`);
        pixiRef.current?.removeSidebetFromSeat(seatPosition, sidebetType);
        // Also clear the config from state since server already removed it
        useGameStateStore.getState().removeSidebetConfig(sidebetType);
      }
    },

    onSidebetChipsCollected: async (seatPosition, sidebetType, amount, duration, stagger, isFinal) => {
      console.log(`[PixiTablePage] Sidebet chips collected: seat ${seatPosition}, ${sidebetType}, $${amount}, duration: ${duration ?? 'default'}, stagger: ${stagger ?? 'default'}, isFinal: ${isFinal ?? false}`);

      // SERVER-AUTHORITATIVE COUNTDOWN: No client decrement - server owns the countdown
      // Client only renders animations and retires icon when server signals isFinal

      // Trigger PixiJS chip animation from sidebet icon to dealer
      await pixiRef.current?.animateSidebetLoss(seatPosition, sidebetType, amount, duration, stagger);

      // SERVER-AUTHORITATIVE: Retire icon AFTER final loss animation completes
      if (isFinal) {
        console.log(`[PixiTablePage] isFinal=true - retiring sidebet icon for ${sidebetType} at seat ${seatPosition}`);
        pixiRef.current?.removeSidebetFromSeat(seatPosition, sidebetType);
        // Also clear the config from state since server already removed it
        useGameStateStore.getState().removeSidebetConfig(sidebetType);
      }
    },

    // Called when a sidebet is removed (terminated by user or expired by server)
    onSidebetRemoved: (seatPosition, sidebetType) => {
      console.log(`[PixiTablePage] Sidebet removed: seat ${seatPosition}, ${sidebetType}`);
      // Remove the visual icon from the seat
      pixiRef.current?.removeSidebetFromSeat(seatPosition, sidebetType);
    },

    // Called after accepting a keyed resync - rebuild Pixi visuals from fresh state
    onKeyedResyncAccepted: () => {
      console.log('[PixiTablePage] onKeyedResyncAccepted - calling fullVisualResync and retryAutoPlay');

      // Log current state before resync
      const state = useGameStateStore.getState();
      const myPlayer = state.mySeatPosition ? state.players.get(state.mySeatPosition) : null;
      console.log('[PixiTablePage] onKeyedResyncAccepted state:', {
        phase: state.phase,
        mySeatPosition: state.mySeatPosition,
        currentTurnSeat: state.currentTurnSeat,
        myBet: myPlayer?.confirmedBet,
        myCards: myPlayer?.hands?.[0]?.cards?.length,
        dealerCards: state.dealer.cards.length,
      });

      pixiRef.current?.fullVisualResync();

      // RESTORE SIDEBET ICONS
      // mySidebets is preserved during clearGameStateForResync, so we can restore the icons
      const { mySidebets, mySeatPosition } = state;
      if (mySeatPosition && mySidebets.size > 0) {
        console.log('[PixiTablePage] Restoring sidebet icons:', Array.from(mySidebets.keys()));
        mySidebets.forEach((sidebet, sidebetType) => {
          if (!sidebet.resolved) {
            // Only restore unresolved sidebets (active bets)
            pixiRef.current?.addSidebetToSeat(mySeatPosition, sidebetType, sidebet.amount, sidebet.rounds);
          }
        });
      }

      // ROBUST RETRY: Event batch processing is async, so we need to retry multiple times
      // to ensure state has fully settled before auto-play attempts.
      // Retry at 50ms, 150ms, and 300ms to cover most batch processing scenarios.
      //
      // Flow: retryAutoPlay refreshes state and computes recommendation,
      //       then executePostResyncAutoplay actually sends the action.
      const retryDelays = [50, 150, 300];
      retryDelays.forEach((delay) => {
        setTimeout(() => {
          console.log(`[PixiTablePage] Post-resync autoplay retry (${delay}ms delay)`);
          // First, refresh state and compute recommendation in control panel
          pixiRef.current?.retryAutoPlay();
          // Then execute the autoplay action (since tryAutoPlay is disabled)
          executePostResyncAutoplay();
        }, delay);
      });
    },
  });

  // ===========================================
  // CHIP CLICK HANDLERS (PixiJS now handles rendering)
  // ===========================================

  // NOTE: Chip rail and staged display are now in PixiJS, so DOM-based animations
  // are no longer used. The staged chips store handles the state.

  // Handle chip click from PixiJS rail
  const handleFieldChipClick = useCallback((value: number) => {
    // Check affordability
    if (balance < value + stagedAmount) return;

    // Cancel any pending swap — we'll recalculate from the new total
    if (swapTimeoutRef.current) {
      clearTimeout(swapTimeoutRef.current);
      swapTimeoutRef.current = null;
    }

    const currentChips = useStagedChipsStore.getState().stagedChips;
    const newAmount = stagedAmount + value;
    const naiveChips = [...currentChips, value];
    const optimalChips = breakdownChips(newAmount);

    // Check if consolidation is needed by comparing naive vs optimal
    const needsSwap = JSON.stringify([...naiveChips].sort((a, b) => a - b)) !==
                      JSON.stringify([...optimalChips].sort((a, b) => a - b));

    const store = useStagedChipsStore.getState();
    store.setStagedAmount(newAmount);

    if (!needsSwap) {
      // No consolidation needed — update to optimal chips immediately
      store.setStagedChips(optimalChips);
    } else {
      // Consolidation needed!
      // 1. First show naive chips (including the just-added chip)
      store.setStagedChips(naiveChips);
      pendingChipsRef.current = naiveChips;

      // 2. After delay, trigger swap animation (update to optimal)
      swapTimeoutRef.current = setTimeout(() => {
        swapTimeoutRef.current = null;

        // Recalculate from current state (handles rapid clicks)
        const currentState = useStagedChipsStore.getState();
        const currentOptimal = breakdownChips(currentState.stagedAmount);

        // Update to optimal chips — this triggers re-render with consolidated chips
        currentState.setStagedChips(currentOptimal);
        pendingChipsRef.current = [];
      }, DEFAULT_UI_DELAY_CONFIG.chipConsolidationSwap);
    }

    // Final state check
    setTimeout(() => {
      const finalState = useStagedChipsStore.getState();
      console.log(`[CHIP CLICK DEBUG] FINAL STATE: amount=${finalState.stagedAmount}, chips=${JSON.stringify(finalState.stagedChips)}, chipSum=${finalState.stagedChips.reduce((a,b)=>a+b,0)}`);
    }, 50);
  }, [balance, stagedAmount]);

  // Handle clear from staged chips display (in PixiJS)
  // Note: displayedAmount will be decremented by animation callbacks as chips fly out
  const handleClear = useCallback(() => {
    // Cancel any pending swap
    if (swapTimeoutRef.current) {
      clearTimeout(swapTimeoutRef.current);
      swapTimeoutRef.current = null;
    }
    pendingChipsRef.current = [];
    useStagedChipsStore.getState().clearAll();
    // displayedAmount will tick down to 0 via animation callbacks
  }, []);

  // Handle click on own seat to straighten cards
  const handleStraightenCards = useCallback((seatPosition: number) => {
    // Get the currently viewed hand index for split hands (defaults to 0 if no split)
    const viewedHandIndex = pixiRef.current?.getCurrentViewedHand(seatPosition) ?? 0;

    // Log outgoing straighten action
    diagLog.action({ seat: seatPosition, action: 'STRAIGHTEN_CARDS', source: 'USER', details: { handIndex: viewedHandIndex } });

    // 1. Visual straightening (local) - only straighten the viewed hand
    pixiRef.current?.straightenSeatCards(seatPosition, viewedHandIndex);

    // 2. Notify server so other clients see straightening too
    // Server broadcasts 'hand_straightened' to all clients at the table
    if (socket && isConnected) {
      socket.emit('client_message', { type: 'straighten_hand', handIndex: viewedHandIndex });
      diagLog.sent({ type: 'straighten_hand', details: { seat: seatPosition, handIndex: viewedHandIndex } });
      console.log(`[PixiTablePage] Sent straighten_hand to server for seat ${seatPosition} hand ${viewedHandIndex}`);
    }
  }, [socket, isConnected]);

  // Simple send message helper (matches TablePage pattern)
  const sendMessage = useCallback((type: string, data: Record<string, unknown> = {}) => {
    if (socket && isConnected) {
      console.log(`[PixiTablePage] sendMessage: ${type}`, data);
      socket.emit('client_message', { type, ...data });
    } else {
      console.warn(`[PixiTablePage] sendMessage DROPPED: ${type}`);
    }
  }, [socket, isConnected]);

  // Helper to create a mock hand
  const createMockHand = (
    cards: { rank: string; suit: string }[],
    bet: number,
    outcome?: HandOutcome
  ): HandState => {
    const score = cards.reduce((sum, c) => {
      if (c.rank === 'A') return sum + 11;
      if (['K', 'Q', 'J'].includes(c.rank)) return sum + 10;
      return sum + parseInt(c.rank);
    }, 0);
    const hasAce = cards.some(c => c.rank === 'A');
    return {
      index: 0,
      cards: cards.map(c => ({ ...c, faceUp: true })) as HandState['cards'],
      bet,
      score: score > 21 && hasAce ? score - 10 : score,
      isSoft: hasAce && score <= 21,
      isDoubled: false,
      isSplit: false,
      isStanding: true,
      isBusted: score > 21,
      isBlackjack: cards.length === 2 && score === 21,
      isSurrendered: outcome === HandOutcome.SURRENDER,
      outcome,
    };
  };

  // Mock player data for positioning (with avatars) - no bets, use Show Bet Chips for chip reference
  const MOCK_PLAYERS: Record<number, PlayerState> = {
    1: {
      seatPosition: 1,
      displayName: 'Alice',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alice',
      hands: [createMockHand([{ rank: 'K', suit: 'hearts' }, { rank: '7', suit: 'spades' }], 0, HandOutcome.WIN)],
      currentHandIndex: 0,
    },
    2: {
      seatPosition: 2,
      displayName: 'Bob',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bob',
      hands: [createMockHand([{ rank: 'A', suit: 'spades' }, { rank: 'K', suit: 'diamonds' }], 0, HandOutcome.BLACKJACK)],
      currentHandIndex: 0,
    },
    3: {
      seatPosition: 3,
      displayName: 'Charlie',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Charlie',
      hands: [createMockHand([{ rank: '10', suit: 'clubs' }, { rank: '6', suit: 'hearts' }, { rank: '8', suit: 'diamonds' }], 0, HandOutcome.BUST)],
      currentHandIndex: 0,
    },
    4: {
      seatPosition: 4,
      displayName: 'Diana',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Diana',
      hands: [createMockHand([{ rank: 'Q', suit: 'hearts' }, { rank: '9', suit: 'clubs' }], 0)],
      currentHandIndex: 0,
    },
    5: {
      seatPosition: 5,
      displayName: 'Eve',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Eve',
      hands: [createMockHand([{ rank: 'J', suit: 'diamonds' }, { rank: '5', suit: 'spades' }], 0, HandOutcome.LOSE)],
      currentHandIndex: 0,
    },
    6: {
      seatPosition: 6,
      displayName: 'Frank',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Frank',
      hands: [createMockHand([{ rank: '8', suit: 'hearts' }, { rank: '8', suit: 'clubs' }], 0, HandOutcome.PUSH)],
      currentHandIndex: 0,
    },
    7: {
      seatPosition: 7,
      displayName: 'Grace',
      avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Grace',
      hands: [createMockHand([{ rank: '2', suit: 'spades' }, { rank: '3', suit: 'hearts' }, { rank: '6', suit: 'diamonds' }, { rank: '9', suit: 'clubs' }], 0, HandOutcome.SURRENDER)],
      currentHandIndex: 0,
    },
  };

  // Convert players Map to the format expected by PixiGameTableV1
  const pixiPlayers = useMemo(() => {
    const map = new Map<number, PlayerState>();

    // If showMockPlayers AND dev controls visible, use mock data for all 7 seats
    // Mock players only appear when dev panel is open
    if (showDevControls && showMockPlayers) {
      for (let seat = 1; seat <= 7; seat++) {
        map.set(seat, MOCK_PLAYERS[seat]);
      }
      return map;
    }

    // Otherwise use real players
    players.forEach((player, seat) => {
      map.set(seat, {
        seatPosition: seat,
        displayName: player.username || `P${seat}`,
        avatarUrl: player.avatarUrl || undefined,
        hands: player.hands || [],
        currentHandIndex: player.currentHandIndex || 0,
        insuranceBet: player.insuranceBet,
        confirmedBet: player.confirmedBet,
      });
    });
    return map;
  }, [players, showMockPlayers, showDevControls]);

  // Track previous player seats to detect departures
  const prevPlayerSeatsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    // Skip if mock players are active
    if (showDevControls && showMockPlayers) return;

    const currentSeats = new Set(players.keys());
    const prevSeats = prevPlayerSeatsRef.current;

    // Find seats that were occupied but are now empty (player left)
    const departedSeats: number[] = [];
    prevSeats.forEach(seat => {
      if (!currentSeats.has(seat)) {
        departedSeats.push(seat);
      }
    });

    // Clear visuals for departed players
    if (departedSeats.length > 0 && pixiRef.current) {
      console.log(`[PixiTablePage] Players departed from seats: ${departedSeats.join(', ')}`);
      departedSeats.forEach(seat => {
        pixiRef.current?.clearSeatVisuals(seat);
      });
    }

    // Update the ref for next comparison
    prevPlayerSeatsRef.current = currentSeats;
  }, [players, showMockPlayers, showDevControls]);

  // Mock dealer for positioning
  const MOCK_DEALER: DealerState = {
    cards: [
      { rank: 'A', suit: 'spades', faceUp: true },
      { rank: '6', suit: 'hearts', faceUp: false }, // Hole card face down
    ],
    score: 17,
    isSoft: true,
    holeCardRevealed: false,
    isBlackjack: false,
    isBusted: false,
    isStanding: false,
    visibleScore: 11,
    visibleCards: [{ rank: 'A', suit: 'spades', faceUp: true }],
  };

  // Use mock dealer when showMockPlayers AND dev controls visible
  const effectiveDealer = (showDevControls && showMockPlayers) ? MOCK_DEALER : dealer;

  // Get current player's hand info
  const myPlayer = mySeatPosition ? players.get(mySeatPosition) : null;
  const currentHand = myPlayer?.hands?.[currentHandIndex];
  const currentBet = currentHand?.bet || 0;
  const handHasOutcome = !!currentHand?.outcome;

  // NOTE: dynamicChipPositions and dynamicChipLabelConfig removed - chip rail now in PixiJS

  // Track if we've joined this table (survives HMR if component isn't remounted)
  const hasJoinedRef = useRef(false);

  // Track last reconnection join timestamp to prevent duplicate emissions
  // This guards against React StrictMode double-mounts and multiple onReconnect callbacks
  const lastReconnectJoinTimeRef = useRef<number>(0);
  const RECONNECT_DEBOUNCE_MS = 2000; // Ignore duplicate reconnection joins within 2 seconds

  // Track whether a reconnection join is in progress
  // Prevents HMR recovery and other effects from emitting duplicate join_table
  const reconnectionInProgressRef = useRef(false);

  // ===========================================
  // JOIN TABLE ON MOUNT (via Lifecycle Registry)
  // ===========================================
  // Uses tableLifecycleRegistry.enterTable() for consistent JOIN behavior.
  // Both fresh JOIN and RETURN from background use the same code path.
  useEffect(() => {
    if (!socket || !isConnected || !tableId) return;

    // Skip if reconnection is in progress - the onReconnect handler will handle joining
    // This prevents race conditions where mount effect runs before reconnection join completes
    if (reconnectionInProgressRef.current) {
      console.log('[PixiTablePage] Mount effect skipped - reconnection in progress');
      return;
    }

    // Only join if not already seated
    const currentTableId = useGameStateStore.getState().tableId;
    if (currentTableId === tableId) {
      console.log('[PixiTablePage] Already at table:', tableId);
      hasJoinedRef.current = true;
      return;
    }

    // Always start fresh - no persisted staged bet
    // Players use autobet if they want to repeat bets
    console.log('[PixiTablePage] Joining table via lifecycle registry:', tableId);
    useStagedChipsStore.getState().reset();

    // Use lifecycle registry for consistent join behavior
    useTableLifecycleRegistry.getState().enterTable(tableId);
    hasJoinedRef.current = true;
  }, [socket, isConnected, tableId]);


  // ===========================================
  // UNMOUNT CLEANUP (via Lifecycle Registry)
  // ===========================================
  // When navigating away (unmount), use exitTable('navigate') for immediate cleanup.
  useEffect(() => {
    return () => {
      console.log('[PixiTablePage] Unmount cleanup via lifecycle registry');
      // Use lifecycle registry for consistent cleanup
      useTableLifecycleRegistry.getState().exitTable('navigate');
      // Also clear game state store
      useGameStateStore.getState().clearState();
      // Clear animation config (will be reloaded on next table join)
      useAnimationConfigStore.getState().clearConfig();
      // NOTE: Don't clear staged chips - they persist for "rebet" functionality
      // NOTE: Don't clear sessionStorage tableId - needed for HMR recovery
    };
  }, []); // Empty deps = only runs on unmount


  // ===========================================
  // RECONNECTION HANDLING
  // ===========================================
  // Re-join table when socket reconnects after a disconnect
  useEffect(() => {
    const unsubscribe = onReconnect(() => {
      if (!tableId) return;

      // CRITICAL: Debounce duplicate reconnection callbacks
      // Prevents multiple join_table emissions from React StrictMode double-mounts
      // or multiple onReconnect subscribers triggering in rapid succession
      const now = Date.now();
      if (now - lastReconnectJoinTimeRef.current < RECONNECT_DEBOUNCE_MS) {
        console.log('[PixiTablePage] Skipping duplicate reconnection join (debounced)');
        return;
      }
      lastReconnectJoinTimeRef.current = now;

      console.log('[PixiTablePage] Reconnection detected, re-joining table:', tableId);

      // Mark reconnection in progress to prevent HMR recovery and other effects
      // from emitting duplicate join_table
      reconnectionInProgressRef.current = true;

      // Enter CTM away mode to prevent stale batches from being processed
      // Fresh state will come from join_table response
      const ctm = getClientTableMaster();
      ctm.enterAwayMode();
      console.log('[PixiTablePage] CTM entered away mode for reconnection');

      // Clear all module-level state synchronously (like F5 does)
      // This prevents stale sprites/labels from persisting through reconnection
      clearModuleState();

      // NOTE: We no longer reset lastEventSequence to -1 on reconnect.
      // The server now sends lastEventSequence in table_joined, and useGameEvents
      // sets it from there. This ensures the client only processes events AFTER
      // the snapshot moment, preventing visual chaos from duplicate/stale events.

      // NOTE: We no longer disable autobet on reconnection.
      // The server now persists autobetEnabled per seat, and it will be restored
      // from the table_joined response. This ensures autobet state survives tab switches.

      // Also clear the PixiJS scene (kitty containers, cards, etc.)
      // IMPORTANT: Wait for clearTable to complete before emitting join_table
      // This prevents race conditions where fullVisualResync runs while clearTable is still animating
      console.log('[PixiTablePage] Clearing rendered table on reconnect');
      const clearPromise = pixiRef.current?.clearTable?.() ?? Promise.resolve();
      clearPromise.then(() => {
        console.log('[PixiTablePage] clearTable complete, emitting join_table');
        // Note: We don't call clearState() because the server properly
        // handles disconnection grace periods. Fresh state comes after join_table,
        // and useGameEvents will sync it via syncFromTableState.
        const currentSocket = useSocketStore.getState().socket;
        if (currentSocket) {
          // Always re-join on reconnect to get fresh state
          currentSocket.emit('client_message', {
            type: 'join_table',
            tableId,
            mode: modeRef.current
          });
          hasJoinedRef.current = true;
        }
        // Clear the flag after join is emitted (give some time for effects to stabilize)
        setTimeout(() => {
          reconnectionInProgressRef.current = false;
        }, 1000);
      });
    });
    return unsubscribe;
  }, [tableId]);

  // ===========================================
  // VISIBILITY CHANGE HANDLING (Tab Switching)
  // ===========================================
  // Now handled by useVisibilityHandler hook (imported at top).
  // Behavior:
  // - Tab hidden for 5+ seconds: enter away mode, clear dynamic state
  // - Tab becomes visible: trigger full resync from server
  //
  // The hook calls handleVisibilityResync (defined above) which:
  // 1. Clears Pixi visuals
  // 2. Triggers resync via useResync hook
  // 3. Runs health check after state is populated
  //
  // This replaces the old approach of just running a health check.
  // Now we do a full state refresh to catch any drift or stale data.

  // ===========================================
  // TABLE LEFT / KICKED HANDLING
  // ===========================================
  // When server sends table_left (kicked for inactivity, table closed, etc.),
  // navigate to lobby and show appropriate message.
  useEffect(() => {
    if (!socket) return;

    const handleTableLeft = (message: { type: string; reason?: string; message?: string }) => {
      // IMPORTANT: Only handle explicit table_left messages
      if (!message || typeof message.type !== 'string' || message.type !== 'table_left') return;

      console.log('[PixiTablePage] Received table_left:', message.reason, message.message);
      hasJoinedRef.current = false;
      setPersistedTableId(null);

      // Navigate to lobby with kick info if provided
      if (message.reason && message.message) {
        navigate('/lobby', {
          state: { kickMessage: message.message, kickReason: message.reason }
        });
      } else {
        navigate('/lobby');
      }
    };

    socket.on('server_message', handleTableLeft);
    return () => {
      socket.off('server_message', handleTableLeft);
    };
  }, [socket, navigate]);


  // ===========================================
  // JOIN TIMEOUT & RETRY LOGIC
  // ===========================================
  // If still stuck on "Joining table..." after 3 seconds, retry join
  const joinRetryCountRef = useRef(0);
  useEffect(() => {
    if (!socket || !isConnected || !tableId) return;
    if (tableName) {
      // Successfully joined - reset retry counter
      joinRetryCountRef.current = 0;
      return;
    }

    console.log('[PixiTablePage] Join timeout effect: waiting for table_joined response...');

    const joinTimeout = setTimeout(() => {
      // Skip retry if reconnection is in progress (reconnection handler handles join)
      if (reconnectionInProgressRef.current) {
        console.log('[PixiTablePage] Join timeout skipped - reconnection in progress');
        return;
      }

      const currentTableName = useGameStateStore.getState().tableName;
      if (!currentTableName) {
        joinRetryCountRef.current++;
        if (joinRetryCountRef.current <= 3) {
          console.warn(`[PixiTablePage] Join timeout - retrying (attempt ${joinRetryCountRef.current}/3)...`);
          socket.emit('client_message', { type: 'join_table', tableId, mode: modeRef.current });
        } else {
          console.error('[PixiTablePage] Join failed after 3 retries');
          // Could set an error state here if desired
        }
      }
    }, 3000); // 3 second timeout

    return () => {
      clearTimeout(joinTimeout);
    };
  }, [socket, isConnected, tableId, tableName]);

  // ===========================================
  // HMR RECOVERY
  // ===========================================
  // Detect when stores were reset (HMR) but we're still connected
  useEffect(() => {
    if (!socket || !isConnected || !tableId) return;
    if (tableName) return; // Already have table state - no recovery needed

    // Skip if reconnection is in progress (prevents duplicate join_table)
    if (reconnectionInProgressRef.current) {
      console.log('[PixiTablePage] HMR recovery skipped - reconnection in progress');
      return;
    }

    // Check if we previously joined this table:
    // 1. hasJoinedRef.current (survives if only stores were HMR'd, not the component)
    // 2. sessionStorage (survives even if component is HMR'd)
    const sessionTableId = sessionStorage.getItem('lastJoinedTableId');
    const wasAtThisTable = hasJoinedRef.current || sessionTableId === tableId;

    if (wasAtThisTable) {
      console.log('[PixiTablePage] Store reset detected (HMR recovery):', {
        hasJoinedRef: hasJoinedRef.current,
        sessionTableId,
        tableId,
      });
      console.log('[PixiTablePage] Re-requesting table state for recovery...');
      socket.emit('client_message', { type: 'join_table', tableId, mode: modeRef.current });
      hasJoinedRef.current = true;
    }
  }, [socket, isConnected, tableId, tableName]);

  // ===========================================
  // PAGE REFRESH DETECTION
  // ===========================================
  // Track page refresh vs intentional navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('wasPageRefresh', 'true');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Clear the flag when navigating away normally (not refresh)
      sessionStorage.removeItem('wasPageRefresh');
    };
  }, []);

  // Game action handlers
  const handleSitDown = useCallback((seatPosition: number) => {
    if (!tableId) return;
    const seatIndex = seatPosition - 1; // Convert 1-indexed to 0-indexed
    console.log('[PixiTablePage] Sitting at seat:', seatPosition, '(index:', seatIndex, ')');
    // Reset staged bet to zero when joining a table - user must set their bet fresh
    useStagedChipsStore.getState().clearAll();
    sendMessage('join_table', { tableId, seatIndex, mode: 'play' });
  }, [sendMessage, tableId]);

  const handlePlaceBet = useCallback((amount: number, chips: number[]) => {
    console.log('[PixiTablePage] ========== BET PRESSED ==========');
    console.log('[PixiTablePage] handlePlaceBet called:', { amount, chips, mySeatPosition });

    // Diagnostic logging
    diagLog.bet({ seat: mySeatPosition ?? 0, amount, source: 'USER', type: 'PLACE' });

    // NEW MULTIPLAYER RAKE ARCHITECTURE:
    // Send player_ready_to_bet instead of place_bet.
    // This marks player as "ready" - rake and chip animation happen via events:
    // 1. Server emits SEAT_RAKE → client rakes this player's cards
    // 2. Server emits BET_CONFIRMED → client animates chips to kitty
    // 3. When last player ready → server emits DEALER_RAKE → client rakes dealer
    // 4. Server starts dealing
    console.log('[PixiTablePage] Sending player_ready_to_bet message to server...');
    sendMessage('player_ready_to_bet', { amount, betChips: chips });
    console.log('[PixiTablePage] player_ready_to_bet message sent');
  }, [sendMessage, mySeatPosition]);

  const handleRakeCards = useCallback(async () => {
    // NEW MULTIPLAYER RAKE ARCHITECTURE:
    // Rake is now handled by SEAT_RAKE and DEALER_RAKE events from server.
    // This function is kept for API compatibility but is intentionally a no-op.
    // The actual rake happens when:
    // 1. Server emits SEAT_RAKE → onSeatRake → rakeCardsForSeat()
    // 2. Server emits DEALER_RAKE → onDealerRake → rakeDealerCards()
    console.log('[PixiTablePage] handleRakeCards - NO-OP (rake handled by server events)');
  }, []);

  const handleHit = useCallback(() => {
    diagLog.action({ seat: mySeatPosition ?? undefined, action: 'HIT', source: 'USER' });
    sendMessage('hit');
  }, [sendMessage, mySeatPosition]);

  // Sidebet handler - sends place_sidebet message to backend
  // rounds: positive number for countdown, -1 for ongoing/infinite
  const handleSidebetPlace = useCallback((sidebetType: string, amount: number, rounds: number) => {
    console.log(`[PixiTablePage] Sidebet placed: ${sidebetType}, $${amount}, ${rounds === -1 ? '∞' : rounds} rounds`);

    // Calculate percentage for config restoration on refresh
    const percentage = Math.round((amount / (currentBet || 1)) * 100);

    // Store sidebet selection locally for UI state
    setSidebetSelections(prev => ({
      ...prev,
      [sidebetType]: { percentage, rounds },
    }));

    // Send to backend (include percentage for refresh persistence)
    sendMessage('place_sidebet', { sidebetType, amount, percentage, rounds });

    // Update local game state store
    useGameStateStore.getState().setSidebet(sidebetType, amount, rounds);

    // IMMEDIATE visual feedback:
    // 1. Create sidebet icon at player's seat (if not exists)
    // 2. Animate chips from rail to the icon
    const seatPos = useGameStateStore.getState().mySeatPosition;
    console.log(`[PixiTablePage] Sidebet animation: seatPos=${seatPos}, pixiRef.current=${!!pixiRef.current}`);
    if (seatPos) {
      // Step 1: Create the icon at the player's seat (returns slot index, or -1 if already exists/full)
      const slotIndex = pixiRef.current?.addSidebetToSeat(seatPos, sidebetType, amount, rounds);
      console.log(`[PixiTablePage] addSidebetToSeat returned slot: ${slotIndex}`);

      // Step 2: Animate chips to the icon
      console.log(`[PixiTablePage] Calling animateSidebetPlacement(${seatPos}, ${sidebetType}, ${amount}, ${rounds})`);
      pixiRef.current?.animateSidebetPlacement(seatPos, sidebetType, amount, rounds);
    } else {
      console.warn(`[PixiTablePage] No seat position, skipping sidebet animation`);
    }
  }, [currentBet, sendMessage]);

  // Sidebet removal handler - sends remove_sidebet message to backend
  const handleSidebetRemove = useCallback((sidebetType: string) => {
    console.log(`[PixiTablePage] handleSidebetRemove called: ${sidebetType}`);
    console.log(`[PixiTablePage] Current mySidebetConfigs:`, Array.from(useGameStateStore.getState().mySidebetConfigs.entries()));

    // Send remove_sidebet to backend - this removes from lastSidebetConfigs
    // so the sidebet won't be auto-placed in future rounds
    console.log(`[PixiTablePage] Sending remove_sidebet message to server for: ${sidebetType}`);
    sendMessage('remove_sidebet', { sidebetType });

    // Mark the sidebet icon for removal during rake (don't remove immediately)
    // The icon should stay visible until the outcome border is cleared
    const seatPos = useGameStateStore.getState().mySeatPosition;
    if (seatPos) {
      console.log(`[PixiTablePage] Marking ${sidebetType} for removal during rake at seat ${seatPos}`);
      pixiRef.current?.markSidebetForRemoval(seatPos, sidebetType);
    }
  }, [sendMessage]);

  const handleStand = useCallback(() => {
    diagLog.action({ seat: mySeatPosition ?? undefined, action: 'STAND', source: 'USER' });
    sendMessage('stand');
  }, [sendMessage, mySeatPosition]);

  const handleDouble = useCallback(() => {
    diagLog.action({ seat: mySeatPosition ?? undefined, action: 'DOUBLE', source: 'USER' });
    sendMessage('double');
  }, [sendMessage, mySeatPosition]);

  const handleSplit = useCallback(() => {
    diagLog.action({ seat: mySeatPosition ?? undefined, action: 'SPLIT', source: 'USER' });
    sendMessage('split');
  }, [sendMessage, mySeatPosition]);

  const handleSurrender = useCallback(() => {
    diagLog.action({ seat: mySeatPosition ?? undefined, action: 'SURRENDER', source: 'USER' });
    sendMessage('surrender');
  }, [sendMessage, mySeatPosition]);

  // Handler for PixiControlPanel bet button (reads from staged chips store)
  const handlePixiBet = useCallback(() => {
    // TIMING TEST: Record when BET button is pressed
    const betPressTime = performance.now();
    (window as unknown as { __BET_PRESS_TIME__: number }).__BET_PRESS_TIME__ = betPressTime;
    console.log(`[TIMING TEST] ⏱️ BET PRESSED at ${betPressTime.toFixed(2)}ms`);

    console.log('[SIDEBET DEBUG] handlePixiBet called');
    try {
      const { stagedAmount, stagedChips } = useStagedChipsStore.getState();
      const chipSum = stagedChips.reduce((sum, c) => sum + c, 0);
      console.log('[BET DEBUG] stagedAmount:', stagedAmount, 'chipSum:', chipSum, 'chips:', JSON.stringify(stagedChips));

      if (stagedAmount > 0) {
        // Diagnostic logging for bet
        diagLog.bet({ seat: mySeatPosition ?? 0, amount: stagedAmount, source: 'USER', type: 'PLACE' });

        // Build sidebets array from configured sidebets
        const { mySidebetConfigs } = useGameStateStore.getState();
        const sidebets: Array<{ sidebetType: string; amount: number; percentage: number; rounds: number }> = [];

        if (mySidebetConfigs && mySidebetConfigs.size > 0) {
          for (const [sidebetType, config] of mySidebetConfigs) {
            const sidebetAmount = Math.floor((stagedAmount * config.percentage) / 100);
            if (sidebetAmount > 0) {
              sidebets.push({ sidebetType, amount: sidebetAmount, percentage: config.percentage, rounds: config.rounds });
              // Diagnostic logging for each sidebet
              diagLog.sidebet({ seat: mySeatPosition ?? 0, sidebetType, amount: sidebetAmount, action: 'PLACE', source: 'USER' });
            }
          }
        }

        // Send player_ready_to_bet (new rake architecture) with sidebets
        // NOTE: autobetEnabled is now managed server-side via toggle_autobet message
        console.log('[SIDEBET DEBUG] Sending player_ready_to_bet with sidebets:', sidebets);
        sendMessage('player_ready_to_bet', { amount: stagedAmount, betChips: stagedChips, sidebets });
      } else {
        console.log('[SIDEBET DEBUG] No staged amount, skipping bet');
      }
    } catch (err) {
      console.error('[SIDEBET DEBUG] handlePixiBet ERROR:', err);
    }
  }, [sendMessage, mySeatPosition]);

  // ===========================================
  // ===========================================
  // SERVER-SIDE AUTOBET
  // ===========================================
  // Autobet logic is now handled on the server. The client just toggles the flag
  // via sendMessage('toggle_autobet', { enabled }) and the server auto-places
  // bets when transitioning to betting phase for players with autobetEnabled.
  // ===========================================

  // ===========================================
  // AUTO-PLAY LOGIC (moved to PixiControlPanel.ts)
  // ===========================================
  // All auto-play logic (hit/stand/double/split/surrender AND insurance)
  // is now handled imperatively in PixiControlPanel to avoid React re-render race conditions.

  const handleInsurance = useCallback((amount: number) => {
    diagLog.action({ seat: mySeatPosition ?? undefined, action: 'INSURANCE_ACCEPT', source: 'USER', details: { amount } });
    setInsuranceDecisionMade(true);
    setInsuranceDecisionPending(true); // Mark as pending until server acks

    // IMMEDIATELY hide modal and badge - don't wait for React state/useEffect cycle
    pixiRef.current?.hideInsurancePanel();
    diagLog.ui({ element: 'INSURANCE_MODAL', action: 'HIDE', seat: mySeatPosition ?? undefined, source: 'USER' });
    if (mySeatPosition) {
      pixiRef.current?.hideInsuranceBadge(mySeatPosition);
      diagLog.ui({ element: 'INSURANCE_BADGE', action: 'HIDE', seat: mySeatPosition, source: 'USER' });
    }

    sendMessage('insurance', { amount });
  }, [sendMessage, mySeatPosition]);

  const handleDeclineInsurance = useCallback(() => {
    diagLog.action({ seat: mySeatPosition ?? undefined, action: 'INSURANCE_DECLINE', source: 'USER' });
    setInsuranceDecisionMade(true);
    setInsuranceDecisionPending(true); // Mark as pending until server acks

    // IMMEDIATELY hide modal and badge - don't wait for React state/useEffect cycle
    pixiRef.current?.hideInsurancePanel();
    diagLog.ui({ element: 'INSURANCE_MODAL', action: 'HIDE', seat: mySeatPosition ?? undefined, source: 'USER' });
    if (mySeatPosition) {
      pixiRef.current?.hideInsuranceBadge(mySeatPosition);
      diagLog.ui({ element: 'INSURANCE_BADGE', action: 'HIDE', seat: mySeatPosition, source: 'USER' });
    }

    sendMessage('decline_insurance');
  }, [sendMessage, mySeatPosition]);

  const handleLeaveTable = useCallback(() => {
    console.log('[PixiTablePage] handleLeaveTable called');

    // ===========================================
    // PHASE 1: Block rendering immediately
    // ===========================================
    // This MUST be first - renders gray screen to prevent any visual glitches
    setIsLeaving(true);

    // ===========================================
    // PHASE 2: Destroy PixiJS canvas
    // ===========================================
    // Do this early to remove the green canvas from DOM
    try {
      pixiRef.current?.destroy();
      console.log('[PixiTablePage] PixiJS destroyed');
    } catch (e) {
      console.error('[PixiTablePage] Error destroying PixiJS:', e);
    }

    // ===========================================
    // PHASE 3: Clear all client state (synchronous)
    // ===========================================
    // All Zustand store operations are synchronous - no race conditions
    try {
      // Clear game state (includes mySidebetConfigs)
      useGameStateStore.getState().clearState();
      // Clear staged chips and autobet
      useStagedChipsStore.getState().reset();
      // Clear animation config
      useAnimationConfigStore.getState().clearConfig();
      // Clear animation queue
      useAnimationQueueStore.getState().clear();
      // Clear persisted table ID (prevents stale HMR recovery)
      setPersistedTableId(null);
      // Mark as not joined (prevents stale reconnection logic)
      hasJoinedRef.current = false;
      console.log('[PixiTablePage] All stores cleared');
    } catch (e) {
      console.error('[PixiTablePage] Error clearing state:', e);
    }

    // ===========================================
    // PHASE 4: Mark lifecycle as inactive
    // ===========================================
    try {
      useTableLifecycleRegistry.setState({
        isActive: false,
        isDormant: false,
        currentTableId: null,
        dormantSince: null,
      });
    } catch (e) {
      console.error('[PixiTablePage] Error updating lifecycle state:', e);
    }

    // ===========================================
    // PHASE 5: Send leave message (fire-and-forget)
    // ===========================================
    // Do this AFTER local cleanup - server response doesn't matter for navigation
    try {
      sendMessage('leave_table');
    } catch (e) {
      console.error('[PixiTablePage] Error sending leave_table:', e);
    }

    // ===========================================
    // PHASE 6: Disconnect socket
    // ===========================================
    try {
      useSocketStore.getState().disconnect();
      console.log('[PixiTablePage] Socket disconnected');
    } catch (e) {
      console.error('[PixiTablePage] Error disconnecting socket:', e);
    }

    // ===========================================
    // PHASE 7: Navigate immediately
    // ===========================================
    // NO setTimeout - all cleanup above is synchronous
    // State is GUARANTEED clean before navigation begins
    console.log('[PixiTablePage] Navigating to /lobby');
    navigate('/lobby');
  }, [sendMessage, navigate]);

  const handleShare = useCallback(() => {
    setShowShareModal(true);
  }, []);

  // SidePanel handlers
  const handleRequestStats = useCallback(() => {
    sendMessage('request_stats');
  }, [sendMessage]);

  const handleSendChatMessage = useCallback((message: string) => {
    sendMessage('chat_message', { message });
  }, [sendMessage]);

  const handleSidebetClick = useCallback((sidebetId: string) => {
    console.log(`[PixiTablePage] Sidebet clicked from SidePanel: ${sidebetId}`);
    pixiRef.current?.showSidebetModal(sidebetId);
  }, []);

  // ===========================================
  // INSURANCE MODAL LOGIC
  // ===========================================
  // Reset insurance decision when dealer cards are cleared (new round)
  const dealerCards = dealer?.cards ?? [];
  const prevDealerCardsRef = useRef<number>(dealerCards.length);
  useEffect(() => {
    // When dealer cards go from >0 to 0, reset the decision (new round)
    if (prevDealerCardsRef.current > 0 && dealerCards.length === 0) {
      setInsuranceDecisionMade(false);
      setInsuranceDecisionPending(false);
    }
    prevDealerCardsRef.current = dealerCards.length;
  }, [dealerCards.length]);

  // Determine if insurance modal should show
  const dealerShowsAce = dealerCards.length > 0 && dealerCards[0]?.faceUp && dealerCards[0]?.rank === 'A';
  const backendInsuranceDecided = myPlayerState?.insuranceDecided ?? false;
  const hasActiveHand = myPlayerState?.hands?.some(h => h.cards?.length > 0) ?? false;

  // Clear pending state when server acknowledges the insurance decision
  useEffect(() => {
    if (backendInsuranceDecided && (insuranceDecisionPending || storeInsuranceDecisionPending)) {
      console.log('[PixiTablePage] Server acknowledged insurance decision, clearing pending state');
      setInsuranceDecisionPending(false);
      useGameStateStore.getState().setInsuranceDecisionPending(false);
    }
  }, [backendInsuranceDecided, insuranceDecisionPending, storeInsuranceDecisionPending]);

  // Reset insurance state when server says insurance is offered but player hasn't decided
  // This handles reconnection during active insurance (local state stale from dropped message)
  // BUT: Do NOT reset if we're waiting for server acknowledgment (pending state)
  useEffect(() => {
    const anyPending = insuranceDecisionPending || storeInsuranceDecisionPending;
    if (insuranceOffered && !backendInsuranceDecided && !anyPending) {
      // Reset local state for new insurance round
      if (insuranceDecisionMade) {
        console.log('[PixiTablePage] Resetting insuranceDecisionMade - server says insurance still active');
        setInsuranceDecisionMade(false);
      }
    }
    // NOTE: Removed "stale detection" that cleared storeInsuranceDecisionPending when !insuranceDecisionPending
    // That logic incorrectly fired during normal autoplay flow (autoplay sets store but not local state)
    // Store pending state is now only cleared by:
    // 1. backendInsuranceDecided becoming true (useEffect above)
    // 2. insuranceOffered becoming false (useEffect below for recommendation)
  }, [insuranceOffered, backendInsuranceDecided, insuranceDecisionMade, insuranceDecisionPending, storeInsuranceDecisionPending]);

  // Also check storeInsuranceDecisionPending - set by autoplay in useGameEvents
  // Insurance is only valid during dealing/playing phases - never after dealer_turn starts or round ends
  const insurancePhaseValid = gamePhase === 'dealing' || gamePhase === 'playing';
  const insuranceActive = insuranceOffered && dealerShowsAce && !insuranceDecisionMade && !backendInsuranceDecided && hasActiveHand && !storeInsuranceDecisionPending && insurancePhaseValid;

  // Calculate insurance cost (half the current bet)
  const currentBetForInsurance = myPlayerState?.hands?.[0]?.bet ?? 0;
  const insuranceCost = currentBetForInsurance / 2;
  const canAffordInsurance = balance >= insuranceCost;

  // ==========================================
  // PIXI INSURANCE PANEL CONTROL
  // ==========================================
  // Track the last non-null recommendation to avoid showing panel with null after autoplay
  const lastInsuranceRecommendationRef = useRef<'ACCEPT' | 'DECLINE' | null>(null);

  // Update the ref when we get a new non-null recommendation
  useEffect(() => {
    if (insuranceRecommendation !== null) {
      lastInsuranceRecommendationRef.current = insuranceRecommendation;
    }
    // Clear when insurance phase ends
    if (!insuranceOffered) {
      lastInsuranceRecommendationRef.current = null;
      // Also clear store's pending state when insurance phase ends
      if (storeInsuranceDecisionPending) {
        useGameStateStore.getState().setInsuranceDecisionPending(false);
      }
    }
  }, [insuranceRecommendation, insuranceOffered, storeInsuranceDecisionPending]);

  // Show/hide the Pixi insurance panel based on insurance state
  useEffect(() => {
    if (!pixiRef.current) return;

    if (insuranceActive) {
      // Use the last known recommendation (avoids showing null after autoplay clears it)
      const effectiveRecommendation = insuranceRecommendation ?? lastInsuranceRecommendationRef.current;
      console.log('[PixiTablePage] Showing Pixi insurance panel, cost:', insuranceCost, 'deadline:', insuranceCountdown.deadline, 'recommendation:', effectiveRecommendation);
      pixiRef.current.showInsurancePanel(insuranceCost, insuranceCountdown.deadline);
      pixiRef.current.setInsuranceCanAfford(canAffordInsurance);
      // Set recommendation highlighting (ACCEPT/DECLINE based on card counting)
      pixiRef.current.setInsuranceRecommendation(effectiveRecommendation);
      diagLog.ui({ element: 'INSURANCE_MODAL', action: 'SHOW', seat: mySeatPosition ?? undefined, source: 'SYSTEM', details: { cost: insuranceCost } });
    } else {
      pixiRef.current.hideInsurancePanel();
      diagLog.ui({ element: 'INSURANCE_MODAL', action: 'HIDE', seat: mySeatPosition ?? undefined, source: 'SYSTEM' });
    }
  }, [insuranceActive, insuranceCost, insuranceCountdown.deadline, canAffordInsurance, insuranceRecommendation, mySeatPosition]);

  // Track when we've already hidden badges to avoid spam logging
  const badgeHideLoggedRef = useRef<{
    myDecision: boolean;
    phaseEnded: boolean;
    otherPlayers: Set<number>;
  }>({ myDecision: false, phaseEnded: false, otherPlayers: new Set() });

  // Track previous insuranceOffered value to detect transition from true -> false
  // This prevents hiding badges immediately when insuranceOffered is false during initial deal
  const prevInsuranceOfferedRef = useRef<boolean>(false);

  // Reset badge logging state when insurance is offered (new insurance round)
  useEffect(() => {
    if (insuranceOffered) {
      badgeHideLoggedRef.current = { myDecision: false, phaseEnded: false, otherPlayers: new Set() };
    }
  }, [insuranceOffered]);

  // Hide insurance badges when player decides (badge shown when Ace lands in onCardDealt)
  useEffect(() => {
    if (!pixiRef.current) return;

    // Hide badge for current player immediately when local decision is made (or pending)
    // This provides instant feedback before server acknowledges
    // Also check storeInsuranceDecisionPending which is set by autoplay
    if (mySeatPosition && (insuranceDecisionMade || insuranceDecisionPending || storeInsuranceDecisionPending)) {
      pixiRef.current.hideInsuranceBadge(mySeatPosition);
      if (!badgeHideLoggedRef.current.myDecision) {
        const hideSource = storeInsuranceDecisionPending ? 'AUTOPLAY' : 'USER';
        diagLog.ui({ element: 'INSURANCE_BADGE', action: 'HIDE', seat: mySeatPosition, source: hideSource });
        badgeHideLoggedRef.current.myDecision = true;
      }
    }

    // Hide badges for other players based on server state
    players.forEach(player => {
      if (!player) return;
      if (player.insuranceDecided) {
        pixiRef.current?.hideInsuranceBadge(player.seatPosition);
        if (!badgeHideLoggedRef.current.otherPlayers.has(player.seatPosition)) {
          diagLog.ui({ element: 'INSURANCE_BADGE', action: 'HIDE', seat: player.seatPosition, source: 'SYSTEM', details: { reason: 'other_player_decided' } });
          badgeHideLoggedRef.current.otherPlayers.add(player.seatPosition);
        }
      }
    });

    // Hide all badges when insurance transitions from offered to not offered
    // We only hide on the TRANSITION from true -> false, not when insuranceOffered is simply false
    // This prevents hiding badges immediately during initial deal before INSURANCE_OFFERED arrives
    const wasOffered = prevInsuranceOfferedRef.current;
    if (wasOffered && !insuranceOffered) {
      pixiRef.current.hideAllInsuranceBadges();
      if (!badgeHideLoggedRef.current.phaseEnded) {
        diagLog.ui({ element: 'INSURANCE_BADGE_ALL', action: 'HIDE', source: 'SYSTEM', details: { reason: 'insurance_no_longer_offered' } });
        badgeHideLoggedRef.current.phaseEnded = true;
      }
    }

    // Update previous value ref for next render
    prevInsuranceOfferedRef.current = insuranceOffered;
  }, [insuranceOffered, players, gamePhase, mySeatPosition, insuranceDecisionMade, insuranceDecisionPending, storeInsuranceDecisionPending]);

  // ===========================================
  // COUNTDOWN RING LOGIC (Betting & Turn Timers)
  // ===========================================
  // Use primitive selectors to avoid object recreation on every render
  const turnCountdownActive = useGameStateStore(state => state.turnCountdownActive);
  const turnCountdownSeat = useGameStateStore(state => state.turnCountdownSeat);
  const turnCountdownDeadline = useGameStateStore(state => state.turnCountdownDeadline);
  const bettingCountdownActive = useGameStateStore(state => state.bettingCountdownActive);
  const bettingCountdownDeadline = useGameStateStore(state => state.bettingCountdownDeadline);
  const playersNeedingBets = useGameStateStore(state => state.playersNeedingBets);

  // Track previous deadline to detect changes (for split hand transitions)
  const prevTurnDeadlineRef = useRef<number | null>(null);

  // Show/hide turn countdown ring
  useEffect(() => {
    if (!pixiRef.current) return;

    if (turnCountdownActive && turnCountdownSeat !== null && turnCountdownDeadline) {
      // Always show/restart countdown when deadline changes (handles split hand transitions)
      const deadlineChanged = prevTurnDeadlineRef.current !== turnCountdownDeadline;
      if (deadlineChanged) {
        console.log('[PixiTablePage] Turn countdown deadline changed:', prevTurnDeadlineRef.current, '->', turnCountdownDeadline);
      }
      prevTurnDeadlineRef.current = turnCountdownDeadline;

      // Show countdown ring for the seat whose turn it is
      pixiRef.current.showCountdownRing(turnCountdownSeat, turnCountdownDeadline, 10000);
    } else {
      // Hide all countdown rings when turn countdown is not active
      prevTurnDeadlineRef.current = null;
      pixiRef.current.hideAllCountdownRings();
    }
  }, [turnCountdownActive, turnCountdownSeat, turnCountdownDeadline]);

  // Show/hide betting countdown rings (can be multiple seats)
  useEffect(() => {
    if (!pixiRef.current) return;

    if (bettingCountdownActive && bettingCountdownDeadline && playersNeedingBets.length > 0) {
      // Show countdown ring for each seat that needs to place a bet
      playersNeedingBets.forEach((seatPosition) => {
        pixiRef.current?.showCountdownRing(seatPosition, bettingCountdownDeadline, 10000);
      });
    } else if (!turnCountdownActive) {
      // Only hide if turn countdown isn't active (don't interfere with turn rings)
      pixiRef.current.hideAllCountdownRings();
    }
  }, [bettingCountdownActive, bettingCountdownDeadline, playersNeedingBets, turnCountdownActive]);

  // Generate share URL for the modal
  const shareUrl = useMemo(() => {
    return window.location.href;
  }, []);

  // Calculate effective header height for layout
  const currentHeaderHeight = useMemo(() => {
    if (!isCollapsibleMode) {
      return isSmallViewport ? SMALL_HEADER_HEIGHT : HEADER_HEIGHT;
    }
    if (isHeaderVisible) {
      return isSmallViewport ? SMALL_HEADER_HEIGHT : HEADER_HEIGHT;
    }
    return isSmallViewport ? SMALL_TAP_TARGET_HEIGHT : TAP_TARGET_HEIGHT;
  }, [isCollapsibleMode, isHeaderVisible, isSmallViewport]);

  // ===========================================
  // CONTAINER MEASUREMENT VIA RESIZEOBSERVER
  // ===========================================
  // Use ResizeObserver to get actual container dimensions after CSS layout settles
  // This is more reliable than calculating from viewport state, especially on refresh
  useEffect(() => {
    if (!gameAreaRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          console.log('[PixiTablePage] ResizeObserver measured:', { width, height });
          setMeasuredDimensions({ width: Math.round(width), height: Math.round(height) });
        }
      }
    });

    observer.observe(gameAreaRef.current);

    return () => observer.disconnect();
  }, []);

  // ===========================================
  // DEV CONTROLS: Populate Table Effect
  // ===========================================
  useEffect(() => {
    if (devPopulateTable) {
      // Small delay to ensure pixi canvas is ready
      const timer = setTimeout(() => {
        pixiRef.current?.populateTableForDev();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      pixiRef.current?.clearDevPopulation();
    }
  }, [devPopulateTable]);

  // ===========================================
  // UNIFIED RENDER - Single PixiGameTableV1 instance
  // ===========================================
  // Uses CSS transforms for rotation instead of conditional JSX trees.
  // This prevents component remounting when viewport size changes.
  // PixiJS canvas scales uniformly, so we use consistent config values
  // for all viewport sizes - no need for viewport-specific adjustments.

  // Calculate available canvas dimensions - prefer measured dimensions when available
  // Fall back to calculated dimensions (viewport minus header) for initial render
  const availableCanvasDimensions = useMemo(() => {
    // If we have measured dimensions from ResizeObserver, use those
    if (measuredDimensions && measuredDimensions.width > 100 && measuredDimensions.height > 100) {
      console.log('[PixiTablePage] Using MEASURED dimensions:', measuredDimensions);
      return measuredDimensions;
    }

    // Fallback to calculated dimensions
    const viewportWidth = effectiveViewport.width;
    const viewportHeight = effectiveViewport.height;

    // Subtract header height only - control panel is inside canvas
    const availableWidth = Math.max(100, viewportWidth);
    const availableHeight = Math.max(100, viewportHeight - currentHeaderHeight);

    console.log('[PixiTablePage] Using CALCULATED dimensions:', {
      effectiveViewport,
      currentHeaderHeight,
      result: { width: availableWidth, height: availableHeight },
      windowSize: { w: window.innerWidth, h: window.innerHeight },
    });

    return { width: availableWidth, height: availableHeight };
  }, [effectiveViewport, currentHeaderHeight, measuredDimensions]);

  // Outer wrapper style - differs between small and large viewports
  const outerWrapperStyle: React.CSSProperties = isSmallViewport
    ? {
        width: physicalViewport.width,
        height: physicalViewport.height,
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
      }
    : {};

  // Inner container style - handles rotation for small viewports
  const innerContainerStyle: React.CSSProperties = isSmallViewport
    ? {
        width: effectiveViewport.width,
        height: effectiveViewport.height,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) rotate(${forceRotation}deg)`,
        transformOrigin: 'center center',
        transition: 'transform 0.15s ease-out',
        willChange: 'transform',
      }
    : {};

  // Block rendering until animation config is loaded from server
  // Server sends animation_config BEFORE table_joined, so this should be fast
  // NOTE: This hook MUST be called before any early returns to avoid "fewer hooks" error
  const animationConfigLoaded = useAnimationConfigStore((state) => state.isLoaded);

  // When leaving, render a blank dark screen to prevent green flash during navigation
  if (isLeaving) {
    return <div className="w-screen h-screen bg-gray-900" />;
  }

  if (!animationConfigLoaded) {
    return <LoadingVideo />;
  }

  return (
    <>
      {/* Outer Wrapper - fixed for small viewport, screen-sized for large */}
      <div
        className={isSmallViewport ? 'bg-gray-900 select-none' : 'w-screen h-screen bg-gray-900 overflow-hidden flex flex-col select-none'}
        style={outerWrapperStyle}
      >
        {/* Inner Container - rotated for small viewport portrait mode */}
        <div
          style={innerContainerStyle}
          className={isSmallViewport ? 'flex flex-col bg-gray-900' : 'flex-1 flex flex-col'}
        >
          {/* Collapsible Header Bar - unified for both viewport sizes */}
          <motion.div
            className={`flex-shrink-0 bg-gray-900 border-b border-gray-700 flex items-center justify-between z-50 overflow-hidden cursor-pointer relative ${isSmallViewport ? 'px-4' : 'px-8'}`}
            animate={{ height: currentHeaderHeight }}
            transition={{ duration: 0.2 }}
            onClick={() => isCollapsibleMode && setIsHeaderVisible(!isHeaderVisible)}
          >
            {/* Collapsed state indicator */}
            {isCollapsibleMode && !isHeaderVisible ? (
              <div className="w-full flex items-center justify-center">
                <div className="text-gray-400 text-xs">Tap to expand</div>
              </div>
            ) : (
              <>
                {/* Left Group - Balance & Buy */}
                <div className={`flex items-center ${isSmallViewport ? 'gap-1 scale-75 origin-left' : 'gap-2'}`}>
                  <div className={isSmallViewport ? 'px-2 py-1 bg-green-600 rounded-lg' : 'px-4 py-2 bg-green-600 rounded-lg'}>
                    <span className={isSmallViewport ? 'text-white text-base' : 'text-white text-xl'}><span className="font-normal">BB </span><span className="font-bold">{formatBalance(balance)}</span></span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBuyModal(true);
                    }}
                    className={isSmallViewport ? 'px-2 py-1 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold rounded-lg whitespace-nowrap' : 'px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-lg transition-colors whitespace-nowrap'}
                  >
                    BUY B-BUCKS
                  </button>
                </div>

                {/* Center Group - Share, Table ID, Settings (absolutely centered in viewport) */}
                <div className={`absolute left-1/2 -translate-x-1/2 flex items-center ${isSmallViewport ? 'gap-1 scale-75' : 'gap-3'}`}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare();
                    }}
                    className={isSmallViewport ? 'p-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg' : 'p-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors'}
                    title="Share"
                  >
                    <svg className={isSmallViewport ? 'w-4 h-4' : 'w-5 h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                  <span className={isSmallViewport ? 'text-white text-sm font-medium px-1' : 'text-white text-lg font-medium'}>
                    Tableid:{tableId}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSettingsModal(true);
                    }}
                    className={isSmallViewport ? 'p-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg' : 'p-2.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors'}
                    title="Settings"
                  >
                    <svg className={isSmallViewport ? 'w-4 h-4' : 'w-5 h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </div>

                {/* Right Group - Join/Watch, Leave */}
                <div className={`flex items-center ${isSmallViewport ? 'gap-1 scale-75 origin-right' : 'gap-2'}`}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // TODO: Join/Watch toggle
                    }}
                    className={isSmallViewport ? 'px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg' : 'px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-colors'}
                  >
                    {mySeatPosition ? 'Watch' : 'Join'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLeaveTable();
                    }}
                    className={isSmallViewport ? 'px-2 py-1 bg-red-500 hover:bg-red-400 text-white text-xs font-bold rounded-lg' : 'px-4 py-2 bg-red-500 hover:bg-red-400 text-white font-bold rounded-lg transition-colors'}
                  >
                    Leave
                  </button>
                </div>
              </>
            )}
          </motion.div>

          {/* Game Area - min-h-0 fixes flexbox height inheritance for children with h-full */}
          <div ref={gameAreaRef} className="flex-1 relative overflow-hidden min-h-0" style={{ backgroundColor: '#1a4731' }}>
            {/* PixiJS Game Table - SINGLE INSTANCE for both viewport sizes */}
            <PixiGameTableV1
              ref={pixiRef}
              dealer={effectiveDealer}
              dealerPersona={dealerPersona}
              players={pixiPlayers}
              mySeatPosition={mySeatPosition}
              currentTurnSeat={currentTurnSeat}
              currentHandIndex={currentHandIndex}
              onSeatClick={handleSitDown}
              seatRadiusScale={seatRadiusScale}
              seatGroupOffset={seatGroupOffset}
              seatSizeScale={seatSizeScale}
              shoeConfig={shoeConfig}
              trayConfig={trayConfig}
              shoeCardsConfig={shoeCardsConfig}
              chipBorderConfig={chipBorderConfig}
              chipCounterConfig={chipCounterConfig}
              kittyConfig={kittyConfig}
              sidebetIconConfig={sidebetIconConfig}
              opponentKittyConfig={opponentKittyConfig}
              opponentKittyConfigs={opponentKittyConfigs}
              opponentSidebetIconConfig={opponentSidebetIconConfig}
              sidebetIconConfigs={sidebetIconConfigs}
              seatConfigs={seatConfigs}
              dealerScoreboxOffset={dealerScoreboxOffset}
              betChipConfigs={betChipConfigs}
              insuranceChipConfigs={insuranceChipConfigs}
              scoreboxConfig={scoreboxConfig}
              outcomeLabelConfig={outcomeLabelConfig}
              shoeCardOffsets={shoeCardOffsets}
              dealerButtonSeat={effectiveDealerButtonSeat}
              dealerButtonConfig={dealerButtonConfig}
              // UI elements (remaining in PixiJS: chip rail and staged bet)
              onChipClick={handleFieldChipClick}
              onClearBet={handleClear}
              stagedAmount={stagedAmount}
              stagedChips={stagedChips}
              displayedAmount={displayedAmount}
              balance={balance}
              chipRailConfig={{ x: chipRailOffset.x, y: chipRailOffset.y, scale: 100, spacing: 85 }}
              stagedBetConfig={{ x: stagedBetOffset.x, y: stagedBetOffset.y, scale: 100, ...stagedBetBoxConfig }}
              trayChipsOffset={trayChipsOffset}
              cardConfig={cardConfig}
              onChipAnimationStart={handleChipAnimationStart}
              onChipAnimationEnd={handleChipAnimationEnd}
              onStraightenCards={handleStraightenCards}
              showBetChips={showBetChips}
              showInsuranceChips={showInsuranceChips}
              showHandLabels={showHandLabels}
              handLabelConfig={handLabelConfig}
              handLabelConfigs={handLabelConfigs}
              devPopulateTable={devPopulateTable}
              chipXOffsets={chipXOffsets}
              chipYOffsets={chipYOffsets}
              chipLabelYOffsets={chipLabelYOffsets}
              chipLabelPillOpacity={chipLabelPillOpacity}
              usernameY={usernameY}
              headerHeight={currentHeaderHeight}
              availableWidth={availableCanvasDimensions.width}
              availableHeight={availableCanvasDimensions.height}
              // PixiJS Control Panel props
              usePixiControlPanel={true}
              tableId={tableId}
              onHit={handleHit}
              onStand={handleStand}
              onDouble={handleDouble}
              onSplit={handleSplit}
              onSurrender={handleSurrender}
              onBet={handlePixiBet}
              onAutobet={toggleAutobet}
              onInsuranceAccept={handleInsurance}
              onInsuranceDecline={handleDeclineInsurance}
              onHowToBetClose={(dontShowAgain) => {
                if (dontShowAgain) {
                  // Fire and forget - save preference to not show again
                  useAuthStore.getState().setHideSeatTutorial(true);
                }
              }}
              panelTextSizes={panelTextSizes}
              beamConfig={{ ...beamConfig, rotations: beamRotations }}
              devShowBeam={devShowBeam}
              devBeamSeat={devBeamSeat}
              kittyAmount={currentBet}
              onSidebetPlace={handleSidebetPlace}
              onSidebetRemove={handleSidebetRemove}
              onSidebetIconClick={handleSidebetClick}
              isConnected={isConnected}
              onControlPanelPositionChange={setPanelPosition}
              buddyActive={showRecommendations}
              penetration={penetration}
              trueCount={trueCount}
              buddyHudConfig={buddyHudConfig}
              // Dev card positioning
              perSeatCardConfigs={perSeatCardConfigs}
              devCardSeat={devCardSeat}
              leftCornerCardConfig={leftCornerCardConfig}
              rightCornerCardConfig={rightCornerCardConfig}
            />

            {/* Insurance Modal - NOW RENDERED IN PIXI (PixiInsurancePanel) */}
            {/* The Pixi insurance panel is controlled via useEffect in this component */}

            {/* Side Panel - position opposite to control panel (Chat & Stats) */}
            <SidePanel
              messages={chatMessages}
              onSendMessage={handleSendChatMessage}
              stats={stats}
              onRequestStats={handleRequestStats}
              side={panelPosition === 'viewport-left' || panelPosition === 'table-left' ? 'right' : 'left'}
              smallViewportMode={isSmallViewport}
              headerHeight={currentHeaderHeight}
              containerHeight={isSmallViewport ? effectiveViewport.height : undefined}
              onSidebetClick={handleSidebetClick}
              activeSidebets={activeSidebetIds as SidebetId[]}
            />
          </div>
        </div>
      </div>

      {/* Buy B-Bucks Modal */}
      {showBuyModal && (
        <ChipPurchaseModal onClose={() => setShowBuyModal(false)} />
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          shareUrl={shareUrl}
          tableName={tableName || 'Table'}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Settings Modal */}
      <TableSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Seat Tutorial Modal - Now using PixiHowToBetModal in PixiGameTableV1 */}

      {/* DEV Controls Panel - Tabbed, Draggable */}
      {showDevControls && (
        <motion.div
          ref={devPanelRef}
          drag
          dragMomentum={false}
          dragElastic={0}
          onDragEnd={(_, info) => setDevPanelPos({ x: devPanelPos.x + info.offset.x, y: devPanelPos.y + info.offset.y })}
          style={{ x: devPanelPos.x, y: devPanelPos.y }}
          className="fixed top-28 left-4 z-[100] bg-gray-900/95 border border-gray-700 rounded-lg p-3 w-80 text-white text-sm max-h-[85vh] overflow-hidden flex flex-col cursor-grab active:cursor-grabbing"
        >
          <h3 className="font-bold text-purple-400 mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm-2 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm8-14a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm-2 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm2 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm6-12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm-2 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm2 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
              </svg>
              <span>DEV Controls</span>
            </div>
            <button
              onClick={() => {
                const config = {
                  seats: { radius: seatRadiusScale, size: seatSizeScale, offset: seatGroupOffset, perSeat: seatConfigs, usernameY, dealerScorebox: dealerScoreboxOffset },
                  chips: { railOffset: chipRailOffset, stagedOffset: stagedBetOffset, stagedBetBox: stagedBetBoxConfig, chipX: chipXOffsets, chipY: chipYOffsets, labelY: chipLabelYOffsets, labelPillOpacity: chipLabelPillOpacity },
                  table: { shoe: shoeConfig, tray: trayConfig, shoeCards: shoeCardsConfig, shoeCardOffsets, chipBorder: chipBorderConfig, chipCounter: chipCounterConfig },
                  dealerButton: { visualSeat: effectiveDealerButtonSeat, override: dealerButtonSeatOverride, serverActualSeat: buttonPosition, config: dealerButtonConfig },
                  perSeatBetChips: betChipConfigs,
                  perSeatInsurance: insuranceChipConfigs,
                  perSeatSidebetIcons: sidebetIconConfigs,
                  perSeatOpponentKitty: opponentKittyConfigs,
                  panel: { position: panelPosition, textSizes: panelTextSizes },
                  scorebox: scoreboxConfig,
                  outcomeLabel: outcomeLabelConfig,
                  cards: cardConfig,
                  beam: beamConfig,
                  sidebetIcons: sidebetIconConfig,
                  buddyHud: buddyHudConfig,
                  perSeatCards: perSeatCardConfigs,
                  cornerCards: { left: leftCornerCardConfig, right: rightCornerCardConfig },
                };
                navigator.clipboard.writeText(JSON.stringify(config, null, 2));
                alert('Config copied to clipboard!');
              }}
              className="px-2 py-1 bg-green-600 hover:bg-green-500 rounded text-xs font-bold"
            >
              Copy
            </button>
            <button
              onClick={async () => {
                const config = {
                  perSeatBetChips: betChipConfigs,
                  perSeatInsurance: insuranceChipConfigs,
                  perSeatSidebetIcons: sidebetIconConfigs,
                  perSeatOpponentKitty: opponentKittyConfigs,
                  perSeatCards: perSeatCardConfigs,
                };
                try {
                  const res = await fetch('/api/dev/save-config', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(config),
                  });
                  const data = await res.json();
                  if (data.success) {
                    alert(`Saved to source! Updated: ${data.updated?.join(', ') || 'none'}`);
                  } else {
                    alert(`Save failed: ${data.message || data.error}`);
                  }
                } catch (err) {
                  alert(`Save error: ${err instanceof Error ? err.message : 'Unknown error'}`);
                }
              }}
              className="px-2 py-1 bg-orange-600 hover:bg-orange-500 rounded text-xs font-bold"
              title="Save per-seat configs directly to PixiTablePage.tsx source code"
            >
              Save
            </button>
            <button
              onClick={handleDevSocketToggle}
              className={`px-2 py-1 rounded text-xs font-bold ${devSocketDisconnected ? 'bg-green-600 hover:bg-green-500' : 'bg-red-600 hover:bg-red-500'}`}
            >
              {devSocketDisconnected ? '🔌 Connect' : '⚡ Disconnect'}
            </button>
          </h3>

          {/* Tabs */}
          <div className="flex gap-1 mb-3 border-b border-gray-700 pb-2">
            {(['seats', 'chips', 'table', 'panel', 'cards', 'layout'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setDevTab(tab)}
                className={`flex-1 py-1 rounded text-xs font-bold capitalize ${devTab === tab ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto pr-1">
            {/* SEATS TAB */}
            {devTab === 'seats' && (
              <div className="space-y-3">
                {/* Per-Seat Controls */}
                <div className="border-b border-gray-700 pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-purple-300 text-xs font-bold">Per-Seat Config</label>
                    <div className="flex gap-1">
                      {/* Dealer seat (0) */}
                      <button
                        onClick={() => setSelectedSeat(0)}
                        className={`w-6 h-6 rounded text-xs font-bold ${selectedSeat === 0 ? 'bg-amber-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        title="Dealer"
                      >
                        D
                      </button>
                      {/* Player seats (1-7) */}
                      {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                        <button
                          key={seat}
                          onClick={() => setSelectedSeat(seat)}
                          className={`w-6 h-6 rounded text-xs font-bold ${selectedSeat === seat ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        >
                          {seat}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Seat Position: X, Y, Rot, Scale */}
                  <div className="mb-2">
                    <span className="text-xs text-gray-400 block mb-1">{selectedSeat === 0 ? 'Dealer' : `Seat ${selectedSeat}`}</span>
                    <div className="grid grid-cols-4 gap-1">
                      {(['x', 'y', 'rot', 'scale'] as const).map(key => (
                        <div key={key} className="flex flex-col items-center">
                          <span className="text-[10px] text-gray-500 uppercase">{key}</span>
                          <div className="flex items-center gap-0.5">
                            <AutoRepeatButton onClick={() => setSeatConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{seatConfigs[selectedSeat][key]}</span>
                            <AutoRepeatButton onClick={() => setSeatConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Dealer Scorebox Offset - only shown when dealer selected */}
                  {selectedSeat === 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-400 block mb-1">Dealer Scorebox</span>
                      <div className="grid grid-cols-2 gap-1">
                        {(['x', 'y'] as const).map(key => (
                          <div key={key} className="flex flex-col items-center">
                            <span className="text-[10px] text-gray-500 uppercase">{key}</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setDealerScoreboxOffset(p => ({ ...p, [key]: p[key] - 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                              <span className="w-6 text-center text-[10px] font-mono">{dealerScoreboxOffset[key]}</span>
                              <AutoRepeatButton onClick={() => setDealerScoreboxOffset(p => ({ ...p, [key]: p[key] + 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Dealer Cards Offset - only shown when dealer selected */}
                  {selectedSeat === 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-400 block mb-1">Dealer Cards</span>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] text-gray-500 uppercase">X</span>
                          <div className="flex items-center gap-0.5">
                            <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, dealerCardsX: p.dealerCardsX - 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{cardConfig.dealerCardsX}</span>
                            <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, dealerCardsX: p.dealerCardsX + 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] text-gray-500 uppercase">Y</span>
                          <div className="flex items-center gap-0.5">
                            <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, dealerCardsY: p.dealerCardsY - 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{cardConfig.dealerCardsY}</span>
                            <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, dealerCardsY: p.dealerCardsY + 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Global Seat Controls */}
                <div className="border-b border-gray-700 pb-3">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Global</label>
                  {/* Seat Radius */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-gray-400 text-xs">Radius</label>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setSeatRadiusScale(p => Math.max(20, p - 1))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{seatRadiusScale}%</span>
                        <AutoRepeatButton onClick={() => setSeatRadiusScale(p => Math.min(200, p + 1))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                  {/* Seat Size */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-gray-400 text-xs">Size</label>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setSeatSizeScale(p => Math.max(50, p - 1))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{seatSizeScale}%</span>
                        <AutoRepeatButton onClick={() => setSeatSizeScale(p => Math.min(150, p + 1))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                  {/* Seat Group X */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-gray-400 text-xs">Group X</label>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setSeatGroupOffset(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{seatGroupOffset.x}</span>
                        <AutoRepeatButton onClick={() => setSeatGroupOffset(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                  {/* Seat Group Y */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-gray-400 text-xs">Group Y</label>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setSeatGroupOffset(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{seatGroupOffset.y}</span>
                        <AutoRepeatButton onClick={() => setSeatGroupOffset(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                  {/* Username Y Offset */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-gray-400 text-xs">Username Y</label>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setUsernameY(p => p - 1)} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{usernameY}</span>
                        <AutoRepeatButton onClick={() => setUsernameY(p => p + 1)} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scorebox Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-gray-400 text-xs block mb-1">Scorebox</label>
                  <div className="flex gap-2 mb-1">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Scale</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, scale: Math.max(50, p.scale - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{scoreboxConfig.scale}%</span>
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, scale: Math.min(200, p.scale + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Font</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, fontSize: Math.max(8, p.fontSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{scoreboxConfig.fontSize}px</span>
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, fontSize: Math.min(24, p.fontSize + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{scoreboxConfig.x}</span>
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{scoreboxConfig.y}</span>
                          <AutoRepeatButton onClick={() => setScoreboxConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Outcome Label Controls */}
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Outcome Label</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Scale</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setOutcomeLabelConfig(p => ({ ...p, scale: Math.max(50, p.scale - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{outcomeLabelConfig.scale}%</span>
                          <AutoRepeatButton onClick={() => setOutcomeLabelConfig(p => ({ ...p, scale: Math.min(200, p.scale + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Font</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setOutcomeLabelConfig(p => ({ ...p, fontSize: Math.max(8, p.fontSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{outcomeLabelConfig.fontSize}px</span>
                          <AutoRepeatButton onClick={() => setOutcomeLabelConfig(p => ({ ...p, fontSize: Math.min(24, p.fontSize + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">X Offset</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setOutcomeLabelConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{outcomeLabelConfig.x}px</span>
                      <AutoRepeatButton onClick={() => setOutcomeLabelConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">Font</span>
                    <select
                      value={outcomeLabelConfig.fontFamily}
                      onChange={(e) => setOutcomeLabelConfig(p => ({ ...p, fontFamily: e.target.value }))}
                      className="bg-gray-700 text-white text-xs rounded px-2 py-1"
                    >
                      <option value="Arial">Arial</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Courier New">Courier New</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Impact">Impact</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* CHIPS TAB */}
            {devTab === 'chips' && (
              <div className="space-y-3">
                {/* Chip Rail X/Y */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-gray-400 text-xs block mb-1">Rail Offset</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setChipRailOffset(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{chipRailOffset.x}</span>
                          <AutoRepeatButton onClick={() => setChipRailOffset(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setChipRailOffset(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{chipRailOffset.y}</span>
                          <AutoRepeatButton onClick={() => setChipRailOffset(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Staged Bet X/Y */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-gray-400 text-xs block mb-1">Staged Bet Offset</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setStagedBetOffset(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{stagedBetOffset.x}</span>
                          <AutoRepeatButton onClick={() => setStagedBetOffset(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setStagedBetOffset(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{stagedBetOffset.y}</span>
                          <AutoRepeatButton onClick={() => setStagedBetOffset(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Staged Bet Box Config */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-gray-400 text-xs block mb-1">Staged Bet Box</label>
                  <div className="space-y-1">
                    {/* Width */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Width</span>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, width: p.width - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{stagedBetBoxConfig.width}</span>
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, width: p.width + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    {/* Height */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Height</span>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, height: p.height - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{stagedBetBoxConfig.height}</span>
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, height: p.height + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    {/* Font Size */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Font</span>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, fontSize: Math.max(8, p.fontSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{stagedBetBoxConfig.fontSize}</span>
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, fontSize: p.fontSize + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    {/* Border */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Border</span>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, borderThickness: Math.max(0, p.borderThickness - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{stagedBetBoxConfig.borderThickness}</span>
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, borderThickness: p.borderThickness + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    {/* Clear Font Size */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Clear Font</span>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, clearFontSize: Math.max(6, p.clearFontSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{stagedBetBoxConfig.clearFontSize}</span>
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, clearFontSize: p.clearFontSize + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    {/* Build Your Bet Font Size */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">BYB Font</span>
                      <div className="flex items-center gap-1">
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, buildYourBetFontSize: Math.max(8, p.buildYourBetFontSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-xs font-mono">{stagedBetBoxConfig.buildYourBetFontSize}</span>
                        <AutoRepeatButton onClick={() => setStagedBetBoxConfig(p => ({ ...p, buildYourBetFontSize: p.buildYourBetFontSize + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Individual Chip X Offsets */}
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Chip X Offsets</label>
                  <div className="grid grid-cols-4 gap-1">
                    {CHIP_DENOMINATIONS.map((d) => (
                      <div key={d} className="text-center">
                        <div className="text-xs text-gray-500 mb-1">{d >= 1000 ? `${d/1000}K` : d}</div>
                        <div className="flex items-center justify-center gap-0.5">
                          <AutoRepeatButton onClick={() => setChipXOffsets(p => ({ ...p, [d]: (p[d] || 0) - 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-6 text-center text-xs font-mono">{chipXOffsets[d] || 0}</span>
                          <AutoRepeatButton onClick={() => setChipXOffsets(p => ({ ...p, [d]: (p[d] || 0) + 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Individual Chip Y Offsets */}
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Chip Y Offsets</label>
                  <div className="grid grid-cols-4 gap-1">
                    {CHIP_DENOMINATIONS.map((d) => (
                      <div key={d} className="text-center">
                        <div className="text-xs text-gray-500 mb-1">{d >= 1000 ? `${d/1000}K` : d}</div>
                        <div className="flex items-center justify-center gap-0.5">
                          <AutoRepeatButton onClick={() => setChipYOffsets(p => ({ ...p, [d]: (p[d] || 0) - 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-6 text-center text-xs font-mono">{chipYOffsets[d] || 0}</span>
                          <AutoRepeatButton onClick={() => setChipYOffsets(p => ({ ...p, [d]: (p[d] || 0) + 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Individual Label Y Offsets */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-gray-400 text-xs block mb-1">Label Y Offsets</label>
                  <div className="grid grid-cols-4 gap-1">
                    {CHIP_DENOMINATIONS.map((d) => (
                      <div key={d} className="text-center">
                        <div className="text-xs text-gray-500 mb-1">{d >= 1000 ? `${d/1000}K` : d}</div>
                        <div className="flex items-center justify-center gap-0.5">
                          <AutoRepeatButton onClick={() => setChipLabelYOffsets(p => ({ ...p, [d]: (p[d] || 0) - 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-6 text-center text-xs font-mono">{chipLabelYOffsets[d] || 0}</span>
                          <AutoRepeatButton onClick={() => setChipLabelYOffsets(p => ({ ...p, [d]: (p[d] || 0) + 1 }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Label Pill Opacity */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-gray-400 text-xs">Label Pill Opacity</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setChipLabelPillOpacity(p => Math.max(0, p - 5))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{chipLabelPillOpacity}%</span>
                      <AutoRepeatButton onClick={() => setChipLabelPillOpacity(p => Math.min(100, p + 5))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TABLE TAB */}
            {devTab === 'table' && (
              <div className="space-y-3">
                {/* Populate Table Toggle */}
                <div className="flex items-center justify-between border-b border-gray-700 pb-2">
                  <span className="text-xs text-gray-400">Populate Table</span>
                  <button
                    onClick={() => setDevPopulateTable(p => !p)}
                    className={`px-2 py-1 rounded text-xs font-bold ${devPopulateTable ? 'bg-green-600' : 'bg-gray-700'}`}
                  >
                    {devPopulateTable ? 'ON' : 'OFF'}
                  </button>
                </div>
                {/* Shoe Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Shoe</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeConfig.x}</span>
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeConfig.y}</span>
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Rot</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, rotation: p.rotation - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeConfig.rotation}°</span>
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, rotation: p.rotation + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Scale</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, scale: Math.max(10, p.scale - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeConfig.scale}%</span>
                          <AutoRepeatButton onClick={() => setShoeConfig(p => ({ ...p, scale: Math.min(200, p.scale + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Tray Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Tray</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{trayConfig.x}</span>
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{trayConfig.y}</span>
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Rot</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, rotation: p.rotation - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{trayConfig.rotation}°</span>
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, rotation: p.rotation + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Scale</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, scale: Math.max(10, p.scale - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{trayConfig.scale}%</span>
                          <AutoRepeatButton onClick={() => setTrayConfig(p => ({ ...p, scale: Math.min(200, p.scale + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Shoe Cards Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Shoe Cards (Group)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeCardsConfig.x}</span>
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeCardsConfig.y}</span>
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Rot</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, rotation: p.rotation - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeCardsConfig.rotation}°</span>
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, rotation: p.rotation + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Scale</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, scale: Math.max(10, p.scale - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{shoeCardsConfig.scale}%</span>
                          <AutoRepeatButton onClick={() => setShoeCardsConfig(p => ({ ...p, scale: Math.min(200, p.scale + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Per-Card Shoe Offsets */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Shoe Cards (Per-Card)</label>
                  <div className="space-y-2">
                    {([0, 1, 2] as const).map((cardIndex) => (
                      <div key={cardIndex} className="space-y-1">
                        <span className="text-xs text-gray-400">Card #{cardIndex + 1}</span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-gray-500">X</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], x: p[cardIndex].x - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{shoeCardOffsets[cardIndex].x}</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], x: p[cardIndex].x + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-gray-500">Y</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], y: p[cardIndex].y - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{shoeCardOffsets[cardIndex].y}</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], y: p[cardIndex].y + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-gray-500">Rot</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], rotation: p[cardIndex].rotation - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{shoeCardOffsets[cardIndex].rotation}</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], rotation: p[cardIndex].rotation + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-gray-500">Scl</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], scale: Math.max(10, p[cardIndex].scale - 5) } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{shoeCardOffsets[cardIndex].scale}</span>
                            <AutoRepeatButton onClick={() => setShoeCardOffsets(p => ({ ...p, [cardIndex]: { ...p[cardIndex], scale: p[cardIndex].scale + 5 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Dealer Button Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Dealer Button</label>
                  <div className="mb-2">
                    <span className="text-xs text-gray-400 block mb-1">Actual→Visual (Server: {buttonPosition ?? 'none'} → Visual: {effectiveDealerButtonSeat})</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setDealerButtonSeatOverride(null)}
                        className={`w-6 h-6 rounded text-xs font-bold ${dealerButtonSeatOverride === null ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        title="Use server value"
                      >
                        S
                      </button>
                      <button
                        onClick={() => setDealerButtonSeatOverride(0)}
                        className={`w-6 h-6 rounded text-xs font-bold ${dealerButtonSeatOverride === 0 ? 'bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        title="Hide button"
                      >
                        X
                      </button>
                      {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                        <button
                          key={seat}
                          onClick={() => setDealerButtonSeatOverride(seat)}
                          className={`w-6 h-6 rounded text-xs font-bold ${dealerButtonSeatOverride === seat ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        >
                          {seat}
                        </button>
                      ))}
                    </div>
                  </div>
                  {effectiveDealerButtonSeat > 0 && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">X</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setDealerButtonConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-6 text-center text-xs font-mono">{dealerButtonConfig.x}</span>
                              <AutoRepeatButton onClick={() => setDealerButtonConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Y</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setDealerButtonConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-6 text-center text-xs font-mono">{dealerButtonConfig.y}</span>
                              <AutoRepeatButton onClick={() => setDealerButtonConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Size</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setDealerButtonConfig(p => ({ ...p, size: Math.max(50, p.size - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-8 text-center text-xs font-mono">{dealerButtonConfig.size}%</span>
                              <AutoRepeatButton onClick={() => setDealerButtonConfig(p => ({ ...p, size: Math.min(200, p.size + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">BG</span>
                          <input
                            type="color"
                            value={dealerButtonConfig.bgColor}
                            onChange={(e) => setDealerButtonConfig(p => ({ ...p, bgColor: e.target.value }))}
                            className="w-8 h-5 rounded cursor-pointer border-0"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Font</span>
                          <input
                            type="color"
                            value={dealerButtonConfig.fontColor}
                            onChange={(e) => setDealerButtonConfig(p => ({ ...p, fontColor: e.target.value }))}
                            className="w-8 h-5 rounded cursor-pointer border-0"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Border</span>
                          <input
                            type="color"
                            value={dealerButtonConfig.borderColor}
                            onChange={(e) => setDealerButtonConfig(p => ({ ...p, borderColor: e.target.value }))}
                            className="w-8 h-5 rounded cursor-pointer border-0"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Chip Border Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Chip Stack Border</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Opacity</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setChipBorderConfig(p => ({ ...p, opacity: Math.max(0, p.opacity - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{chipBorderConfig.opacity}%</span>
                          <AutoRepeatButton onClick={() => setChipBorderConfig(p => ({ ...p, opacity: Math.min(100, p.opacity + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Thick</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setChipBorderConfig(p => ({ ...p, thickness: Math.max(0, p.thickness - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-6 text-center text-xs font-mono">{chipBorderConfig.thickness}</span>
                          <AutoRepeatButton onClick={() => setChipBorderConfig(p => ({ ...p, thickness: Math.min(10, p.thickness + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Color</span>
                        <input
                          type="color"
                          value={chipBorderConfig.color}
                          onChange={(e) => setChipBorderConfig(p => ({ ...p, color: e.target.value }))}
                          className="w-8 h-5 rounded cursor-pointer border-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* PLAYER vs OPPONENT Config Selector */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Config Target</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDevConfigTarget('player')}
                      className={`flex-1 px-2 py-1 rounded text-xs font-bold transition-colors ${devConfigTarget === 'player' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                    >
                      PLAYER
                    </button>
                    <button
                      onClick={() => setDevConfigTarget('opponent')}
                      className={`flex-1 px-2 py-1 rounded text-xs font-bold transition-colors ${devConfigTarget === 'opponent' ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                    >
                      OPPONENT
                    </button>
                  </div>
                </div>
                {/* Kitty Stack Position Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className={`text-xs font-bold block mb-2 ${devConfigTarget === 'player' ? 'text-green-300' : 'text-orange-300'}`}>
                    Kitty Stack Position ({devConfigTarget === 'player' ? 'Player' : 'Opponent'})
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setKittyConfig : setOpponentKittyConfig)(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{(devConfigTarget === 'player' ? kittyConfig : opponentKittyConfig).x}</span>
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setKittyConfig : setOpponentKittyConfig)(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setKittyConfig : setOpponentKittyConfig)(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{(devConfigTarget === 'player' ? kittyConfig : opponentKittyConfig).y}</span>
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setKittyConfig : setOpponentKittyConfig)(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Rot</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setKittyConfig : setOpponentKittyConfig)(p => ({ ...p, rotation: p.rotation - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{(devConfigTarget === 'player' ? kittyConfig : opponentKittyConfig).rotation}°</span>
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setKittyConfig : setOpponentKittyConfig)(p => ({ ...p, rotation: p.rotation + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Chip Counter Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-purple-300 text-xs font-bold">Chip Counter</label>
                    <button
                      onClick={() => setChipCounterConfig(p => ({ ...p, show: !p.show }))}
                      className={`px-2 py-1 rounded text-xs font-bold ${chipCounterConfig.show ? 'bg-green-600' : 'bg-gray-700'}`}
                    >
                      {chipCounterConfig.show ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  {chipCounterConfig.show && (
                    <>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Pill</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, pillOpacity: Math.max(0, p.pillOpacity - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-8 text-center text-xs font-mono">{chipCounterConfig.pillOpacity}%</span>
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, pillOpacity: Math.min(100, p.pillOpacity + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Font</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, fontSize: Math.max(8, p.fontSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-6 text-center text-xs font-mono">{chipCounterConfig.fontSize}</span>
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, fontSize: Math.min(24, p.fontSize + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Color</span>
                            <input
                              type="color"
                              value={chipCounterConfig.fontColor}
                              onChange={(e) => setChipCounterConfig(p => ({ ...p, fontColor: e.target.value }))}
                              className="w-8 h-5 rounded cursor-pointer border-0"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">X</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-8 text-center text-xs font-mono">{chipCounterConfig.x}</span>
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Y</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-8 text-center text-xs font-mono">{chipCounterConfig.y}</span>
                              <AutoRepeatButton onClick={() => setChipCounterConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* Turn Indicator Beam Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Turn Indicator Beam</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Color</span>
                        <input
                          type="color"
                          value={beamConfig.color}
                          onChange={(e) => setBeamConfig(p => ({ ...p, color: e.target.value }))}
                          className="w-8 h-5 rounded cursor-pointer border-0"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Opacity</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setBeamConfig(p => ({ ...p, opacity: Math.max(5, p.opacity - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{beamConfig.opacity}%</span>
                          <AutoRepeatButton onClick={() => setBeamConfig(p => ({ ...p, opacity: Math.min(100, p.opacity + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Width</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setBeamConfig(p => ({ ...p, width: Math.max(5, p.width - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{beamConfig.width}°</span>
                          <AutoRepeatButton onClick={() => setBeamConfig(p => ({ ...p, width: Math.min(60, p.width + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    {/* Show Beam toggle for positioning */}
                    <div className="col-span-2 mt-2 flex items-center gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={devShowBeam}
                          onChange={(e) => setDevShowBeam(e.target.checked)}
                          className="w-3.5 h-3.5 rounded"
                        />
                        <span className="text-xs text-gray-400">Show Beam</span>
                      </label>
                      {devShowBeam && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Seat:</span>
                          {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                            <button
                              key={seat}
                              onClick={() => setDevBeamSeat(seat)}
                              className={`w-5 h-5 rounded text-[10px] font-bold ${devBeamSeat === seat ? 'bg-amber-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                            >
                              {seat}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Beam Rotations per seat */}
                    <div className="col-span-2 mt-2">
                      <span className="text-xs text-gray-500 block mb-1">Seat Rotations</span>
                      <div className="grid grid-cols-7 gap-0.5">
                        {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                          <div key={seat} className="flex flex-col items-center">
                            <span className="text-[9px] text-gray-500">{seat}</span>
                            <div className="flex flex-col items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setBeamRotations(p => ({ ...p, [seat]: p[seat] + 1 }))} className="w-5 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[9px]">+</AutoRepeatButton>
                              <span className="text-[9px] font-mono w-7 text-center">{beamRotations[seat]}°</span>
                              <AutoRepeatButton onClick={() => setBeamRotations(p => ({ ...p, [seat]: p[seat] - 1 }))} className="w-5 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[9px]">-</AutoRepeatButton>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Per-Seat Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-purple-300 text-xs font-bold">Per-Seat Config</label>
                    <div className="flex gap-1">
                      {/* Dealer seat (0) */}
                      <button
                        onClick={() => setSelectedSeat(0)}
                        className={`w-6 h-6 rounded text-xs font-bold ${selectedSeat === 0 ? 'bg-amber-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        title="Dealer"
                      >
                        D
                      </button>
                      {/* Player seats (1-7) */}
                      {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                        <button
                          key={seat}
                          onClick={() => setSelectedSeat(seat)}
                          className={`w-6 h-6 rounded text-xs font-bold ${selectedSeat === seat ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        >
                          {seat}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Note: Seat positioning moved to SEATS tab */}
                  {/* Bet Chips */}
                  <div className="mb-2">
                    <span className="text-xs text-gray-400 block mb-1">{selectedSeat === 0 ? 'Kitty (Dealer Chips)' : `Seat ${selectedSeat} Bet Chips`}</span>
                    <div className="grid grid-cols-4 gap-1">
                      {(['radiusOffset', 'x', 'y', 'scale'] as const).map(key => (
                        <div key={key} className="flex flex-col items-center">
                          <span className="text-[10px] text-gray-500 capitalize">{key === 'radiusOffset' ? 'R' : key}</span>
                          <div className="flex items-center gap-0.5">
                            <AutoRepeatButton onClick={() => setBetChipConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{betChipConfigs[selectedSeat][key]}</span>
                            <AutoRepeatButton onClick={() => setBetChipConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Insurance Chips */}
                  <div>
                    <span className="text-xs text-gray-400 block mb-1">{selectedSeat === 0 ? 'Insurance Line' : `Seat ${selectedSeat} Insurance`}</span>
                    <div className="grid grid-cols-4 gap-1">
                      {(['radiusOffset', 'x', 'y', 'scale'] as const).map(key => (
                        <div key={key} className="flex flex-col items-center">
                          <span className="text-[10px] text-gray-500 capitalize">{key === 'radiusOffset' ? 'R' : key}</span>
                          <div className="flex items-center gap-0.5">
                            <AutoRepeatButton onClick={() => setInsuranceChipConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                            <span className="w-6 text-center text-[10px] font-mono">{insuranceChipConfigs[selectedSeat][key]}</span>
                            <AutoRepeatButton onClick={() => setInsuranceChipConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Opponent Kitty (per-seat) - only show for seats 1-7 */}
                  {selectedSeat >= 1 && selectedSeat <= 7 && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-400 block mb-1">Seat {selectedSeat} Opponent Kitty</span>
                      <div className="grid grid-cols-4 gap-1">
                        {(['radiusOffset', 'x', 'y', 'scale'] as const).map(key => (
                          <div key={key} className="flex flex-col items-center">
                            <span className="text-[10px] text-gray-500 capitalize">{key === 'radiusOffset' ? 'R' : key}</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setOpponentKittyConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                              <span className="w-6 text-center text-[10px] font-mono">{opponentKittyConfigs[selectedSeat]?.[key] ?? 0}</span>
                              <AutoRepeatButton onClick={() => setOpponentKittyConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Tray Chips (Dealer Chip Rail) Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Tray Chips</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setTrayChipsOffset(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{trayChipsOffset.x}</span>
                          <AutoRepeatButton onClick={() => setTrayChipsOffset(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setTrayChipsOffset(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{trayChipsOffset.y}</span>
                          <AutoRepeatButton onClick={() => setTrayChipsOffset(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Mock Players Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Show Mock Players</span>
                  <button
                    onClick={() => setShowMockPlayers(p => !p)}
                    className={`px-2 py-1 rounded text-xs font-bold ${showMockPlayers ? 'bg-green-600' : 'bg-gray-700'}`}
                  >
                    {showMockPlayers ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Bet Chips Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Show Bet Chips</span>
                  <button
                    onClick={() => setShowBetChips(p => !p)}
                    className={`px-2 py-1 rounded text-xs font-bold ${showBetChips ? 'bg-green-600' : 'bg-gray-700'}`}
                  >
                    {showBetChips ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Insurance Chips Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Show Insurance Chips</span>
                  <button
                    onClick={() => setShowInsuranceChips(p => !p)}
                    className={`px-2 py-1 rounded text-xs font-bold ${showInsuranceChips ? 'bg-green-600' : 'bg-gray-700'}`}
                  >
                    {showInsuranceChips ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Hand Labels Toggle + Position */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Show Hand Labels</span>
                  <button
                    onClick={() => setShowHandLabels(p => !p)}
                    className={`px-2 py-1 rounded text-xs font-bold ${showHandLabels ? 'bg-green-600' : 'bg-gray-700'}`}
                  >
                    {showHandLabels ? 'ON' : 'OFF'}
                  </button>
                </div>
                {/* Hand Label Position Controls - only show when labels are visible */}
                {showHandLabels && (
                  <div className="space-y-2 ml-2 border-l-2 border-gray-700 pl-2">
                    {/* Global offsets */}
                    <div className="text-xs text-purple-300 font-bold">Global</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setHandLabelConfig(p => ({ ...p, x: p.x - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{handLabelConfig.x}</span>
                          <AutoRepeatButton onClick={() => setHandLabelConfig(p => ({ ...p, x: p.x + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setHandLabelConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{handLabelConfig.y}</span>
                          <AutoRepeatButton onClick={() => setHandLabelConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    {/* Per-seat offsets */}
                    <div className="text-xs text-purple-300 font-bold">Per-Seat (Seat {selectedSeat || 1})</div>
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                        <button
                          key={seat}
                          onClick={() => setSelectedSeat(seat)}
                          className={`w-6 h-6 rounded text-xs font-bold ${selectedSeat === seat ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                        >
                          {seat}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">X</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setHandLabelConfigs(p => ({ ...p, [selectedSeat || 1]: { ...p[selectedSeat || 1], x: p[selectedSeat || 1].x - 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{handLabelConfigs[selectedSeat || 1]?.x || 0}</span>
                          <AutoRepeatButton onClick={() => setHandLabelConfigs(p => ({ ...p, [selectedSeat || 1]: { ...p[selectedSeat || 1], x: p[selectedSeat || 1].x + 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => setHandLabelConfigs(p => ({ ...p, [selectedSeat || 1]: { ...p[selectedSeat || 1], y: p[selectedSeat || 1].y - 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-8 text-center text-xs font-mono">{handLabelConfigs[selectedSeat || 1]?.y || 0}</span>
                          <AutoRepeatButton onClick={() => setHandLabelConfigs(p => ({ ...p, [selectedSeat || 1]: { ...p[selectedSeat || 1], y: p[selectedSeat || 1].y + 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sidebet Icon Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className={`text-xs font-bold block mb-2 ${devConfigTarget === 'player' ? 'text-green-300' : 'text-orange-300'}`}>
                    Sidebet Icons ({devConfigTarget === 'player' ? 'Player' : 'Opponent'})
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Base Y</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setSidebetIconConfig : setOpponentSidebetIconConfig)(p => ({ ...p, baseRadiusOffset: p.baseRadiusOffset - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-10 text-center text-xs font-mono">{(devConfigTarget === 'player' ? sidebetIconConfig : opponentSidebetIconConfig).baseRadiusOffset}</span>
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setSidebetIconConfig : setOpponentSidebetIconConfig)(p => ({ ...p, baseRadiusOffset: p.baseRadiusOffset + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Scale</span>
                        <div className="flex items-center gap-0.5">
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setSidebetIconConfig : setOpponentSidebetIconConfig)(p => ({ ...p, scale: Math.max(50, p.scale - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-10 text-center text-xs font-mono">{(devConfigTarget === 'player' ? sidebetIconConfig : opponentSidebetIconConfig).scale}%</span>
                          <AutoRepeatButton onClick={() => (devConfigTarget === 'player' ? setSidebetIconConfig : setOpponentSidebetIconConfig)(p => ({ ...p, scale: Math.min(200, p.scale + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Per-slot controls */}
                  <div className="space-y-2 mt-2">
                    {(['slot0', 'slot1', 'slot2'] as const).map((slot, index) => {
                      const activeConfig = devConfigTarget === 'player' ? sidebetIconConfig : opponentSidebetIconConfig;
                      const setConfig = devConfigTarget === 'player' ? setSidebetIconConfig : setOpponentSidebetIconConfig;
                      return (
                        <div key={slot} className="space-y-1">
                          <span className="text-xs text-gray-400">{['Left', 'Center', 'Right'][index]}</span>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-0.5">
                              <span className="text-[10px] text-gray-500">X</span>
                              <AutoRepeatButton onClick={() => setConfig(p => ({ ...p, [slot]: { ...p[slot], x: p[slot].x - 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-8 text-center text-[10px] font-mono">{activeConfig[slot].x}</span>
                              <AutoRepeatButton onClick={() => setConfig(p => ({ ...p, [slot]: { ...p[slot], x: p[slot].x + 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                            <div className="flex items-center gap-0.5">
                              <span className="text-[10px] text-gray-500">Y</span>
                              <AutoRepeatButton onClick={() => setConfig(p => ({ ...p, [slot]: { ...p[slot], y: p[slot].y - 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                              <span className="w-8 text-center text-[10px] font-mono">{activeConfig[slot].y}</span>
                              <AutoRepeatButton onClick={() => setConfig(p => ({ ...p, [slot]: { ...p[slot], y: p[slot].y + 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Per-seat sidebet icon offset (on top of global config) */}
                  {selectedSeat > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-600">
                      <span className="text-xs text-yellow-300 block mb-1">Seat {selectedSeat} Offset</span>
                      <div className="grid grid-cols-3 gap-1">
                        {(['radiusOffset', 'x', 'y'] as const).map(key => (
                          <div key={key} className="flex flex-col items-center">
                            <span className="text-[10px] text-gray-500 capitalize">{key === 'radiusOffset' ? 'R' : key}</span>
                            <div className="flex items-center gap-0.5">
                              <AutoRepeatButton onClick={() => setSidebetIconConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] - 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">-</AutoRepeatButton>
                              <span className="w-6 text-center text-[10px] font-mono">{sidebetIconConfigs[selectedSeat]?.[key] ?? 0}</span>
                              <AutoRepeatButton onClick={() => setSidebetIconConfigs(p => ({ ...p, [selectedSeat]: { ...p[selectedSeat], [key]: p[selectedSeat][key] + 1 } }))} className="w-4 h-4 bg-gray-700 hover:bg-gray-600 rounded text-[10px]">+</AutoRepeatButton>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* BUDDY HUD Controls */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-cyan-300 text-xs font-bold block mb-2">BUDDY HUD (DP/TC)</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <span className="text-xs text-gray-500">DP X</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, dpX: p.dpX - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-[10px] font-mono">{buddyHudConfig.dpX}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, dpX: p.dpX + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">TC X</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, tcX: p.tcX - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-[10px] font-mono">{buddyHudConfig.tcX}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, tcX: p.tcX + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Y</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, y: p.y - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-[10px] font-mono">{buddyHudConfig.y}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, y: p.y + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Bar Y</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, barY: p.barY - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-[10px] font-mono">{buddyHudConfig.barY}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, barY: p.barY + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <span className="text-xs text-gray-500">Bar Width</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, barWidth: p.barWidth - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-[10px] font-mono">{buddyHudConfig.barWidth}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, barWidth: p.barWidth + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Bar Height</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, barHeight: p.barHeight - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-10 text-center text-[10px] font-mono">{buddyHudConfig.barHeight}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, barHeight: p.barHeight + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <span className="text-xs text-gray-500">Label</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, labelFontSize: p.labelFontSize - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-8 text-center text-[10px] font-mono">{buddyHudConfig.labelFontSize}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, labelFontSize: p.labelFontSize + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Value</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, valueFontSize: p.valueFontSize - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-8 text-center text-[10px] font-mono">{buddyHudConfig.valueFontSize}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, valueFontSize: p.valueFontSize + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Tick</span>
                      <div className="flex items-center gap-0.5">
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, tickLabelFontSize: p.tickLabelFontSize - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                        <span className="w-8 text-center text-[10px] font-mono">{buddyHudConfig.tickLabelFontSize}</span>
                        <AutoRepeatButton onClick={() => setBuddyHudConfig(p => ({ ...p, tickLabelFontSize: p.tickLabelFontSize + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PANEL TAB */}
            {devTab === 'panel' && (
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs block mb-2">Position (4 vertical + bottom)</label>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <button onClick={() => setPanelPosition('viewport-left')} className={`flex-1 py-2 rounded text-xs font-bold ${panelPosition === 'viewport-left' ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}>VPort L</button>
                      <button onClick={() => setPanelPosition('table-left')} className={`flex-1 py-2 rounded text-xs font-bold ${panelPosition === 'table-left' ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}>Table L</button>
                      <button onClick={() => setPanelPosition('bottom')} className={`flex-1 py-2 rounded text-xs font-bold ${panelPosition === 'bottom' ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}>Bottom</button>
                      <button onClick={() => setPanelPosition('table-right')} className={`flex-1 py-2 rounded text-xs font-bold ${panelPosition === 'table-right' ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}>Table R</button>
                      <button onClick={() => setPanelPosition('viewport-right')} className={`flex-1 py-2 rounded text-xs font-bold ${panelPosition === 'viewport-right' ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}>VPort R</button>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  <p>VPort L/R: Flush with viewport edge</p>
                  <p>Table L/R: Flush with game table edge</p>
                  <p>Bottom: Centered horizontal panel</p>
                </div>

                {/* Panel Text Sizes */}
                <div className="border-t border-gray-700 pt-3 mt-3">
                  <label className="text-gray-400 text-xs block mb-2 font-bold">Button Text Sizes</label>

                  {/* Main Buttons - Vertical */}
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-400 text-xs">Main (Vert)</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, mainButtonsVert: Math.max(25, p.mainButtonsVert - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{panelTextSizes.mainButtonsVert}%</span>
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, mainButtonsVert: p.mainButtonsVert + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>

                  {/* Main Buttons - Horizontal */}
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-400 text-xs">Main (Horiz)</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, mainButtonsHoriz: Math.max(25, p.mainButtonsHoriz - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{panelTextSizes.mainButtonsHoriz}%</span>
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, mainButtonsHoriz: p.mainButtonsHoriz + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>

                  {/* Side Buttons - Vertical */}
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-400 text-xs">Side (Vert)</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, sideButtonsVert: Math.max(25, p.sideButtonsVert - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{panelTextSizes.sideButtonsVert}%</span>
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, sideButtonsVert: p.sideButtonsVert + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>

                  {/* Side Buttons - Horizontal */}
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-400 text-xs">Side (Horiz)</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, sideButtonsHoriz: Math.max(25, p.sideButtonsHoriz - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{panelTextSizes.sideButtonsHoriz}%</span>
                      <AutoRepeatButton onClick={() => setPanelTextSizes(p => ({ ...p, sideButtonsHoriz: p.sideButtonsHoriz + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mt-1">
                    <p>Main: HIT, STAND, BET</p>
                    <p>Side: SPLIT, DOUBLE, SURRENDER, AUTOBET</p>
                  </div>
                </div>
              </div>
            )}

            {/* CARDS TAB */}
            {devTab === 'cards' && (
              <div className="space-y-3">
                {/* Card Scale */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-400 text-xs">Scale</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, scale: Math.max(50, p.scale - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{cardConfig.scale}%</span>
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, scale: Math.min(200, p.scale + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Corner Font Size */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-400 text-xs">Corner Font</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, cornerFontSize: Math.max(6, p.cornerFontSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{cardConfig.cornerFontSize}px</span>
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, cornerFontSize: Math.min(24, p.cornerFontSize + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Corner Suit Size */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-400 text-xs">Corner Suit</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, cornerSuitSize: Math.max(6, p.cornerSuitSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{cardConfig.cornerSuitSize}px</span>
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, cornerSuitSize: Math.min(24, p.cornerSuitSize + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Corner Gap */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-400 text-xs">Corner Gap</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, cornerGap: p.cornerGap - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{cardConfig.cornerGap}px</span>
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, cornerGap: p.cornerGap + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Center Suit Size */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-400 text-xs">Center Suit</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, centerSuitSize: Math.max(20, p.centerSuitSize - 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{cardConfig.centerSuitSize}px</span>
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, centerSuitSize: Math.min(96, p.centerSuitSize + 1) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Border Thickness */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-400 text-xs">Border</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, borderThickness: Math.max(0, +(p.borderThickness - 0.5).toFixed(1)) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{cardConfig.borderThickness}px</span>
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, borderThickness: Math.min(5, +(p.borderThickness + 0.5).toFixed(1)) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Seat Cards Y Position */}
                <div className="border-b border-gray-700 pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-gray-400 text-xs">Seat Cards Y</label>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, seatCardsY: p.seatCardsY - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-10 text-center text-xs font-mono">{cardConfig.seatCardsY}</span>
                      <AutoRepeatButton onClick={() => setCardConfig(p => ({ ...p, seatCardsY: p.seatCardsY + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Per-Seat Card Hand Position */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Per-Seat Hand Position</label>
                  {/* Seat Selector */}
                  <div className="flex items-center gap-1 mb-2">
                    <span className="text-gray-400 text-xs">Seat:</span>
                    {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                      <button
                        key={seat}
                        onClick={() => setDevCardSeat(devCardSeat === seat ? null : seat)}
                        className={`w-6 h-6 rounded text-xs font-bold ${devCardSeat === seat ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                      >
                        {seat}
                      </button>
                    ))}
                  </div>
                  {/* Position Controls (show when a seat is selected) */}
                  {devCardSeat !== null && devCardVisualPos !== null && (
                    <div className="space-y-1 bg-gray-800 p-2 rounded">
                      <div className="text-xs text-purple-300 mb-1">Seat {devCardSeat} Hand <span className="text-gray-500">(pos {devCardVisualPos})</span></div>
                      {/* X Offset */}
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-xs">X</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setPerSeatCardConfigs(p => ({ ...p, [devCardVisualPos]: { ...p[devCardVisualPos], x: p[devCardVisualPos].x - 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-10 text-center text-xs font-mono">{perSeatCardConfigs[devCardVisualPos]?.x ?? 0}</span>
                          <AutoRepeatButton onClick={() => setPerSeatCardConfigs(p => ({ ...p, [devCardVisualPos]: { ...p[devCardVisualPos], x: p[devCardVisualPos].x + 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                      {/* Y Offset */}
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-xs">Y</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setPerSeatCardConfigs(p => ({ ...p, [devCardVisualPos]: { ...p[devCardVisualPos], y: p[devCardVisualPos].y - 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-10 text-center text-xs font-mono">{perSeatCardConfigs[devCardVisualPos]?.y ?? 0}</span>
                          <AutoRepeatButton onClick={() => setPerSeatCardConfigs(p => ({ ...p, [devCardVisualPos]: { ...p[devCardVisualPos], y: p[devCardVisualPos].y + 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                      {/* Radius Offset */}
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-xs">Radius</span>
                        <div className="flex items-center gap-1">
                          <AutoRepeatButton onClick={() => setPerSeatCardConfigs(p => ({ ...p, [devCardVisualPos]: { ...p[devCardVisualPos], radiusOffset: p[devCardVisualPos].radiusOffset - 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                          <span className="w-10 text-center text-xs font-mono">{perSeatCardConfigs[devCardVisualPos]?.radiusOffset ?? 0}</span>
                          <AutoRepeatButton onClick={() => setPerSeatCardConfigs(p => ({ ...p, [devCardVisualPos]: { ...p[devCardVisualPos], radiusOffset: p[devCardVisualPos].radiusOffset + 1 } }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Corner Cards */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Left Corner Card (Scorekeeper)</label>
                  {/* X */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-400 text-xs">X</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, x: p.x - 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{leftCornerCardConfig.x}</span>
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, x: p.x + 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                  {/* Y */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-400 text-xs">Y</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, y: p.y - 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{leftCornerCardConfig.y}</span>
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, y: p.y + 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                  {/* Rotation */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-400 text-xs">Rot</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, rotation: p.rotation - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{leftCornerCardConfig.rotation}°</span>
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, rotation: p.rotation + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                  {/* Scale */}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs">Scale</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, scale: Math.max(10, p.scale - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{leftCornerCardConfig.scale}%</span>
                      <AutoRepeatButton onClick={() => setLeftCornerCardConfig(p => ({ ...p, scale: Math.min(300, p.scale + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>

                {/* Right Corner Card */}
                <div>
                  <label className="text-purple-300 text-xs font-bold block mb-2">Right Corner Card (Modal)</label>
                  {/* X */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-400 text-xs">X</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, x: p.x - 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{rightCornerCardConfig.x}</span>
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, x: p.x + 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                  {/* Y */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-400 text-xs">Y</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, y: p.y - 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{rightCornerCardConfig.y}</span>
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, y: p.y + 5 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                  {/* Rotation */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-400 text-xs">Rot</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, rotation: p.rotation - 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{rightCornerCardConfig.rotation}°</span>
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, rotation: p.rotation + 1 }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                  {/* Scale */}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs">Scale</span>
                    <div className="flex items-center gap-1">
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, scale: Math.max(10, p.scale - 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">-</AutoRepeatButton>
                      <span className="w-12 text-center text-xs font-mono">{rightCornerCardConfig.scale}%</span>
                      <AutoRepeatButton onClick={() => setRightCornerCardConfig(p => ({ ...p, scale: Math.min(300, p.scale + 5) }))} className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs">+</AutoRepeatButton>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* LAYOUT TAB - Animation Endpoint Editor */}
            {devTab === 'layout' && (
              <div className="space-y-3">
                {/* Endpoint Selector */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-purple-300 text-xs font-bold block mb-2">Animation Endpoints</label>
                  <select
                    value={selectedLayoutEndpoint}
                    onChange={(e) => setSelectedLayoutEndpoint(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs"
                  >
                    <optgroup label="Core">
                      <option value="staged-bet">Staged Bet</option>
                      <option value="tray">Tray</option>
                      <option value="shoe">Shoe</option>
                      <option value="dealer">Dealer</option>
                    </optgroup>
                    <optgroup label="Kitty (Per Seat)">
                      {[1, 2, 3, 4, 5, 6, 7].map(seat => (
                        <option key={`kitty-${seat}`} value={BlackjackEndpointIds.kitty(seat)}>
                          Seat {seat} Kitty
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Tray Chips">
                      {BLACKJACK_DENOMINATIONS.map(denom => (
                        <option key={`tray-${denom}`} value={BlackjackEndpointIds.trayChip(denom)}>
                          Tray Chip BB {denom}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Rail Chips">
                      {BLACKJACK_DENOMINATIONS.map(denom => (
                        <option key={`rail-${denom}`} value={BlackjackEndpointIds.railChip(denom)}>
                          Rail Chip BB {denom}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* Position Editor */}
                {layout && layout.endpoints[selectedLayoutEndpoint] && (
                  <div className="border-b border-gray-700 pb-2">
                    <label className="text-gray-400 text-xs block mb-2">
                      Position: <span className="font-mono text-purple-300">{selectedLayoutEndpoint}</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">X</span>
                          <div className="flex items-center gap-1">
                            <AutoRepeatButton
                              onClick={() => updateLayoutPosition(selectedLayoutEndpoint, { x: layout.endpoints[selectedLayoutEndpoint].position.x - 5 })}
                              className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs"
                            >-</AutoRepeatButton>
                            <span className="w-12 text-center text-xs font-mono">
                              {Math.round(layout.endpoints[selectedLayoutEndpoint].position.x)}
                            </span>
                            <AutoRepeatButton
                              onClick={() => updateLayoutPosition(selectedLayoutEndpoint, { x: layout.endpoints[selectedLayoutEndpoint].position.x + 5 })}
                              className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs"
                            >+</AutoRepeatButton>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Y</span>
                          <div className="flex items-center gap-1">
                            <AutoRepeatButton
                              onClick={() => updateLayoutPosition(selectedLayoutEndpoint, { y: layout.endpoints[selectedLayoutEndpoint].position.y - 5 })}
                              className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs"
                            >-</AutoRepeatButton>
                            <span className="w-12 text-center text-xs font-mono">
                              {Math.round(layout.endpoints[selectedLayoutEndpoint].position.y)}
                            </span>
                            <AutoRepeatButton
                              onClick={() => updateLayoutPosition(selectedLayoutEndpoint, { y: layout.endpoints[selectedLayoutEndpoint].position.y + 5 })}
                              className="w-5 h-5 bg-gray-700 hover:bg-gray-600 rounded text-xs"
                            >+</AutoRepeatButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="border-b border-gray-700 pb-2">
                  <label className="text-gray-400 text-xs block mb-2">Quick Actions</label>
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => {
                        console.log('[DEV] Testing animateRailToKitty...');
                        if (pixiRef.current && mySeatPosition) {
                          pixiRef.current.animateRailToKitty(mySeatPosition, 100);
                        }
                      }}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded text-xs"
                    >
                      Test Rail→Kitty
                    </button>
                    <button
                      onClick={() => {
                        console.log('[DEV] Testing animateChipFromDealer...');
                        if (pixiRef.current && mySeatPosition) {
                          pixiRef.current.animateChipFromDealer(mySeatPosition, 100);
                        }
                      }}
                      className="px-2 py-1 bg-green-600 hover:bg-green-500 rounded text-xs"
                    >
                      Test Tray→Kitty
                    </button>
                    <button
                      onClick={() => {
                        console.log('[DEV] Testing animateChipFromKitty...');
                        if (pixiRef.current && mySeatPosition) {
                          pixiRef.current.animateChipFromKitty(mySeatPosition, 100);
                        }
                      }}
                      className="px-2 py-1 bg-yellow-600 hover:bg-yellow-500 rounded text-xs"
                    >
                      Test Kitty→Rail
                    </button>
                    <button
                      onClick={() => {
                        console.log('[DEV] Testing animateChipToDealer...');
                        if (pixiRef.current && mySeatPosition) {
                          pixiRef.current.animateChipToDealer(mySeatPosition, 100);
                        }
                      }}
                      className="px-2 py-1 bg-red-600 hover:bg-red-500 rounded text-xs"
                    >
                      Test Kitty→Tray
                    </button>
                  </div>
                </div>

                {/* Reset Layout */}
                <div>
                  <button
                    onClick={() => {
                      console.log('[DEV] Resetting layout to defaults...');
                      initializeLayout();
                    }}
                    className="w-full px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs"
                  >
                    Reset Layout to Defaults
                  </button>
                </div>

                {/* Layout Info */}
                <div className="text-[10px] text-gray-500">
                  <p>Version: {layout?.version || 'N/A'}</p>
                  <p>Endpoints: {layout ? Object.keys(layout.endpoints).length : 0}</p>
                </div>
              </div>
            )}
          </div>

          {/* Reset Button */}
          <button
            onClick={() => {
              // Reset to locked-in config values
              setSeatRadiusScale(191);
              setSeatSizeScale(100);
              setSeatGroupOffset({ x: 0, y: -40 });
              setChipRailOffset({ x: 0, y: 183 });
              setStagedBetOffset({ x: 0, y: 7 });
              setStagedBetBoxConfig({ width: 275, height: 50, fontSize: 19, borderThickness: 3, clearFontSize: 13, buildYourBetFontSize: 14 });
              setChipXOffsets({ 1: -76, 5: -99, 25: -118, 100: -136, 500: 140, 1000: 126, 5000: 107, 10000: 84 });
              setChipYOffsets({ 1: -342, 5: -294, 25: -260, 100: -238, 500: -238, 1000: -260, 5000: -294, 10000: -342 });
              setChipLabelYOffsets({ 1: 0, 5: 0, 25: 0, 100: 0, 500: 0, 1000: 0, 5000: 0, 10000: 0 });
              setChipLabelPillOpacity(100);
              setUsernameY(-2);
              setShoeConfig({ x: -14, y: 6, rotation: 90, scale: 120 });
              setTrayConfig({ x: 105, y: -9, rotation: 0, scale: 90 });
              setShoeCardsConfig({ x: -29, y: -6, rotation: -5, scale: 116 });
              setShoeCardOffsets({ 0: { x: 0, y: -2, rotation: 0, scale: 100 }, 1: { x: 0, y: -1, rotation: 0, scale: 100 }, 2: { x: 0, y: 2, rotation: 0, scale: 100 } });
              setDealerButtonSeatOverride(4);
              setDealerButtonConfig({ x: 0, y: 0, size: 110, bgColor: '#ffffff', fontColor: '#111827', borderColor: '#111827', borderWidth: 2 });
              setChipBorderConfig({ opacity: 30, thickness: 7, color: '#c2c89c' });
              setChipCounterConfig({ show: true, pillOpacity: 100, fontColor: '#FB923C', fontSize: 14, x: 0, y: 8 });
              setKittyConfig({ x: 0, y: 0, rotation: 0 });
              // Reset per-seat configs (including dealer seat 0)
              const defaultSeatConfig = { x: 0, y: 0, rot: 0, scale: 100 };
              const seat1Config = { x: -43, y: 0, rot: 0, scale: 100 };
              const seat2Config = { x: -10, y: -8, rot: 0, scale: 100 };
              const seat3Config = { x: 0, y: -10, rot: 0, scale: 100 };
              const seat4Config = { x: 0, y: 2, rot: 0, scale: 108 };
              const seat5Config = { x: 0, y: -10, rot: 0, scale: 100 };
              const seat6Config = { x: 10, y: -8, rot: 0, scale: 100 };
              const seat7Config = { x: 44, y: 0, rot: 0, scale: 100 };
              const defaultChipConfig = { radiusOffset: 0, x: 0, y: 0, scale: 100 };
              setSeatConfigs({ 0: defaultSeatConfig, 1: seat1Config, 2: seat2Config, 3: seat3Config, 4: seat4Config, 5: seat5Config, 6: seat6Config, 7: seat7Config });
              setDealerScoreboxOffset({ x: 0, y: 0 });
              setBetChipConfigs({ 0: defaultChipConfig, 1: defaultChipConfig, 2: defaultChipConfig, 3: defaultChipConfig, 4: defaultChipConfig, 5: defaultChipConfig, 6: defaultChipConfig, 7: defaultChipConfig });
              setInsuranceChipConfigs({ 0: defaultChipConfig, 1: defaultChipConfig, 2: defaultChipConfig, 3: defaultChipConfig, 4: defaultChipConfig, 5: defaultChipConfig, 6: defaultChipConfig, 7: defaultChipConfig });
              setTrayChipsOffset({ x: 0, y: 0 });
              setCardConfig({ scale: 117, cornerFontSize: 20, cornerSuitSize: 24, centerSuitSize: 70, borderThickness: 1, seatCardsY: -5, dealerCardsX: 0, dealerCardsY: 35, cornerGap: -6 });
              setScoreboxConfig({ scale: 150, fontSize: 19, x: 0, y: 0, minWidth: 35 });
              setOutcomeLabelConfig({ scale: 100, fontSize: 15, x: 27, fontFamily: 'Arial' });
              setPanelPosition('table-right');
            }}
            className="mt-2 w-full py-2 bg-red-700 hover:bg-red-600 rounded text-xs font-bold"
          >
            Reset All
          </button>
        </motion.div>
      )}

      {/* DEV Controls Button - Fixed lower-left corner */}
      {user?.isAdmin && (
        <button
          onClick={() => setShowDevControls(!showDevControls)}
          className="fixed bottom-4 left-4 z-50 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-lg transition-colors"
        >
          {showDevControls ? 'Hide DEV' : 'DEV'}
        </button>
      )}

      {/* PANIC Button - Admin only, marks diagnostic logs */}
      {user?.isAdmin && (
        <button
          onClick={() => {
            diagLog.panic();
            // Visual feedback
            const btn = document.activeElement as HTMLButtonElement;
            if (btn) {
              btn.style.transform = 'scale(1.2)';
              setTimeout(() => { btn.style.transform = 'scale(1)'; }, 150);
            }
          }}
          className="fixed bottom-4 left-20 z-50 px-3 py-2 bg-red-600 hover:bg-red-500 text-white text-xs font-bold rounded-lg transition-all animate-pulse"
          title="Insert PANIC marker into diagnostic logs (Ctrl+Shift+D to view)"
        >
          PANIC
        </button>
      )}

      {/* Reconnection Modal - Shows when socket disconnects */}
      <ReconnectingModal tableId={tableId} />
    </>
  );
}
